/** Macheta planului de situație: amplasamentul întreg, sub soarele lui.
 *
 *  A DOUA MACHETĂ, și e deliberat alt desen decât `Randare3D`. Aceea e macheta
 *  unui NIVEL — volume tăiate la 1,15 m, cameră ortografică, ca una de carton pe
 *  masă, fiindcă din ea se citesc dimensiuni. Asta e amplasamentul: perspectivă,
 *  cer, umbre lungi. Răspunde la altă întrebare — cum stă clădirea pe teren și
 *  cum cade lumina pe ea — iar la întrebarea aia o cameră ortografică minte prin
 *  lipsă: fără fugă, nu se vede că un corp e în spatele altuia.
 *
 *  SOARELE NU E O DIRECȚIE ALEASĂ. Vine din `soare.ts`, calculat pentru
 *  coordonatele parcelei și ora de pe ceas. De-aia macheta și panoul de însorire
 *  nu se pot contrazice: lumina care se vede și ora care se numără sunt același
 *  vector.
 *
 *  DE CE NU HDRI. `Randare3D` încarcă o hartă de mediu fotografică, cu soarele
 *  ei copt înăuntru. Aici ar fi o minciună: harta ar lumina din altă parte decât
 *  soarele calculat, iar diferența s-ar vedea exact pe fațada despre care se
 *  pronunță studiul. Cerul de aici e desenat din poziția soarelui — același
 *  gradient e și fundal, și mediu, generat cu PMREM din el.
 *
 *  CADRUL e al desenului: +x est, +y SUD, +z sus (v. `vectorSoare`). */

import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { cadrulElementului, cotele, deseneazaCotele } from "./cote3d";
import { bcp47, esteCheie, limba, t as tr, type Cheie } from "../../i18n";
import type { StoreyPlan } from "../canonical";
import { TaieturaEtaj } from "./taieturaEtaj";
import { tipulApartamentului, type ApartamentEtaj } from "./interiorEtaj";
import { aleseInZona, bazaEid, bucatileObiectului, coltulComun, eAlEi, eidLaTriunghi, fataElementului,
         infoElemente, intrarile,
         liniiDeLipire, liniileFetelor, lipeste, margineaBucatii, peFata, planurileBucatii, tabelAlegere,
         triunghiuri, triunghiurileDin, zonaDinTragere, type InfoElement, type Intrare, type LiniaFetei,
         type TabelAlegere, type ZonaMacheta } from "./selectie3d";
import * as BGU from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { construiesteMasini } from "./masini3d";
import { construiestePomi } from "./copaci3d";
import { construiesteZone } from "./zone3d";
import { construiestePlante, geometriePamant, plantePeJardiniera, type PlantaSit } from "./plante3d";
import { geometriaCortinei } from "./cortina3d";
import { materialCamera, materialGeam } from "./camera3d";
import { CULOARE_FEL, conturCuLogii, cutiePanou, insetInel, potrivitePentruUnire, profilPeFata, texturaGranulatie, texturaMaterial, type GolPanou, taieDupaRelief, type ReliefPeFata } from "./panou3d";
import { PAT_RETRAS_M, geometriaPardoselii, texturaFibra } from "./pardoseala3d";
import { geometriaManii, susulManii } from "./manaCurenta3d";
import { manaDeDesenat } from "../manaCurenta";
import type { Fatada3, ScenaSit, Suprafata } from "./sit3d";
import type { PanouFatada, ParapetFatada } from "../canonical";
import { geometriaGolului, IMPLICIT as TAMPLARIE } from "./tamplarie3d";
import type { GolFatada } from "./sit3d";
import { vectorSoare } from "./soare";
import { inCadrulDocumentului, inDocument, inLume, lumeDreapta, planInLume, razaInDocument } from "./oglinda3d";
import { tasteInAfaraCampurilor } from "./tasteMacheta";
import { FINISAJ_M, mainiCurente, platforma3d, treptePeAmprenta } from "./trepte3d";
import { VERDE_SOL, VERDE_SUBSOL } from "./verdeLot";
import { ataseaza as ataseazaOras, type StratOras } from "../oras/strat3d";
import type { FelCladire, StareMacheta, StratAtasat, StratSol, Tinta } from "../macheta/tipuri";

/** O poziție de cameră.
 *
 *  `incadreaza` — distanța se CALCULEAZĂ ca tot amplasamentul să intre în cadru,
 *  nu se ia ca multiplu al razei. Un multiplu fix nu poate merge pe două parcele
 *  diferite: raza e a amprentei la sol, iar ce iese din cadru sunt CLĂDIRILE.
 *  Pe 27.000 m² cu blocuri de cincizeci de metri, factorul care încadra o
 *  parcelă mică tăia jumătate din ansamblu.
 *
 *  `d` rămâne pentru vederile de aproape, unde nu se vrea tot amplasamentul —
 *  acolo e fracție din rază, ca să se scaleze cu parcela. */
export interface Vedere {
  az: number; el: number; fov: number;
  d?: number;
  incadreaza?: boolean;
}

export const VEDERI: Record<string, Vedere> = {
  ansamblu: { az: 152, el: 22, fov: 38, incadreaza: true },
  strada:   { az: 5,   el: 7,  fov: 46, d: 1.5 },
  curte:    { az: 205, el: 8,  fov: 50, d: 1.2 },
  pasare:   { az: 186, el: 52, fov: 34, incadreaza: true },
};

/** Culorile cerului, după înălțimea soarelui. Între praguri se interpolează. */
const RAMPA = [
  { a: -14, sus: [0.030, 0.042, 0.085], jos: [0.070, 0.090, 0.150] },
  { a: -6,  sus: [0.085, 0.115, 0.215], jos: [0.280, 0.215, 0.270] },
  { a: -1,  sus: [0.180, 0.260, 0.430], jos: [0.840, 0.440, 0.270] },
  { a: 3,   sus: [0.270, 0.420, 0.650], jos: [0.960, 0.660, 0.400] },
  { a: 9,   sus: [0.265, 0.445, 0.705], jos: [0.930, 0.770, 0.570] },
  { a: 18,  sus: [0.245, 0.455, 0.730], jos: [0.860, 0.830, 0.760] },
  { a: 34,  sus: [0.180, 0.410, 0.730], jos: [0.720, 0.810, 0.870] },
  { a: 70,  sus: [0.130, 0.360, 0.715], jos: [0.660, 0.790, 0.880] },
];

const mix = (a: number[], b: number[], t: number) =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const neted = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

function culoriCer(alt: number): { sus: number[]; jos: number[] } {
  if (alt <= RAMPA[0].a) return { sus: RAMPA[0].sus, jos: RAMPA[0].jos };
  for (let i = 0; i < RAMPA.length - 1; i++) {
    if (alt < RAMPA[i + 1].a) {
      const t = (alt - RAMPA[i].a) / (RAMPA[i + 1].a - RAMPA[i].a);
      return { sus: mix(RAMPA[i].sus, RAMPA[i + 1].sus, t),
               jos: mix(RAMPA[i].jos, RAMPA[i + 1].jos, t) };
    }
  }
  const u = RAMPA[RAMPA.length - 1];
  return { sus: u.sus, jos: u.jos };
}

const VS_CER = `
  varying vec3 vPoz;
  void main() {
    vPoz = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;

const FS_CER = `
  uniform vec3 sus; uniform vec3 jos; uniform vec3 dirSoare;
  uniform vec3 culSoare; uniform float taria;
  varying vec3 vPoz;
  void main() {
    vec3 d = normalize(vPoz);
    float t = clamp(d.z * 1.35 + 0.08, 0.0, 1.0);
    vec3 c = mix(jos, sus, pow(t, 0.62));
    float s = max(dot(d, normalize(dirSoare)), 0.0);
    c += culSoare * pow(s, 420.0) * 3.0 * taria;     // discul
    c += culSoare * pow(s, 7.0) * 0.14 * taria;      // halo
    gl_FragColor = vec4(c, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }`;

/** Un inel de puncte → `THREE.Shape`, cu golurile lui. */
function forma(s: Suprafata): THREE.Shape | null {
  if (s.poly.length < 3) return null;
  const f = new THREE.Shape();
  f.moveTo(s.poly[0][0], s.poly[0][1]);
  for (let i = 1; i < s.poly.length; i++) f.lineTo(s.poly[i][0], s.poly[i][1]);
  f.closePath();
  for (const h of s.holes) {
    if (h.length < 3) continue;
    const g = new THREE.Path();
    g.moveTo(h[0][0], h[0][1]);
    for (let i = 1; i < h.length; i++) g.lineTo(h[i][0], h[i][1]);
    g.closePath();
    f.holes.push(g);
  }
  return f;
}

/** Un dreptunghi vertical pe un segment de perete: golul, ca suprafață.
 *
 *  Se scoate cu trei centimetri în afara zidului, ca să nu lupte cu el pe
 *  adâncime. Normala se ia din latura conturului pe care stă golul — nu din
 *  segmentul lui: o fereastră e paralelă cu peretele, dar care din cele două
 *  perpendiculare e „afară" o știe doar conturul. */
function inPoligonRing(x: number, y: number, ring: number[][]): boolean {
  let c = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

function distLaInel(x: number, y: number, ring: number[][]): number {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[j], [bx, by] = ring[i];
    const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
    const t = L2 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / L2)) : 0;
    best = Math.min(best, Math.hypot(x - ax - t * dx, y - ay - t * dy));
  }
  return best;
}

/** Harta alfa a unui ECRAN FIX, pe un modul: alb = tablă, negru = gol.
 *  `perforated` — o gaură rotundă pe mijlocul modulului, cu aria `gol` din el;
 *  `slats` — o fantă verticală de lățime `gol`. Se repetă pe foaie. */
function texturaEcran(desen: string, gol: number): THREE.Texture {
  const N = 64;
  const c = document.createElement("canvas");
  c.width = c.height = N;
  const x = c.getContext("2d");
  if (x) {
    x.fillStyle = "#fff";
    x.fillRect(0, 0, N, N);
    x.fillStyle = "#000";
    if (desen === "slats") {
      x.fillRect(Math.round(N * (1 - gol) / 2), 0, Math.round(N * gol), N);
    } else {
      x.beginPath();
      x.arc(N / 2, N / 2, N * Math.sqrt(gol / Math.PI), 0, Math.PI * 2);
      x.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

/** FRUNZIȘUL unui ecran verde, pe un modul de 1,2 m care se repetă: spalierul
 *  (o plasă închisă la culoare), iar peste el frunze în câteva nuanțe, de la
 *  cele din adânc la cele din față. `gol` = cât din spalier rămâne neacoperit.
 *  Determinist (același zar la fiecare încărcare) și fără cusătură: frunza
 *  care trece de margine se desenează și dincolo. */
function texturaFrunzis(gol: number): THREE.Texture {
  const N = 256;
  const c = document.createElement("canvas");
  c.width = c.height = N;
  const x = c.getContext("2d");
  if (x) {
    x.clearRect(0, 0, N, N);
    // spalierul: sârme la 20 cm, pe ambele direcții
    x.strokeStyle = "#2b2f2a";
    x.lineWidth = 3;
    for (let k = 0; k <= 6; k++) {
      const p = Math.round((k / 6) * N);
      x.beginPath(); x.moveTo(p, 0); x.lineTo(p, N); x.stroke();
      x.beginPath(); x.moveTo(0, p); x.lineTo(N, p); x.stroke();
    }
    let s = 0x9e3779b1;
    const zar = () => { s = Math.imul(s ^ (s >>> 15), 0x2c1b3c6d) + 0x297a2d39 | 0; return ((s >>> 0) % 100000) / 100000; };
    const verzi = ["#24451f", "#2f5a27", "#3f7030", "#55883a", "#6d9e45", "#86b256"];
    const frunze = Math.round(760 * (1 - gol * 0.75));
    for (let i = 0; i < frunze; i++) {
      const px = zar() * N, py = zar() * N, a = zar() * Math.PI * 2;
      const lung = 9 + zar() * 7, lat = 4.5 + zar() * 3;
      // întâi cele închise (din adânc), la urmă cele deschise (din față)
      x.fillStyle = verzi[Math.min(verzi.length - 1, Math.floor((i / frunze) * verzi.length + zar() * 1.2))];
      for (const dx of [-N, 0, N]) for (const dy of [-N, 0, N]) {
        if (px + dx < -20 || px + dx > N + 20 || py + dy < -20 || py + dy > N + 20) continue;
        x.beginPath();
        x.ellipse(px + dx, py + dy, lung, lat, a, 0, Math.PI * 2);
        x.fill();
      }
    }
    // colțul modulului e mereu frunză: canturile cutiei iau culoarea de acolo
    x.fillStyle = verzi[2];
    for (const [px, py] of [[0, 0], [N, 0], [0, N], [N, N]]) {
      x.beginPath(); x.arc(px, py, 10, 0, Math.PI * 2); x.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/** DALELE trotuarului, pe o foaie de 2 × 2 m care se repetă: dale pătrate de
 *  40 cm, fiecare în nuanța ei, cu rostul mai închis. Pavelele mici (20 × 10)
 *  se topeau într-un gri uniform de la distanța la care se privește macheta
 *  (01.11.2026); dala de 40 cm se mai citește și din vederea de ansamblu.
 *  `ShapeGeometry` dă UV-urile în metri, așa că dalele cad la aceeași scară pe
 *  orice trotuar, oricât de întors. */
function texturaPavele(): THREE.Texture {
  const N = 512, FOAIE_M = 2, DALE = 5;
  const c = document.createElement("canvas");
  c.width = c.height = N;
  const x = c.getContext("2d");
  if (x) {
    x.fillStyle = "#7e7a70";
    x.fillRect(0, 0, N, N);
    let s = 0x2545f491;
    const zar = () => { s = Math.imul(s ^ (s >>> 15), 0x2c1b3c6d) + 0x297a2d39 | 0; return ((s >>> 0) % 100000) / 100000; };
    const w = N / DALE, rost = Math.round(N * 0.012 / FOAIE_M);
    for (let r = 0; r < DALE; r++) {
      for (let k = 0; k < DALE; k++) {
        const t = 0.9 + zar() * 0.2;
        x.fillStyle = `rgb(${Math.round(0xb9 * t)}, ${Math.round(0xb3 * t)}, ${Math.round(0xa5 * t)})`;
        x.fillRect(Math.round(k * w + rost / 2), Math.round(r * w + rost / 2),
                   Math.round(w - rost), Math.round(w - rost));
      }
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(1 / FOAIE_M, 1 / FOAIE_M);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** DALELE HEXAGONALE de cauciuc ale locului de joacă, pe o foaie care se repetă:
 *  hexagoane cu vârful în sus, de 0,43 m, în aceeași cărămiziu cu tonuri abia
 *  diferite și rostul mai închis. Covorul plat, dintr-o singură culoare, se
 *  citea ca o pată vopsită (Oana, 02.11.2026). Foaia are 4 × 4 dale ca repetiția
 *  să nu se vadă, iar dalele de pe margine iau tonul perechii de pe cealaltă
 *  margine, ca să se închidă fără cusătură. UV-urile sunt în metri (`ShapeGeometry`). */
function texturaCauciuc(): THREE.Texture {
  const R = 0.25, w = Math.sqrt(3) * R, COL = 4, RND = 4;
  const FOAIE_W = COL * w, FOAIE_H = RND * 1.5 * R;             // perioada: pe orizontală w, pe verticală 3R (două rânduri)
  const PX = 320;                                                 // pixeli pe metru
  const c = document.createElement("canvas");
  c.width = Math.round(FOAIE_W * PX); c.height = Math.round(FOAIE_H * PX);
  const x = c.getContext("2d");
  if (x) {
    x.fillStyle = "#9e4f36"; x.fillRect(0, 0, c.width, c.height);  // rostul
    let sm = 0x1a2b3c4d;
    const zar = () => { sm = Math.imul(sm ^ (sm >>> 15), 0x2c1b3c6d) + 0x297a2d39 | 0; return ((sm >>> 0) % 100000) / 100000; };
    const ton: number[][] = Array.from({ length: RND }, () => Array.from({ length: COL }, () => 0.92 + zar() * 0.16));
    const rost = 0.012, r = (R - rost / Math.cos(Math.PI / 6)) * PX;
    for (let j = -1; j <= RND; j++) for (let i = -1; i <= COL; i++) {
      const cx = (i * w + (((j % 2) + 2) % 2) * w / 2) * PX, cy = j * 1.5 * R * PX;
      const t = ton[((j % RND) + RND) % RND][((i % COL) + COL) % COL];
      x.fillStyle = `rgb(${Math.round(0xc8 * t)}, ${Math.round(0x69 * t)}, ${Math.round(0x4a * t)})`;
      x.beginPath();
      for (let k = 0; k < 6; k++) {
        const a = Math.PI / 6 + k * Math.PI / 3;
        x.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      x.closePath(); x.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(1 / FOAIE_W, 1 / FOAIE_H);
  t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
  return t;
}

/** STICLA BALUSTRADELOR. Implicit clară, abia nuanțată; cu o culoare din stil,
 *  în nuanța ei, iar cât de mult se vede prin ea urmează lumina culorii: o
 *  sticlă deschisă e aproape transparentă, una fumurie închisă acoperă mai mult
 *  — dar niciuna nu ajunge o placă opacă. Aceeași regulă o folosește Blender
 *  (`tools/blender/canonical_roundtrip.py: OPACITATE_STICLA`). */
export const CULOARE_STICLA_IMPLICITA = 0xdbe8ec;
export const OPACITATE_STICLA = 0.3;
export function opacitateSticla(culoare: string): number {
  const c = new THREE.Color(culoare.startsWith("#") ? culoare : `#${culoare}`);
  const lumina = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;      // liniară, [0, 1]
  return Math.round((0.28 + 0.42 * (1 - Math.sqrt(lumina))) * 100) / 100;
}
function materialSticla(culoare: string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(culoare), roughness: 0.05,
                                          metalness: 0.1, transparent: true,
                                          opacity: opacitateSticla(culoare), depthWrite: false,
                                          side: THREE.DoubleSide });
}

function golQuad(a: number[], b: number[], z0: number, z1: number,
                 nx: number, ny: number, afara = 0.10): THREE.BufferGeometry | null {
  const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (l < 0.2 || z1 <= z0) return null;
  const ox = nx * afara, oy = ny * afara;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute([
    a[0] + ox, a[1] + oy, z0,  b[0] + ox, b[1] + oy, z0,  b[0] + ox, b[1] + oy, z1,
    a[0] + ox, a[1] + oy, z0,  b[0] + ox, b[1] + oy, z1,  a[0] + ox, a[1] + oy, z1,
  ], 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(
    Array.from({ length: 6 }, () => [nx, ny, 0]).flat(), 3));
  return g;
}

/** Panoul de finisaj din dreptul unui gol: cel mai ieșit panou de finisaj
 *  care îl acoperă (aceeași normală, golul sub el pe u și pe z). Fața lui
 *  exterioară spune cât iese finisajul din zidărie — tocul se trage în
 *  spatele ei cu retragerea scrisă în tâmplărie — iar ȘPALETUL îi ia
 *  culoarea: căptușeala golului e întoarcerea finisajului feței, nu o
 *  tencuială albă oricare ar fi fața («fâșiile albe» de lângă ușile terasei
 *  placate închis, Cătălin, 5.10.2026). Fără panou: implicitul machetei. */
/** GEAMUL PE COLȚ: pentru fiecare gol legat în document de unul de pe fața
 *  vecină (`coltCu`), capătul lui din colț, linia feței vecine pe `x`-ul
 *  golului cu colțul adus la `b`, și cine desenează montantul de colț. Numai
 *  la un colț IEȘIND (normala vecinului iese spre colț), cu goluri de aceeași
 *  înălțime; altfel golul rămâne cu stâlpul lui. */
export function corelaColturile(goluri: GolFatada[]): void {
  const dupaEid = new Map<string, GolFatada>();
  for (const g of goluri) if (g.eid) dupaEid.set(g.eid, g);
  for (const g1 of goluri) {
    g1.colt = undefined;
    if (!g1.coltCu || g1.tip === "Door") continue;
    const g2 = dupaEid.get(g1.coltCu);
    if (!g2 || !g1.eid || g2.tip === "Door") continue;
    if (Math.abs(g1.parapet - g2.parapet) > 0.05 || Math.abs(g1.inaltime - g2.inaltime) > 0.05) continue;
    const W = Math.hypot(g1.b[0] - g1.a[0], g1.b[1] - g1.a[1]) || 1;
    const u = [(g1.b[0] - g1.a[0]) / W, (g1.b[1] - g1.a[1]) / W];
    const cos = u[0] * g2.n[0] + u[1] * g2.n[1];
    if (Math.abs(cos) < 0.9) continue;                    // nu sunt pe fețe perpendiculare
    const xP = (g2.a[0] - g1.a[0]) * u[0] + (g2.a[1] - g1.a[1]) * u[1];
    const la: "a" | "b" = Math.abs(xP - W) < Math.abs(xP) ? "b" : "a";
    const x = la === "b" ? xP : W - xP;
    // colț ieșind: normala vecinului iese spre capătul din colț
    if ((la === "b" ? cos : -cos) < 0.9 || Math.abs(x - W) > 0.5) continue;
    g1.colt = { la, x, stapan: g1.eid < (g2.eid ?? "") };
  }
}

function finisajulGolului(panouri: PanouFatada[], gol: GolFatada): PanouFatada | null {
  const mx = (gol.a[0] + gol.b[0]) / 2, my = (gol.a[1] + gol.b[1]) / 2;
  const zm = gol.parapet + gol.inaltime / 2;
  let best: PanouFatada | null = null;
  for (const p of panouri) {
    if (p.layer !== "finish" || !p.n || p.surface === "reveal" || p.surface === "soffit") continue;
    if (p.n[0] * gol.n[0] + p.n[1] * gol.n[1] < 0.7) continue;
    if (zm < p.z0 || zm > p.z1) continue;
    const L = Math.hypot(p.b[0] - p.a[0], p.b[1] - p.a[1]);
    if (L < 0.05) continue;
    const ux = (p.b[0] - p.a[0]) / L, uy = (p.b[1] - p.a[1]) / L;
    const u = (mx - p.a[0]) * ux + (my - p.a[1]) * uy;
    const d = (mx - p.a[0]) * p.n[0] + (my - p.a[1]) * p.n[1];
    if (u < -0.05 || u > L + 0.05 || Math.abs(d) > 0.6) continue;
    if (!best || p.depth_m > best.depth_m) best = p;
  }
  return best;
}

/** O bandă orizontală în jurul unui inel, scoasă puțin în afară.
 *
 *  Fiecare latură se deplasează pe normala EI, nu se „micșorează" poligonul: o
 *  amprentă în L nu se poate scala fără să iasă din ea însăși. La cinci
 *  centimetri, golul rămas în colțuri nu se vede de la scara amplasamentului. */
function bandaPeInel(ring: number[][], z0: number, z1: number,
                     afara: number): THREE.BufferGeometry[] {
  const out: THREE.BufferGeometry[] = [];
  if (z1 <= z0) return out;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ax = ring[j][0], ay = ring[j][1], bx = ring[i][0], by = ring[i][1];
    const ex = bx - ax, ey = by - ay, l = Math.hypot(ex, ey);
    if (l < 0.4) continue;
    let nx = ey / l, ny = -ex / l;
    const mx = (ax + bx) / 2, my = (ay + by) / 2;
    if (inPoligon(mx + nx * 0.05, my + ny * 0.05, ring)) { nx = -nx; ny = -ny; }
    const q = golQuad([ax, ay], [bx, by], z0, z1, nx, ny, afara);
    if (q) out.push(q);
  }
  return out;
}

/** Punctul e în poligon? Trasare de rază. */
function inPoligon(x: number, y: number, ring: number[][]): boolean {
  let c = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

interface Props {
  scena: ScenaSit;
  /** Poziția soarelui ACUM, în grade. */
  soare: { alt: number; az: number };
  /** Presetul de cameră cerut; schimbarea lui mută camera lin. */
  vedere: keyof typeof VEDERI;
  /** SELECȚIA (v. `selectie3d.ts`): id-urile elementelor alese și zonele trase,
   *  evidențiate pe machetă. */
  selectie?: readonly string[];
  zone?: readonly ZonaMacheta[];
  /** Clic pe un element (`null` = în gol); `adauga` = cu Shift. Fără el,
   *  macheta nu alege nimic. */
  laAlegere?: (eid: string | null, adauga: boolean) => void;
  /** ⌥/Alt + tragere pe o față: o zonă; `adauga` = cu Shift. */
  laZona?: (z: ZonaMacheta, adauga: boolean) => void;
  /** Coloanele de apartamente ale fiecărei fețe (id → `[u0, u1]`, metri pe
   *  față): marginile zonei se lipesc de ele. */
  coloane?: Readonly<Record<string, number[][]>>;
  /** Ce se aprinde mai tare (cipul de sub mouse, în asistent): `e:<id>` sau
   *  `z:<indice>`. */
  aprins?: string | null;
  /** Elementul COTAT (cel arătat în panoul de detalii): lungime, înălțime,
   *  adâncime, măsurate pe ce e desenat (v. `cote3d.ts`). */
  cote?: string | null;
  /** Cifrele cotelor din document (v. `dimensiuniCote`); lipsă = măsurate. */
  coteValori?: { lungime?: number; inaltime?: number; adancime?: number };
  /** ETAJUL TĂIAT (v. `taieturaEtaj.ts`): numărul lui; `null` = toată clădirea. */
  etaj?: number | null;
  /** Planurile etajelor arhitecturii, din care se face interiorul tăiat. */
  planuri?: readonly StoreyPlan[];
  /** HARTA ORAȘULUI din jurul lotului (v. `viewer/oras/`): un strat gata
   *  construit, pus în scenă și scos din ea fără să o reconstruiască. */
  oras?: StratOras | null;
  /** MACHETA E PE ECRAN: chemat după primul cadru al fiecărei scene construite
   *  — abia atunci are cine o acoperă (scheletul de încărcare din
   *  `Vizualizator`) de ce să plece. Construcția scenei ține firul principal o
   *  secundă și mai bine, deci «montată» și «desenată» nu sunt același moment. */
  laGata?: () => void;
  /** CLĂDIREA (corpurile, fațadele, interiorul) e aprinsă; stinsă, rămân
   *  terenul și straturile atașate — așa se vede structura din macheta
   *  unificată (`viewer/macheta`). Se aplică fără reconstrucție. */
  cladire?: boolean;
  /** STRATURILE ATAȘATE (structura…): grupuri construite în afară, în cadrul
   *  documentului, puse în `lume` ca harta orașului; clicabile prin `tinta`. */
  atasate?: readonly StratAtasat[];
  /** Clic pe o piesă a unui strat atașat (`null` = în gol, când nici
   *  clădirea n-a fost lovită). */
  laTinta?: (t: Tinta | null, adauga: boolean) => void;
  /** PE NIVEL: plasele fațadei se lipesc pe planșeu, nu pe tot amplasamentul,
   *  și fiecare bucată a clădirii poartă corpul și nivelul ei — ca `stare` să
   *  le poată aprinde pe rând (timelapse-ul). Costă mai multe desene pe cadru. */
  perNivel?: boolean;
  /** STAREA PE SĂPTĂMÂNĂ (v. `macheta/tipuri`): ce bucăți ale clădirii și ce
   *  straturi ale solului se văd. Se aplică fără reconstrucție. */
  stare?: StareMacheta | null;
}

/** Ce ține evidențierea și zonele de scena construită acum (v. efectul mare). */
interface StareAlegere {
  /** Grupul în care stă scena, în cadrul documentului (v. `oglinda3d`). */
  lume: THREE.Object3D;
  /** Plasele lipite (cu tabelul lor) și plasele unui singur obiect. */
  plase: THREE.Mesh[];
  fete: Map<string, LiniaFetei>;
  fatade: ScenaSit["fatade"];
  cam: THREE.PerspectiveCamera;
  ctrl: OrbitControls;
  rand: THREE.WebGLRenderer;
  taietura: TaieturaEtaj;
  /** Tot ce e al CLĂDIRII în `lume` (corpuri, fațade, plante), ca să se
   *  poată stinge dintr-o dată (prop `cladire`). */
  cladire: THREE.Object3D[];
  /** Grupul solului, cu straturile etichetate (`userData.strat`). */
  sol: THREE.Group;
  /** Urcă (sau coboară) ținta camerei la cota `z`, lin. */
  laCota: (z: number) => void;
  /** Făcute la prima zonă (o trecere pe toate triunghiurile alese). */
  info?: Map<string, InfoElement>;
  intrari?: Intrare[];
}

const CULOARE_ALES = 0x13b5c8;
/** Soclul unui corp fără placaj de soclu în fațadă: un gri cald de piatră,
 *  mai închis decât pereții, cât să se citească drept bază a clădirii. */
const CULOARE_SOCLU = 0x8e8a83;
/** Cât se retrage capătul drept al unui strat de sub finisaj în spatele
 *  capătului finisajului: peste rezoluția adâncimii de aproape (~1 mm). */
const SUB_FINISAJ_M = 0.005;

/** Câte cadre se desenează după o cerere (v. `cereCadru`). */
const CADRE_PE_CERERE = 3;
/** Și un cadru la atâtea milisecunde oricum, ca plasă: o schimbare care n-a
 *  cerut cadru apare cel târziu atunci, nu niciodată. */
const BATAIE_MS = 1000;
const CULOARE_TARE = 0x00e0ff;

/** Materialul evidențierii: aceleași poziții ca elementul, `LessEqualDepth`,
 *  deci se desenează peste el fără să pâlpâie. */
const umplereAles = (tare: boolean, opacitate = 0.55) =>
  new THREE.MeshBasicMaterial({ color: tare ? CULOARE_TARE : CULOARE_ALES, transparent: true,
                                opacity: tare ? 0.82 : opacitate, depthFunc: THREE.LessEqualDepth,
                                depthWrite: false, side: THREE.DoubleSide });

/** Copiile triunghiurilor alese, pe plasă (`null` = plasa întreagă), puse în
 *  grup cu umplerea și — dacă e dat — conturul văzut și prin ce stă în față
 *  (umplerea singură se amesteca cu culoarea elementului: portocaliu + cyan =
 *  un verde șters). */
function evidentiaza(grup: THREE.Group, deArs: { dispose(): void }[],
                     pePlasa: Map<THREE.Mesh, [number, number][] | null>,
                     umplere: THREE.Material, contur: THREE.Material | null) {
  for (const [m, b] of pePlasa) {
    const g = b === null ? m.geometry.clone() : triunghiurileDin(m.geometry, b);
    if (!g) continue;
    deArs.push(g);
    const mm = new THREE.Mesh(g, umplere);
    mm.renderOrder = 15;
    grup.add(mm);
    if (contur && triunghiuri(g) <= 60000) {
      const eg = new THREE.EdgesGeometry(g, 25);
      deArs.push(eg);
      const ml = new THREE.LineSegments(eg, contur);
      ml.renderOrder = 18;
      grup.add(ml);
    }
  }
}

function pePlasa(l: readonly Intrare[]): Map<THREE.Mesh, [number, number][] | null> {
  const out = new Map<THREE.Mesh, [number, number][] | null>();
  for (const x of l) {
    if (x.n < 0) { out.set(x.mesh, null); continue; }
    const v = out.get(x.mesh);
    if (v === null) continue;
    out.set(x.mesh, [...(v ?? []), [x.start, x.n]]);
  }
  return out;
}

interface DesenZona { scoate(): void }

/** O ZONĂ PE MACHETĂ = ELEMENTELE EI (Cătălin, 13.10.2026: dreptunghiul
 *  plutitor «se vede ciudat»): elementele bucată întregi, suprafețele continue
 *  tăiate de două planuri la marginile zonei — exact ce va schimba agentul —
 *  și o linie punctată pe față, pe marginea zonei. `previz` = în timpul
 *  tragerii: fără contururi (costă), mai transparent. */
function deseneazaZona(st: StareAlegere, z: ZonaMacheta, previz: boolean, tare = false): DesenZona {
  st.info ??= infoElemente(st.fatade);
  st.intrari ??= intrarile(st.plase);
  const grup = new THREE.Group();
  grup.renderOrder = 15;
  const deArs: { dispose(): void }[] = [];
  const umplere = umplereAles(tare, previz ? 0.45 : 0.55);
  const contur = new THREE.LineBasicMaterial({ color: CULOARE_TARE, depthTest: false, transparent: true,
                                               opacity: 0.95 });
  const linie = new THREE.LineDashedMaterial({ color: CULOARE_TARE, dashSize: 0.45, gapSize: 0.25,
                                               depthTest: false, transparent: true, opacity: 0.95 });
  deArs.push(umplere, contur, linie);
  const { intregi, taiate } = aleseInZona(st.intrari, st.info, st.fete, z);
  evidentiaza(grup, deArs, pePlasa(intregi), umplere, previz ? null : contur);
  for (const b of z.bucati) {
    const f = st.fete.get(b.face);
    if (!f) continue;
    const l = taiate.get(b.face);
    if (l?.length) {
      const mat = umplere.clone();
      mat.clippingPlanes = planurileBucatii(f, b).map(planInLume);
      deArs.push(mat);
      evidentiaza(grup, deArs, pePlasa(l), mat, null);
    }
    const gl = new THREE.BufferGeometry().setFromPoints(margineaBucatii(f, b, z.z0, z.z1));
    deArs.push(gl);
    const ls = new THREE.LineSegments(gl, linie);
    ls.computeLineDistances();
    ls.renderOrder = 19;
    grup.add(ls);
  }
  st.taietura.taie(grup);
  st.lume.add(grup);
  return { scoate: () => { st.lume.remove(grup); deArs.forEach((d) => d.dispose()); } };
}

export default function RandareSit3D({ scena, soare, vedere, selectie, zone, laAlegere, laZona, coloane,
                                       aprins, cote, coteValori, etaj = null, planuri, oras = null,
                                       laGata, cladire = true, atasate, laTinta, perNivel = false, stare = null }: Props) {
  const gazda = useRef<HTMLDivElement>(null);
  /** Citit la primul cadru, nu la construirea scenei: o funcție nouă nu
   *  reconstruiește nimic. */
  const laGataRef = useRef(laGata);
  laGataRef.current = laGata;
  const puneSoare = useRef<((alt: number, az: number) => void) | null>(null);
  /** Ultima poziție a soarelui, pentru scena care se reconstruiește fără ca ora să
   *  se fi mișcat (v. sfârșitul efectului scenei). */
  const soareRef = useRef(soare);
  soareRef.current = soare;
  const puneVedere = useRef<((v: Vedere) => void) | null>(null);
  /** CAMERA SUPRAVIEȚUIEȘTE RECONSTRUCȚIEI. Scena se reface din temelii la
   *  fiecare document nou (jobul rescrie soluția pe parcurs), iar camera și
   *  controalele trăiesc în efect — deci la fiecare refacere omul se trezea
   *  întors la presetul «ansamblu», din locul în care tocmai se uita
   *  (28.09.2026). Se ține poziția și ținta la demontare și se pun la loc la
   *  montarea următoare; presetul se aplică doar prima dată sau la cerere. */
  const camTinuta = useRef<{ poz: THREE.Vector3; tinta: THREE.Vector3; fov: number } | null>(null);
  /** FĂRĂ PÂLPÂIRE LA RECITIRE: pânza scenei vechi rămâne pe ecran până când
   *  cea nouă a desenat primul cadru. Scoasă la demontare, între cele două
   *  rămânea un cadru gol — fațada schimbată din discuție clipea (Cătălin,
   *  6.10.2026). Demontarea lasă aici închiderea; montarea următoare o cheamă
   *  după primul ei cadru, iar dacă nu mai vine nicio montare (omul a plecat),
   *  o microsarcină o cheamă oricum. */
  const deInchis = useRef<(() => void) | null>(null);
  /** RANDORUL ȘI PÂNZA SUPRAVIEȚUIESC SCENEI. Un document nou (jobul care își
   *  rescrie soluția, parcarea, fațada) reface scena, dar nu mai deschide un
   *  context WebGL nou: același randor desenează scena următoare pe aceeași
   *  pânză, care ține ultimul cadru al celei vechi până la primul cadru al celei
   *  noi. Un context pe document costa compilarea tuturor shaderelor de la
   *  capăt, iar după câteva zeci de reconstruiri browserul începea să le
   *  piardă pe cele vechi (Cătălin, 13.10.2026: «mi se dă refresh la scenă»).
   *  Se eliberează numai la demontarea machetei. */
  const randTinut = useRef<THREE.WebGLRenderer | null>(null);
  useEffect(() => () => {
    const r = randTinut.current;
    randTinut.current = null;
    if (!r) return;
    r.dispose();
    r.domElement.remove();
  }, []);
  /** Scena de acum, pentru evidențiere (v. efectul selecției). */
  const alegere = useRef<StareAlegere | null>(null);
  /** DESEN LA NEVOIE: macheta nu se redesenează la fiecare cadru al ecranului,
   *  ci numai când ceva s-a schimbat — camera, soarele, scena, selecția, mărimea
   *  pânzei. Pe o mașină fără placă video, o machetă care stă pe loc nu mai
   *  ține procesorul ocupat. Cine schimbă ceva în scenă din afara buclei cheamă
   *  `cereCadru.current()`; orice randare a componentei o cheamă singură (v.
   *  ultimul efect). */
  const cereCadru = useRef<() => void>(() => undefined);
  /** Cârligele de clic, citite la eveniment (props care se schimbă fără să
   *  reconstruiască scena). */
  const cuAlegere = useRef({ laAlegere, laZona, coloane, laTinta, atasate, cladire });
  cuAlegere.current = { laAlegere, laZona, coloane, laTinta, atasate, cladire };
  /** Planurile etajelor, citite la construirea scenei (se schimbă odată cu ea). */
  const planuriRef = useRef(planuri);
  planuriRef.current = planuri;
  /** Apartamentul de sub mouse, pe etajul tăiat, și unde e mouse-ul. */
  const [peste, setPeste] = useState<{ a: ApartamentEtaj; x: number; y: number } | null>(null);

  useEffect(() => {
    const el = gazda.current;
    if (!el) return;
    let viu = true;

    // ADÂNCIME LOGARITMICĂ. Camera stă la 100–300 m de fațade, iar pe ele stau
    // suprafețe la 11–17 cm una de alta (geam în gol, finisaj peste corp, brâu
    // peste finisaj). Cu un tampon liniar de adâncime pe un interval de la
    // 0,5 m la câțiva kilometri, rezoluția la 200 m e de ordinul centimetrilor
    // — sau al metrului pe un tampon de 16 biți — și fețele apropiate se bat
    // între ele: finisajul pâlpâia cu corpul de dedesubt, mai ales unde
    // culorile diferă (parterul). Logaritmic, rezoluția e proporțională cu
    // distanța, nu cu pătratul ei, și milimetrii la 200 m rămân milimetri.
    //
    // UN SINGUR RANDOR PE MACHETĂ, nu unul pe scenă: v. `randTinut`.
    const rand = (randTinut.current?.domElement.parentNode === el ? randTinut.current : null) ?? (() => {
      const r = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true });
      r.setPixelRatio(Math.min(devicePixelRatio, 2));
      r.shadowMap.enabled = true;
      r.shadowMap.type = THREE.PCFShadowMap;   // PCFSoft e deprecat în 0.185
      r.toneMapping = THREE.ACESFilmicToneMapping;
      r.toneMappingExposure = 1.0;
      el.appendChild(r.domElement);
      r.domElement.style.display = "block";
      r.domElement.style.position = "absolute";
      r.domElement.style.inset = "0";
      randTinut.current = r;
      return r;
    })();

    const s3 = new THREE.Scene();
    // CADRUL DREPT: tot ce e al amplasamentului stă în `lume`, întors pe y, ca
    // macheta să nu iasă în oglindă (v. `oglinda3d`). Cerul e o sferă, rămâne afară.
    const lume = lumeDreapta();
    s3.add(lume);
    const { cadru } = scena;
    const R = cadru.raza;

    // ------------------------------------------------------------------ cerul
    const uni = {
      sus: { value: new THREE.Vector3(0.2, 0.45, 0.75) },
      jos: { value: new THREE.Vector3(0.8, 0.85, 0.9) },
      dirSoare: { value: new THREE.Vector3(0, 0, 1) },
      culSoare: { value: new THREE.Vector3(1, 0.97, 0.92) },
      taria: { value: 1 },
    };
    const matCer = new THREE.ShaderMaterial({
      uniforms: uni, vertexShader: VS_CER, fragmentShader: FS_CER,
      side: THREE.BackSide, depthWrite: false,
    });
    const geoCer = new THREE.SphereGeometry(Math.max(1200, R * 26), 32, 20);
    s3.add(new THREE.Mesh(geoCer, matCer));

    // Mediul se coace DIN ACELAȘI CER, ca lumina indirectă să vină de unde vine
    // și cea directă. O hartă fotografică ar aduce alt soare în scenă.
    //
    // SFERA PENTRU PMREM E MICĂ, și nu e o economie. `fromScene` randează cu
    // near 0,1 și far 100 dacă nu-i spui altceva, iar sfera de fundal stă la
    // 2.600 m: cădea întreagă dincolo de planul îndepărtat, deci mediul ieșea
    // GOL. `environmentIntensity` înmulțea nimicul, iar lipsa se citea ca „scena
    // e cam plată", nu ca o eroare. Shaderul lucrează pe direcție normalizată,
    // deci o sferă de cinci metri dă exact aceeași imagine.
    const pmrem = new THREE.PMREMGenerator(rand);
    const geoCerMic = new THREE.SphereGeometry(5, 32, 20);
    const cerScena = new THREE.Scene();
    cerScena.add(new THREE.Mesh(geoCerMic, matCer));
    // ȚINTA, nu textura ei: `texture.dispose()` pe ținta unui PMREM nu-i
    // eliberează memoria — rămânea câte un mediu pe GPU la fiecare mișcare a
    // soarelui și la fiecare scenă refăcută pe același randor (v. `randTinut`)
    let mediu: THREE.WebGLRenderTarget | null = null;

    // ---------------------------------------------------------------- lumină
    const soareL = new THREE.DirectionalLight(0xfff2e0, 3.0);
    soareL.castShadow = true;
    soareL.shadow.mapSize.set(2048, 2048);
    soareL.shadow.radius = 3;
    soareL.shadow.bias = -0.0006;
    soareL.shadow.normalBias = 0.04;
    // CAMERA DE UMBRĂ SE REÎNCADREAZĂ LA FIECARE MUTARE A SOARELUI, în
    // `puneSoare` — și asta e altceva decât a-i scrie marginile o dată.
    //
    // `LightShadow.updateMatrices()` NU cheamă `updateProjectionMatrix()`. Cine
    // scrie `left/right/top/bottom` pe camera ortografică și se oprește acolo
    // lasă în urmă matricea din constructor: ±5 m, far 500. Pe un amplasament de
    // două sute de metri, harta de umbre acoperă un petic de zece pe zece în
    // jurul țintei — adică nimic din ce se vede — iar restul iese NEUMBRIT.
    // Nu aruncă, nu se plânge: scena arată luminată și fără umbre, exact ca o
    // scenă fără soare.
    const sc = soareL.shadow.camera as THREE.OrthographicCamera;
    const hMax = Math.max(1, ...scena.corpuri.map((c) => c.h));
    soareL.target.position.set(cadru.cx, cadru.cy, 0);
    lume.add(soareL, soareL.target);
    const cerLumina = new THREE.HemisphereLight(0xcfe0f2, 0x7d7a63, 0.55);
    lume.add(cerLumina);

    // ------------------------------------------------------------- materiale
    const pavele = texturaPavele();
    const cauciuc = texturaCauciuc();
    const M = {
      // terenul din jur, deschis (26.09.2026): la 0x8d9179 placa mare de sub
      // amplasament ținea tot fundalul verde-închis, iar clădirile se pierdeau
      teren: new THREE.MeshStandardMaterial({ color: 0xc3c8bd, roughness: 1 }),
      iarba: new THREE.MeshStandardMaterial({ color: VERDE_SOL, roughness: 1 }),
      iarbaSlab: new THREE.MeshStandardMaterial({ color: VERDE_SUBSOL, roughness: 1 }),
      trotuar: new THREE.MeshStandardMaterial({ map: pavele, roughness: 0.95 }),
      asfalt: new THREE.MeshStandardMaterial({ color: 0x54534f, roughness: 0.9 }),
      // platforma de gunoi: dală de beton; locul de joacă: covor de cauciuc cărămiziu
      beton: new THREE.MeshStandardMaterial({ color: 0xb9b5ab, roughness: 1 }),
      cauciuc: new THREE.MeshStandardMaterial({ map: cauciuc, roughness: 1 }),
      // rampa: asfalt mai închis, ca să se citească coborârea; pereții ei de
      // sprijin, beton gri, pe amândouă fețele (se văd dinspre groapă)
      asfaltRampa: new THREE.MeshStandardMaterial({ color: 0x33333a, roughness: 0.95 }),
      // beton ÎNCHIS: peretele din partea opusă se vede de sus pe toată adâncimea
      // (zero la gură, trei metri la capăt) și, deschis, făcea groapa să pară albă
      zidRampa: new THREE.MeshStandardMaterial({ color: 0x3f4145, roughness: 0.95, side: THREE.DoubleSide }),
      // portalul: gura subsolului, negru mat — nu reflectă nimic, ca o deschidere
      portalRampa: new THREE.MeshStandardMaterial({ color: 0x08080a, roughness: 1.0, side: THREE.DoubleSide }),
      // masca gropii: nu desenează nimic, scrie doar adâncimea (v. rampele)
      mascaGroapa: new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true, side: THREE.DoubleSide }),
      perete: new THREE.MeshStandardMaterial({ color: 0xf1eee7, roughness: 0.92 }),
      peretePlin: new THREE.MeshStandardMaterial({ color: 0xf1eee7, roughness: 0.92 }),
      placa: new THREE.MeshStandardMaterial({ color: 0xe8e3d9, roughness: 0.9 }),
      // COPERTINA DE METAL a intrării: oțel vopsit antracit, ca tâmplăria blocului
      copertinaMetal: new THREE.MeshStandardMaterial({ color: 0x2f3236, roughness: 0.45, metalness: 0.5 }),
      // SPOTUL: lumina caldă a LED-ului, neluminată (se vede și în umbra copertinei)
      spot: new THREE.MeshBasicMaterial({ color: 0xfff1d2, side: THREE.DoubleSide }),
      toc: new THREE.MeshStandardMaterial({ color: 0xb9b2a5, roughness: 0.85,
                                            side: THREE.DoubleSide }),
      // Balustrada din lamele/metal: se vede PRIN ea, ca printr-un grilaj.
      // Opacă, fiecare balcon devenea o cutie neagră lipită de fațadă — un
      // șir de cutii negre pe toată înălțimea, care nu semăna cu nimic.
      lamele: new THREE.MeshStandardMaterial({ color: 0x2a2d31, roughness: 0.55,
                                               metalness: 0.3, transparent: true,
                                               opacity: 0.55, depthWrite: false,
                                               side: THREE.DoubleSide }),
      // STICLA FĂRĂ CULOARE ÎN STIL: clară, abia nuanțată. Cu 0x9fb3bd la 45 %
      // și metal 0,35 se citea ca o foaie fumurie, iar pe fundalul închis al
      // încăperii — neagră («sticla clară, nu neagră», Demo-UTR6, 6.10.2026).
      // Cu culoare în stil, sticla o ia pe a ei (`sticlaIn`).
      parapet: new THREE.MeshStandardMaterial({ color: CULOARE_STICLA_IMPLICITA, roughness: 0.05,
                                                metalness: 0.1, transparent: true,
                                                opacity: OPACITATE_STICLA, depthWrite: false,
                                                side: THREE.DoubleSide }),
      // DoubleSide ca plasă de siguranță: un dreptunghi de trei centimetri care
      // și-ar întoarce normala ar dispărea cu totul, iar lipsa unei ferestre nu
      // se vede ca defect — se vede ca fațadă oarbă.
      // Rugos, nu oglindă: cu 0,12 geamul oglindea cerul și pe fața orientată
      // spre lumină ferestrele ieșeau albe, iar pe cea din umbră negre — două
      // clădiri din același stil păreau vopsite diferit.
      // GEAMUL E STICLĂ, NU METAL. Ca „metal" (metalness 0,6) oglindea cerul
      // ca o tablă: pe fețele întoarse spre cer ieșea gri, pe celelalte negru,
      // și două corpuri din același stil aveau ferestre de culori diferite.
      // Sticla e un dielectric: puțin reflexivă (Fresnel, mai mult la unghiuri
      // razante), altfel transparentă — ceea ce se vede e ÎNCĂPEREA din spatele
      // ei (`interior`, întunecată) plus cerul oglindit pe deasupra.
      // GEAMUL E ACELAȘI LA TOATE GOLURILE: sticlă albastru-deschis, opacă,
      // care oglindește cerul — de afară, ziua, aşa se vede. Transparent, cu
      // încăperea neagră în spate, o parte din ferestre ieșeau negre și altele
      // gri-maro după cum le prindea lumina și reflexia, «culori distincte»
      // pe aceeași față (Cătălin, 29.09.2026).
      // …și cu reflexia ținută în frâu: cu oglindirea la 1,2 ferestrele de pe
      // fețele întoarse spre cer ieșeau albe, «fără culoare», iar cele din
      // umbră albastre — pe același corp (fețele interioare ale U-ului).
      // ACEEAȘI CULOARE PE ORICE FAȚĂ: pe fețele din umbră (interiorul U-ului)
      // geamul ieșea gri, pe cele la soare albastru deschis. Nuanța e dată în
      // cea mai mare parte de o emisie proprie, nu de lumină și reflexie, deci
      // nu mai depinde de orientare; reflexia rămâne o urmă.
      // NELUMINAT (`MeshBasicMaterial`): culoarea e culoarea, pe orice față,
      // la orice soare — nici umbra, nici reflexia nu o schimbă. Cu emisie
      // peste un material luminat ieșea aproape alb (măsurat: RGB ≈ 210) și
      // tot diferit de la o față la alta (29.09.2026).
      // …ȘI PUȚIN STRĂVEZIU (30 %): prin el se văd, întunecate, cerceveaua din
      // spate și mânerul de pe canatul mobil — ce cere macheta de la o fereastră
      // «cu toc, glaf și mâner» (Cătălin, 30.09.2026). Culoarea rămâne cea a
      // sticlei, neluminată, pe orice față: ce se adaugă e același fundal
      // întunecat la toate ferestrele.
      // …PÂNĂ LA 15.10.2026: acum sticla e sticlă — oglindește cerul din unghi
      // (Fresnel) și lasă să se vadă, din față, camera din spatele ei
      // («o transparență, să se vadă foarte puțin în interior», Cătălin)
      geam: materialGeam(0x8fb0c4, 0.3),
      // mânerul: metal deschis, mat
      maner: new THREE.MeshStandardMaterial({ color: 0xd8d8d6, roughness: 0.45, metalness: 0.4 }),
      // camerele închipuite din spatele geamurilor (v. `camera3d`)
      camera: materialCamera(),
      lot: new THREE.LineBasicMaterial({ color: 0x0a6462 }),
    };
    const deArs: { dispose(): void }[] = [geoCer, geoCerMic, matCer, pavele, cauciuc, ...Object.values(M)];

    // TERENUL ȘI CE STĂ PE EL — curtea, aleile, rampele, parcarea la sol,
    // pomii, mașinile — într-un grup coborât cu soclul (`scena.ridicare`):
    // ±0,00 e pardoseala parterului, iar terenul stă sub ea. Clădirea rămâne pe
    // cotele ei, deci selecția, tăietura pe etaj și cotele nu se schimbă.
    const sol = new THREE.Group();
    sol.name = "sol";
    sol.position.z = -scena.ridicare;
    lume.add(sol);
    /** STRATURILE SOLULUI, etichetate pe măsură ce se adaugă (terenul, iarba,
     *  aleile…), ca starea pe săptămână să le poată aprinde pe rând. */
    let solDe = 0;
    const marcheaza = (strat: string) => {
      for (const o of sol.children.slice(solDe)) o.userData.strat = strat;
      solDe = sol.children.length;
    };

    /** O suprafață plană la cota `z` față de teren. Cotele se dau crescător pe
     *  straturi, ca două suprafețe suprapuse să nu pâlpâie una prin alta. */
    const plan = (s: Suprafata, mat: THREE.Material, z: number, primesteUmbra = true) => {
      const f = forma(s);
      if (!f) return;
      const g = new THREE.ShapeGeometry(f);
      g.translate(0, 0, z);
      deArs.push(g);
      const m = new THREE.Mesh(g, mat);
      m.receiveShadow = primesteUmbra;
      sol.add(m);
    };

    // -------------------------------------------------------------- terenul
    // O placă mare sub tot, ca amplasamentul să nu plutească peste cer.
    const geoTeren = new THREE.PlaneGeometry(R * 24, R * 24);
    geoTeren.translate(cadru.cx, cadru.cy, -0.05);
    deArs.push(geoTeren);
    const teren = new THREE.Mesh(geoTeren, M.teren);
    teren.receiveShadow = true;
    sol.add(teren);
    marcheaza("teren");

    if (scena.lot.length >= 3) plan({ poly: scena.lot, holes: [] }, M.iarba, 0.01);
    marcheaza("iarba");
    // ASFALTUL PESTE TROTUAR: un traseu pietonal trece carosabilul ca trotuar
    // obișnuit, iar sub cota asfaltului bucata lui de pe drum dispare și rămâne
    // doar zebra trecerii, vopsită pe asfalt (Oana, 01.11.2026: «fără
    // background gri sub trecerea de pietoni»).
    scena.trotuare.forEach((s) => plan(s, M.trotuar, 0.02));
    marcheaza("trotuare");
    scena.drumuri.forEach((s) => plan(s, M.asfalt, 0.03));
    marcheaza("drumuri");
    scena.verde.forEach((s) => plan(s, M.iarba, 0.04));
    scena.verdePeSlab.forEach((s) => plan(s, M.iarbaSlab, 0.04));
    marcheaza("verde");
    // ZONELE EXTERIOARE, peste verde: dala ghenei, cauciucul locului de joacă,
    // pavajul piațetei; mobilierul lor vine din `zone3d` (mai jos, lângă pomi)
    scena.zone.forEach((z) => plan({ poly: z.poly, holes: [] },
                                   z.kind === "waste_platform" ? M.beton : z.kind === "playground" ? M.cauciuc : M.trotuar,
                                   0.045));
    marcheaza("zone");

    // RAMPELE COBOARĂ: placa pleacă de la gură (cota aleii) și cade pe panta
    // documentului (altfel 15 %) spre capătul adânc, cel mult la cota
    // subsolului; asfaltul ei e mai închis decât al aleii, pe laturi urcă
    // pereții de sprijin până la cota terenului, iar peretele din capătul adânc
    // e negru: gura subsolului (Cătălin, 22.10.2026).
    //
    // GROAPA SE FACE PRIN ADÂNCIME, NU PRIN GĂURI ÎN PLANURI. O gaură tăiată
    // într-un plan pe care rampa îl calcă doar parțial (trotuarul de la gură,
    // verdele de peste placă) strică triangularea — triunghiuri peste rampă și
    // dungi pe tot verdele. În loc: placa și pereții se desenează PRIMELE, apoi
    // o MASCĂ invizibilă pe conturul rampei, puțin peste cota pavajelor, care
    // scrie doar adâncimea; planurile de la sol, desenate după ea, pică testul
    // de adâncime exact în dreptul rampei și lasă să se vadă placa de sub ele.
    // Bordura (până la 12 cm) rămâne deasupra măștii. Strict zonal: în afara
    // conturului rampei nimic nu se schimbă.
    {
      const PANTA = 0.15, ADANC_MAX = 3.2, COTA_GURA = 0.05, MASCA_Z = 0.07;
      for (const r of scena.rampe) {
        if (r.poly.length < 3 || !r.a || !r.b) continue;
        const ax = r.a[0] - r.b[0], ay = r.a[1] - r.b[1];
        const L2 = ax * ax + ay * ay;
        if (L2 < 1e-6) continue;
        const panta = Number.isFinite(r.slope) && (r.slope as number) > 0 ? (r.slope as number) : PANTA;
        const adanc = Math.min(ADANC_MAX, panta * Math.sqrt(L2));
        const cota = (p: number[]) => {
          const t = Math.max(0, Math.min(1, ((p[0] - r.b[0]) * ax + (p[1] - r.b[1]) * ay) / L2));
          return COTA_GURA - adanc * t;
        };
        const f = forma({ poly: r.poly, holes: [] });
        if (!f) continue;
        // placa, triangulată ca formă și apoi coborâtă vârf cu vârf
        const g = new THREE.ShapeGeometry(f);
        const pos = g.getAttribute("position") as THREE.BufferAttribute;
        for (let i = 0; i < pos.count; i++) pos.setZ(i, cota([pos.getX(i), pos.getY(i)]));
        pos.needsUpdate = true;
        g.computeVertexNormals();
        deArs.push(g);
        const placa = new THREE.Mesh(g, M.asfaltRampa);
        placa.receiveShadow = true;
        placa.renderOrder = -3;
        sol.add(placa);
        // pereții: pe fiecare latură, de pe marginea plăcii până la cota gurii;
        // latura cea mai apropiată de capătul adânc al axei e PORTALUL, negru
        const poz: number[] = [], portal: number[] = [];
        let iPortal = -1, dPortal = Infinity;
        for (let i = 0; i < r.poly.length; i++) {
          const p0 = r.poly[i], p1 = r.poly[(i + 1) % r.poly.length];
          const d = Math.hypot((p0[0] + p1[0]) / 2 - r.a[0], (p0[1] + p1[1]) / 2 - r.a[1]);
          if (d < dPortal) { dPortal = d; iPortal = i; }
        }
        for (let i = 0; i < r.poly.length; i++) {
          const p0 = r.poly[i], p1 = r.poly[(i + 1) % r.poly.length];
          const z0 = cota(p0), z1 = cota(p1);
          if (Math.max(COTA_GURA - z0, COTA_GURA - z1) < 0.02) continue;
          (i === iPortal ? portal : poz).push(
            p0[0], p0[1], z0, p1[0], p1[1], z1, p1[0], p1[1], COTA_GURA,
            p0[0], p0[1], z0, p1[0], p1[1], COTA_GURA, p0[0], p0[1], COTA_GURA);
        }
        for (const [v, mat] of [[poz, M.zidRampa], [portal, M.portalRampa]] as const) {
          if (!v.length) continue;
          const gz = new THREE.BufferGeometry();
          gz.setAttribute("position", new THREE.Float32BufferAttribute(v, 3));
          gz.computeVertexNormals();
          deArs.push(gz);
          const z = new THREE.Mesh(gz, mat);
          z.castShadow = true; z.receiveShadow = true;
          z.renderOrder = -3;
          sol.add(z);
        }
        // masca: adâncime fără culoare, pe conturul rampei, peste pavaje
        const gm = new THREE.ShapeGeometry(f);
        gm.translate(0, 0, MASCA_Z);
        deArs.push(gm);
        const masca = new THREE.Mesh(gm, M.mascaGroapa);
        masca.renderOrder = -2;
        sol.add(masca);
      }
    }

    // VOPSEAUA DE PE ASFALT, toată într-o singură geometrie: fâșii plate
    // alb-gălbui, puțin peste asfalt ca să nu se bată cu el în același plan.
    marcheaza("rampe");
    const vopsea: number[] = [];
    const Z_VOPSEA = 0.065;
    const fasie = (x0: number, y0: number, x1: number, y1: number, lat: number) => {
      const L = Math.hypot(x1 - x0, y1 - y0);
      if (L < 1e-6) return;
      const nx = -(y1 - y0) / L * lat / 2, ny = (x1 - x0) / L * lat / 2;
      vopsea.push(x0 + nx, y0 + ny, Z_VOPSEA, x1 + nx, y1 + ny, Z_VOPSEA, x1 - nx, y1 - ny, Z_VOPSEA,
                  x0 + nx, y0 + ny, Z_VOPSEA, x1 - nx, y1 - ny, Z_VOPSEA, x0 - nx, y0 - ny, Z_VOPSEA);
    };

    // MARCAJUL DINTRE BENZI: axa punctată a fiecărui drum cu dublu sens, cu
    // arcele ei la colțuri și la T — aceleași linii ca pe planșă
    // (`scena.marcaje`), ca liniuțe de 1,6 m cu 1,6 m gol, late de 12 cm.
    {
      const DUNGA = 1.6, GOL = 1.6, LAT = 0.12;
      for (const linie of scena.marcaje) {
        const pts = linie.filter((q) => Number.isFinite(q?.[0]) && Number.isFinite(q?.[1]));
        let rest = 0;                // cât mai e din liniuța/golul curent
        let vopsit = true;
        for (let i = 0; i + 1 < pts.length; i++) {
          const [ax, ay] = pts[i], [bx, by] = pts[i + 1];
          const L = Math.hypot(bx - ax, by - ay);
          if (L < 1e-6) continue;
          const ux = (bx - ax) / L, uy = (by - ay) / L;
          let s = 0;
          while (s < L - 1e-6) {
            // un rest de virgulă mobilă (1e-17) nu mai avansa `s`, iar bucla nu se oprea
            if (rest <= 1e-6) { vopsit = !vopsit; rest = vopsit ? DUNGA : GOL; }
            const e = Math.min(L, s + rest);
            if (vopsit) fasie(ax + ux * s, ay + uy * s, ax + ux * e, ay + uy * e, LAT);
            rest -= e - s;
            s = e;
          }
        }
      }
    }

    // ZEBRELE trecerilor de pietoni: dungile planșei (`scena.zebre`, tăiate la
    // asfalt acolo), vopsite ca patrulatere la cota marcajelor
    for (const d of scena.zebre) {
      if (d.length < 4) continue;
      const [a, b, c, e] = d;
      vopsea.push(a[0], a[1], Z_VOPSEA, b[0], b[1], Z_VOPSEA, c[0], c[1], Z_VOPSEA,
                  a[0], a[1], Z_VOPSEA, c[0], c[1], Z_VOPSEA, e[0], e[1], Z_VOPSEA);
    }

    // SĂGEȚILE DE SENS ale aleilor cu sens unic (`scena.sageti`): aceeași săgeată
    // ca pe planșă (`RoadArrowMark`, plăcuța de 1,20 × 2,55 m), în cadrul glifei
    // −y e sensul de mers și `deg` o rotește ca `rotate(deg)` din SVG; cadrul
    // documentului e același, deci rotația e aceeași matrice
    {
      const H = 2.55, b = -H / 2, t = H / 2, shaft = 1.2 * 0.17, head = 1.2 * 0.36;
      for (const s of scena.sageti) {
        const a = (s.deg * Math.PI) / 180, c = Math.cos(a), sn = Math.sin(a);
        const w = (u: number, v: number): number[] => [s.x + c * u - sn * v, s.y + sn * u + c * v, Z_VOPSEA];
        const tri = (p: number[], q: number[], r: number[]) => vopsea.push(...p, ...q, ...r);
        // vârful: triunghiul de la vârf (b + 0,16) până la umeri (b + 0,92)
        tri(w(0, b + 0.16), w(head, b + 0.92), w(-head, b + 0.92));
        // tija: dreptunghiul de la umeri până la baza plăcuței (t − 0,18)
        tri(w(-shaft, b + 0.92), w(shaft, b + 0.92), w(shaft, t - 0.18));
        tri(w(-shaft, b + 0.92), w(shaft, t - 0.18), w(-shaft, t - 0.18));
      }
    }

    // Locurile de parcare: platforma și CHENARUL vopsit pe ea — fâșii de 8 cm
    // pe dinăuntrul fiecărui loc, deci două locuri vecine dau o linie de 16 cm,
    // nu două fâșii una peste alta (Cătălin, 21.10.2026: «benzi albe în jurul
    // locurilor de parcare la suprateran»).
    scena.locuri.forEach((l) => {
      const a = (l.deg * Math.PI) / 180;
      const c = Math.cos(a), s = Math.sin(a);
      const col = (u: number, v: number): number[] =>
        [l.cx + c * (u * l.w / 2) - s * (v * l.d / 2),
         l.cy + s * (u * l.w / 2) + c * (v * l.d / 2)];
      plan({ poly: [col(-1, -1), col(1, -1), col(1, 1), col(-1, 1)], holes: [] },
           M.asfalt, 0.06);
      const LAT = 0.08, iu = 1 - LAT / l.w, iv = 1 - LAT / l.d;    // chenarul, pe dinăuntru
      const colturi = [col(-iu, -iv), col(iu, -iv), col(iu, iv), col(-iu, iv)];
      for (let k = 0; k < 4; k++) {
        const [x0, y0] = colturi[k], [x1, y1] = colturi[(k + 1) % 4];
        fasie(x0, y0, x1, y1, LAT);
      }
    });
    marcheaza("parcare");

    if (vopsea.length) {
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.Float32BufferAttribute(vopsea, 3));
      g.computeVertexNormals();
      deArs.push(g);
      const alb = new THREE.MeshBasicMaterial({ color: 0xe8e4d0, side: THREE.DoubleSide });
      deArs.push(alb);
      sol.add(new THREE.Mesh(g, alb));
    }
    marcheaza("vopsea");

    // BORDURA, PIATRĂ CU PIATRĂ: pe muchiile exterioare ale asfaltului
    // (`scena.borduri`, v. `borduri.ts`), pietre de `PIATRA` m lungime, 20 cm
    // lățime și 10 cm înălțime, cu un rost de `ROST` între ele, călare pe
    // muchie — 10 cm pe asfalt, 10 pe verde. Fiecare piatră e un bloc drept pe
    // coarda ei, deci pe o curbă pietrele urmează arcul ca o bordură zidită,
    // nu o panglică trasă (Cătălin, 22.10.2026: «piatră cu piatră, 30 cm»).
    {
      const LAT = 0.20, H = 0.10, Z0 = 0.02, PIATRA = 0.30, ROST = 0.015;
      const poz: number[] = [];
      const quad = (a: number[], b: number[], c: number[], d: number[]) =>
        poz.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], a[0], a[1], a[2], c[0], c[1], c[2], d[0], d[1], d[2]);
      const piatra = (a: number[], b: number[]) => {
        const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (L < 0.03) return;
        const nx = -(b[1] - a[1]) / L * LAT / 2, ny = (b[0] - a[0]) / L * LAT / 2;
        const aL = [a[0] + nx, a[1] + ny], aR = [a[0] - nx, a[1] - ny];
        const bL = [b[0] + nx, b[1] + ny], bR = [b[0] - nx, b[1] - ny];
        const sus = Z0 + H;
        quad([aL[0], aL[1], sus], [bL[0], bL[1], sus], [bR[0], bR[1], sus], [aR[0], aR[1], sus]);     // fața de sus
        quad([aL[0], aL[1], Z0], [bL[0], bL[1], Z0], [bL[0], bL[1], sus], [aL[0], aL[1], sus]);       // o latură
        quad([aR[0], aR[1], sus], [bR[0], bR[1], sus], [bR[0], bR[1], Z0], [aR[0], aR[1], Z0]);       // cealaltă
        quad([aR[0], aR[1], Z0], [aL[0], aL[1], Z0], [aL[0], aL[1], sus], [aR[0], aR[1], sus]);       // capetele: rostul se vede
        quad([bL[0], bL[1], Z0], [bR[0], bR[1], Z0], [bR[0], bR[1], sus], [bL[0], bL[1], sus]);
      };
      for (const linie of scena.borduri) {
        const pts = linie.filter((q) => Number.isFinite(q?.[0]) && Number.isFinite(q?.[1]));
        if (pts.length < 2) continue;
        // lungimile cumulate, ca pietrele să se taie pe LUNGIMEA liniei, nu pe noduri
        const cum = [0];
        for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
        const total = cum[cum.length - 1];
        let seg = 0;
        const la = (d: number): number[] => {
          while (seg + 1 < cum.length - 1 && cum[seg + 1] < d) seg++;
          const l = cum[seg + 1] - cum[seg] || 1;
          const t = Math.max(0, Math.min(1, (d - cum[seg]) / l));
          return [pts[seg][0] + (pts[seg + 1][0] - pts[seg][0]) * t, pts[seg][1] + (pts[seg + 1][1] - pts[seg][1]) * t];
        };
        for (let d = 0; d < total - 0.03; d += PIATRA + ROST) {
          const e = Math.min(total, d + PIATRA);
          piatra(la(d), la(e));
        }
      }
      if (poz.length) {
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
        g.computeVertexNormals();
        deArs.push(g);
        const mat = new THREE.MeshStandardMaterial({ color: 0xf2f0ea, roughness: 0.85, side: THREE.DoubleSide });
        deArs.push(mat);
        const m = new THREE.Mesh(g, mat);
        m.castShadow = true; m.receiveShadow = true;
        sol.add(m);
      }
      marcheaza("borduri");
    }

    // ------------------------------------------------------------- corpurile
    // tot ce se adaugă de aici până la limita lotului e CLĂDIREA: pe ea se
    // pune planul tăieturii pe etaj (terenul, copacii și mașinile rămân)
    const inceputCladire = lume.children.length;
    // SELECȚIA: fiecare geometrie pusă într-o listă primește id-ul obiectului
    // desenat ACUM (`eidAcum`, pus de buclele fațadei mai jos); la lipire se
    // ține tabelul lor, ca un clic să găsească obiectul (v. `selectie3d`).
    let eidAcum: string | null = null;
    const lista = (): THREE.BufferGeometry[] => {
      const a: THREE.BufferGeometry[] = [];
      a.push = (...gs: THREE.BufferGeometry[]) => {
        if (eidAcum) for (const g of gs) if (g && !g.userData.eid) g.userData.eid = eidAcum;
        return Array.prototype.push.apply(a, gs);
      };
      return a;
    };
    let geoGeam = lista();
    let geoInterior = lista();
    let geoManer = lista();
    let nrGol = 0;
    // CORPUL SE RETRAGE CU 5 CM unde are fațadă. Panourile pornesc din planul
    // zidăriei, deci fața corpului cădea exact în planul din spate al pielii
    // și în planul capetelor de panou de la colțuri; două fețe coplanare se bat
    // pe aceiași pixeli oricât de fină e adâncimea. Retras, corpul nu mai are
    // nicio față în vreun plan al fațadei — și nu se vede, e în spatele ei.
    const cuFatada = new Set(scena.fatade.filter((f) => f.panouri.length).map((f) => f.corp));
    const corpMesh = (pts: number[][], z0: number, z1: number, id: string, nivel?: number,
                      fel: "corp" | "atic" = "corp") => {
      const f = forma({ poly: pts, holes: [] });
      if (!f || z1 - z0 < 0.02) return;
      const g = new THREE.ExtrudeGeometry(f, { depth: z1 - z0, bevelEnabled: false });
      g.translate(0, 0, z0);
      deArs.push(g);
      const m = new THREE.Mesh(g, M.perete);
      m.castShadow = true;
      m.receiveShadow = true;
      m.userData.corp = id;
      // o bucată de corp pe etaj se ascunde când etajul e tăiat (v. `TaieturaEtaj`)
      m.userData.corpEtaj = { sus: z1 };
      // nivelul bucății, pentru starea pe săptămână (`StareMacheta`)
      if (nivel != null) m.userData.corpNivel = { corp: id, nivel, fel };
      lume.add(m);
    };
    // SOCLUL: de la teren (`-scena.ridicare`) la ±0,00, volum SEPARAT de corpul
    // parterului — tăiat la parter, corpul etajului se ascunde, dar blocul nu
    // are voie să „coboare" pe teren — și în placajul de soclu al fațadei (cel
    // al corpului, `surface: plinth`), nu în albul corpului. Pe conturul
    // planșeului, deci retras sub pielea fațadei, cum stă un soclu.
    const materialeSoclu = new Map<number, THREE.Material>();
    const materialSoclu = (ci: number): THREE.Material => {
      const gata = materialeSoclu.get(ci);
      if (gata) return gata;
      const pn = scena.fatade.filter((f) => f.corp === ci)
        .flatMap((f) => f.panouri)
        .find((q) => q.surface === "plinth" && q.layer === "finish");
      const cul = pn?.colour && /^#?[0-9a-f]{6}$/i.test(pn.colour)
        ? new THREE.Color(pn.colour.startsWith("#") ? pn.colour : `#${pn.colour}`).getHex()
        : CULOARE_SOCLU;
      const tx = pn ? texturaMaterial(pn.material, cul) : null;
      let m: THREE.MeshStandardMaterial;
      if (tx) {
        // laturile unui volum extrudat au UV în metri: textura se repetă la
        // `latura_m`, pe o copie, ca panourile care o împart să nu se schimbe
        const map = tx.map.clone();
        map.wrapS = map.wrapT = THREE.RepeatWrapping;
        map.repeat.set(1 / tx.latura_m, 1 / tx.latura_m);
        map.needsUpdate = true;
        deArs.push(map);
        m = new THREE.MeshStandardMaterial({ map, roughness: tx.roughness, metalness: tx.metalness });
      } else {
        m = new THREE.MeshStandardMaterial({ color: cul, roughness: 0.9 });
      }
      deArs.push(m);
      materialeSoclu.set(ci, m);
      return m;
    };
    const soclu = (pts: number[][], ci: number, id: string) => {
      if (scena.ridicare < 0.02) return;
      const f = forma({ poly: pts, holes: [] });
      if (!f) return;
      const g = new THREE.ExtrudeGeometry(f, { depth: scena.ridicare, bevelEnabled: false });
      g.translate(0, 0, -scena.ridicare);
      deArs.push(g);
      const m = new THREE.Mesh(g, materialSoclu(ci));
      m.castShadow = true;
      m.receiveShadow = true;
      m.userData.corp = id;
      m.userData.corpEtaj = { sus: 0 };
      m.userData.corpNivel = { corp: id, nivel: 0, fel: "soclu" };
      lume.add(m);
    };
    // UN CORP FĂRĂ ARHITECTURĂ e un volum întreg, de la sol la acoperiș: n-are
    // etaje de arătat, iar tăiat ar rămâne o cutie goală pe dinăuntru — deci
    // nu se taie (materialul lui, separat, rămâne fără plan)
    const corpIntreg = (pts: number[][], h: number, id: string) => {
      const f = forma({ poly: pts, holes: [] });
      if (!f || h < 0.02) return;
      // de la teren: soclul face parte din corp
      const g = new THREE.ExtrudeGeometry(f, { depth: h + scena.ridicare, bevelEnabled: false });
      g.translate(0, 0, -scena.ridicare);
      deArs.push(g);
      const m = new THREE.Mesh(g, M.peretePlin);
      m.castShadow = true; m.receiveShadow = true;
      m.userData.corp = id;
      m.userData.netaiat = true;
      lume.add(m);
    };
    // CASELE DE SCARĂ DE EVACUARE ale parcajului (§35): cutii la sol, ca o construcție mică
    for (const sc of scena.scari) corpIntreg(sc.poly, sc.h, `scara:${sc.id}`);
    scena.corpuri.forEach((c, ci) => {
      // CORP CU ÎNĂLȚIMI DIFERITE: bucată cu bucată, fiecare la înălțimea ei
      // (`Corp3.trepte`). Bucățile sunt despărțite de rostul de 20 cm, deci
      // nicio față a uneia nu cade în planul alteia. Un corp uniform e o bucată.
      const piese = c.trepte?.length ? c.trepte : [{ pts: c.pts, h: c.h }];
      if (!cuFatada.has(ci)) { piese.forEach((p) => corpIntreg(p.pts, p.h, c.id)); return; }
      const niveluri = scena.fatade
        .filter((f) => f.corp === ci && !f.acoperis && f.inaltime > 0)
        .sort((a, b) => a.cota - b.cota);
      const placile = (n: Fatada3) => (n.parti?.length ? n.parti : [n.contur as number[][]]);
      const peContururi = niveluri.length > 0 && niveluri.every((n) => (n.contur?.length ?? 0) >= 3);
      // …SCOBIT la holul de intrare coborât la teren (`denivelate`, 3.12): ușa
      // e în soclu, iar în spatele ei e groapa holului, nu soclu plin.
      // PE CONTURUL PARTERULUI când arhitectura îl dă, ca volumele de mai jos:
      // bucata planului de situație e gabaritul corpului — pe copia unui modul
      // izolat, plicul cu balcoane cu tot — și soclul ridicat pe ea ieșea cât
      // balconul în fața fațadei, o bază pe care stătea blocul, cu ușa coborâtă
      // ascunsă în spatele ei.
      const gropi = scena.fatade.filter((f) => f.corp === ci && f.nivel === 0)
        .flatMap((f) => f.denivelate ?? []).filter((g) => g.cota < -0.05).map((g) => g.poly);
      const temelii = peContururi ? placile(niveluri[0]) : piese.map((p) => p.pts);
      // FĂRĂ SOCLU la un parter cu pardoseala la teren (comercial, G3): volumul
      // lui pornește chiar de la teren (`Math.min(z, niv.cota)` mai jos), iar un
      // soclu de piatră s-ar vedea prin cortină, în spatele ei
      const laTeren = niveluri.length > 0 && niveluri[0].cota <= -scena.ridicare + 0.05;
      if (!laTeren) for (const pts of temelii) soclu(gropi.length ? conturCuLogii(pts, gropi, 0) : pts, ci, c.id);
      // CU FAȚADĂ, CORPUL SE DESENEAZĂ PE NIVELURI: fiecare cu logiile lui
      // scobite (v. `conturCuLogii`), retras 5 cm sub piele. Ce rămâne peste
      // ultimul nivel (aticul) îl dă nivelul de terasă, mai jos.
      if (!niveluri.length) { piese.forEach((p) => corpMesh(insetInel(p.pts, 0.05), 0, p.h, c.id)); return; }
      // PE CONTURUL PLANȘEULUI FIECĂRUI NIVEL, când arhitectura îl dă: la un L
      // sau la o treaptă bucata planului de situație trece dincolo de fațada
      // arhitecturii — ridicată pe toate nivelurile, îngropa ferestrele de sub
      // terasă și lăsa un cub deasupra ei («cubul de pe terasă», Cătălin,
      // 2.10.2026). Nivelul retras își aduce singur conturul mai mic, deci
      // treapta și retragerea ies din contururi, nu din bucăți.
      // …ȘI PE FIECARE PLACĂ a lui, când planșeul stă în două (`Fatada3.parti`):
      // deasupra unei bucăți oprite între două care urcă, volumul e două
      // volume, cu acoperișul bucății joase între ele — nu un contur întins
      // peste el.
      if (peContururi) {
        let z = 0, ultim: number[][][] = placile(niveluri[0]);
        for (const niv of niveluri) {
          const sus = niv.cota + niv.inaltime;
          for (const pl of placile(niv)) corpMesh(conturCuLogii(pl, niv.logii, 0.05), Math.min(z, niv.cota), sus, c.id, niv.nivel ?? 0);
          z = sus; ultim = placile(niv);
        }
        const hMax = Math.max(...piese.map((p) => p.h));
        if (z < hMax - 0.02) for (const pl of ultim) corpMesh(insetInel(pl, 0.05), z, hMax, c.id, (niveluri[niveluri.length - 1]?.nivel ?? 0) + 1, "atic");
        return;
      }
      for (const p of piese) {
        let z = 0;
        for (const niv of niveluri) {
          if (niv.cota >= p.h - 0.02) break;         // bucata s-a oprit sub nivelul ăsta
          const sus = Math.min(p.h, niv.cota + niv.inaltime);
          corpMesh(conturCuLogii(p.pts, niv.logii, 0.05), Math.min(z, niv.cota), sus, c.id, niv.nivel ?? 0);
          z = sus;
        }
        if (z < p.h - 0.02) corpMesh(insetInel(p.pts, 0.05), z, p.h, c.id, (niveluri[niveluri.length - 1]?.nivel ?? 0) + 1, "atic");
      }
    });

    // FAȚADA, din document. Nimic dedus din înălțime: muchiile de planșeu stau
    // la cotele scrise, golurile sunt `Window`-urile arhitecturii, balcoanele
    // sunt `Space`-uri cu `kind: "balcony"`. Unde arhitectura lipsește, volumul
    // rămâne curat — o fațadă plauzibilă și falsă e mai rea decât una absentă.
    // TOCURILE, pe CULOAREA tâmplăriei din document (regula `window`): o
    // geometrie pe culoare; fără culoare, cea a materialului `toc`.
    let geoToc = lista();
    let geoTocCuloare = new Map<string, THREE.BufferGeometry[]>();
    const tocIn = (culoare: string | undefined, g: THREE.BufferGeometry) => {
      if (!culoare || !/^#?[0-9a-f]{3,8}$/i.test(culoare)) { geoToc.push(g); return; }
      const k = culoare.startsWith("#") ? culoare : `#${culoare}`;
      (geoTocCuloare.get(k) ?? geoTocCuloare.set(k, lista()).get(k)!).push(g);
    };
    let geoPlaca = lista();
    let geoParapet = lista();
    // PANOURILE DE FAȚADĂ, grupate pe culoare: o geometrie pe material, ca
    // un P+11 cu patru fețe să nu fie patru sute de mesh-uri.
    let geoPanouri = new Map<string, THREE.BufferGeometry[]>();
    const matPanouri = new Map<string, THREE.MeshStandardMaterial>();
    const panouIn = (cheie: string, culoare: number, rugos: number, g: THREE.BufferGeometry,
                     material?: string) => {
      if (!matPanouri.has(cheie)) {
        // TEXTURA după cheia de MATERIAL (cea de cost), culoarea îi dă nuanța.
        // Fără textură rămâne culoarea plată — un brâu, un glaf.
        // CÂMPUL VITRAT (`kind: glazing`) e sticlă opacă: oglindește cerul, în
        // nuanța lui, nu se vede prin ea — ca geamul unui perete-cortină de
        // afară, ziua. Cheia începe cu kind-ul, deci se recunoaște de acolo.
        const tx = cheie.startsWith("glazing|") || cheie.startsWith("curtain|") ? null : texturaMaterial(material, culoare);
        // PERETELE CORTINĂ: sticla vizibilă oglindește cerul mai tare decât
        // câmpul vitrat; sticla emailată a zonei opace e lucioasă, în tonul ei;
        // profilul e aluminiu vopsit, cu luciu metalic
        // …ȘI SE VEDE PRIN EA, puțin, camera din spate (v. `camera3d`)
        const mat = cheie.startsWith("glazing|cortina|")
          ? materialGeam(culoare, 0.3)
          // STICLA EMAILATĂ a zonei opace e STICLĂ, nu panou: luciu de geam și
          // cerul oglindit în tonul ei, ca fața cortinei să se citească de sus
          // până jos ca sticlă — desenată mată, cu puțin metal, zona de foc și
          // celulele din fața betonului se citeau ca placaj, iar frontonul ca
          // «zidărie cu geamuri înguste» (Cătălin, 20.10.2026)
          : cheie.startsWith("curtain|opac|")
          ? new THREE.MeshPhysicalMaterial({ color: culoare, roughness: 0.06, metalness: 0,
                                             envMapIntensity: 1.2, specularIntensity: 1,
                                             clearcoat: 0.8, clearcoatRoughness: 0.04 })
          : cheie.startsWith("curtain|profil|")
          ? new THREE.MeshStandardMaterial({ color: culoare, roughness: 0.35, metalness: 0.55 })
          : cheie.startsWith("glazing|")
          ? new THREE.MeshPhysicalMaterial({ color: culoare, roughness: 0.08, metalness: 0,
                                             envMapIntensity: 1, specularIntensity: 1,
                                             clearcoat: 0.6, clearcoatRoughness: 0.05 })
          : tx
          ? new THREE.MeshStandardMaterial({ map: tx.map, roughness: tx.roughness,
                                             metalness: tx.metalness })
          // GLAFUL E TABLĂ VOPSITĂ în culoarea fațadei: aceeași nuanță, dar
          // lucioasă — altfel lăcrimarul, în același plan de lumină cu
          // finisajul de sub el, nu se deosebea de zid (Cătălin, 8.10.2026:
          // «să văd aceste detalii când mă uit în scenă»)
          : material === "glaf_atic"
          ? new THREE.MeshStandardMaterial({ color: culoare, roughness: 0.38, metalness: 0.35 })
          : new THREE.MeshStandardMaterial({ color: culoare, roughness: rugos });
        matPanouri.set(cheie, mat);
        deArs.push(mat);
      }
      (geoPanouri.get(cheie) ?? geoPanouri.set(cheie, lista()).get(cheie)!).push(g);
    };
    const culoarePanou = (kind: string, colour?: string): number => {
      if (colour && /^#?[0-9a-f]{3,8}$/i.test(colour)) {
        return new THREE.Color(colour.startsWith("#") ? colour : `#${colour}`).getHex();
      }
      return CULOARE_FEL[kind] ?? 0xd8d3c9;
    };
    let geoLamele = lista();
    // STICLA ȘI LAMELELE, pe CULOAREA din stil (`Railing.colour`): fără ea
    // balustrada rămânea în materialul fix, oricât schimba omul culoarea.
    let geoSticlaCuloare = new Map<string, THREE.BufferGeometry[]>();
    let geoLameleCuloare = new Map<string, THREE.BufferGeometry[]>();
    const balustradaIn = (kind: string, culoare: string | undefined, g: THREE.BufferGeometry) => {
      const lamele = kind === "slats";
      if (!culoare || !/^#?[0-9a-f]{3,8}$/i.test(culoare)) {
        (lamele ? geoLamele : geoParapet).push(g); return;
      }
      const k = culoare.startsWith("#") ? culoare : `#${culoare}`;
      const m = lamele ? geoLameleCuloare : geoSticlaCuloare;
      (m.get(k) ?? m.set(k, lista()).get(k)!).push(g);
    };
    let geoPlin = lista();
    // CALEPINAJUL (format 3.6, §24): plăcile unui placaj, fiecare o cutie cu
    // tonul ei, grupate pe culoare; și fundalul închis din spatele rosturilor
    // (membrana, substructura), grupat pe culoarea rostului.
    let geoPlaci = new Map<string, THREE.BufferGeometry[]>();
    let geoFundalRost = new Map<string, THREE.BufferGeometry[]>();
    // SCÂNDURILE DE DECK (§26): ca plăcile, cu fibra lor în lungul scândurii
    let geoScanduri = new Map<string, THREE.BufferGeometry[]>();
    const inGrup = (m: Map<string, THREE.BufferGeometry[]>, k: string, g: THREE.BufferGeometry) =>
      (m.get(k) ?? m.set(k, lista()).get(k)!).push(g);
    // PARAPETELE PLINE ȘI JARDINIERELE, pe CULOAREA lor din document: desenate
    // toate în albul zidului, cutia închisă la culoare a unui balcon ieșea albă.
    let geoPlinCuloare = new Map<string, THREE.BufferGeometry[]>();
    const plinIn = (culoare: string | undefined, g: THREE.BufferGeometry) => {
      if (!culoare || !/^#?[0-9a-f]{3,8}$/i.test(culoare)) { geoPlin.push(g); return; }
      const k = culoare.startsWith("#") ? culoare : `#${culoare}`;
      (geoPlinCuloare.get(k) ?? geoPlinCuloare.set(k, lista()).get(k)!).push(g);
    };
    // MÂINILE CURENTE, pe culoarea și materialul lor din document (3.9, §27)
    let geoMana = new Map<string, THREE.BufferGeometry[]>();
    // ECRANELE FIXE (`kind: "screen"`, format 3.5): o tablă pe marginea
    // balconului, cu găurile ei TĂIATE de o hartă alfa (`alphaTest`), nu
    // amestecate: tabla perforată se citește ca tablă — opacă de departe, cu
    // puncte de aproape — și nu are nevoie de sortarea transparențelor.
    // Coordonatele de textură sunt în MODULE de `ECRAN_MODUL_M`.
    // CU GROSIME, nu o foaie: o foaie fără grosime văzută exact din profil
    // dădea o coloană de pixeli care pâlpâia pe peretele din spate (proba de
    // z-fight: 11 px pe un corp, 5.10.2026). Cutia are fața de afară pe ax și
    // intră în balcon; canturile ei sunt pline (uv 0,0 = tablă).
    const ECRAN_MODUL_M: Record<string, number> = { perforated: 0.09, slats: 0.11, green: 1.2 };
    // JARDINIERELE CU PLANTE (v. `plante3d.ts`): pământul din jgheab și ce
    // crește în el, adunate de pe toate fațadele și instanțiate o dată
    let planteScena: PlantaSit[] = [];
    let geoPamant = lista();
    let geoEcran = new Map<string, THREE.BufferGeometry[]>();
    const ecranIn = (r: ParapetFatada, a: number[], b: number[], z0: number, z1: number,
                     nx: number, ny: number, capA: boolean, capB: boolean) => {
      const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (l < 0.15 || z1 <= z0) return;
      const desen = r.pattern === "slats" ? "slats" : r.pattern === "green" ? "green" : "perforated";
      const gol = Math.min(0.7, Math.max(0.1, r.openness ?? 0.35));
      const m = ECRAN_MODUL_M[desen];
      // ecranul VERDE e un spalier cu frunziș: gros cât frunzele, nu cât tabla
      const t = desen === "green" ? Math.max(0.1, r.thickness_m ?? 0.1) : Math.max(0.02, r.thickness_m ?? 0.05);
      const ix = -nx * t, iy = -ny * t;                       // spre balcon
      const ux = (b[0] - a[0]) / l, uy = (b[1] - a[1]) / l;
      const U = l / m, V = (z1 - z0) / m;
      const pos: number[] = [], nor: number[] = [], uvs: number[] = [];
      const fata = (p: number[][], n: number[], uv: number[][]) => {
        for (const k of [0, 1, 2, 0, 2, 3]) { pos.push(...p[k]); nor.push(...n); uvs.push(...uv[k]); }
      };
      const A0 = [a[0], a[1], z0], B0 = [b[0], b[1], z0], B1 = [b[0], b[1], z1], A1 = [a[0], a[1], z1];
      const a0 = [a[0] + ix, a[1] + iy, z0], b0 = [b[0] + ix, b[1] + iy, z0];
      const b1 = [b[0] + ix, b[1] + iy, z1], a1 = [a[0] + ix, a[1] + iy, z1];
      const DESEN = [[0, 0], [U, 0], [U, V], [0, V]], PLIN = [[0, 0], [0, 0], [0, 0], [0, 0]];
      fata([A0, B0, B1, A1], [nx, ny, 0], DESEN);
      fata([a0, b0, b1, a1], [-nx, -ny, 0], DESEN);
      // cantul de la capăt numai unde ecranul se termină: la colțul liniei,
      // cantul ar sta în planul feței bucății următoare
      if (capA) fata([A0, a0, a1, A1], [-ux, -uy, 0], PLIN);
      if (capB) fata([B0, b0, b1, B1], [ux, uy, 0], PLIN);
      fata([A1, B1, b1, a1], [0, 0, 1], PLIN);
      fata([A0, B0, b0, a0], [0, 0, -1], PLIN);
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
      g.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
      const k = `${desen}|${gol.toFixed(2)}|${r.colour ?? ""}`;
      (geoEcran.get(k) ?? geoEcran.set(k, lista()).get(k)!).push(g);
    };
    const plaseAlese: THREE.Mesh[] = [];
    /** BINURILE GOALE: pe nivel (`perNivel`), fiecare planșeu al unei fațade
 *  își lipește plasele lui, nu una pe tot amplasamentul. */
    const binsNoi = () => {
      geoGeam = lista();
      geoInterior = lista();
      geoManer = lista();
      geoToc = lista();
      geoTocCuloare = new Map<string, THREE.BufferGeometry[]>();
      geoPlaca = lista();
      geoParapet = lista();
      geoPanouri = new Map<string, THREE.BufferGeometry[]>();
      geoLamele = lista();
      geoSticlaCuloare = new Map<string, THREE.BufferGeometry[]>();
      geoLameleCuloare = new Map<string, THREE.BufferGeometry[]>();
      geoPlin = lista();
      geoPlaci = new Map<string, THREE.BufferGeometry[]>();
      geoFundalRost = new Map<string, THREE.BufferGeometry[]>();
      geoScanduri = new Map<string, THREE.BufferGeometry[]>();
      geoPlinCuloare = new Map<string, THREE.BufferGeometry[]>();
      geoMana = new Map<string, THREE.BufferGeometry[]>();
      planteScena = [];
      geoPamant = lista();
      geoEcran = new Map<string, THREE.BufferGeometry[]>();
    };
    /** VĂRSAREA binurilor în plase lipite, pe materiale (v. `ordine`). */
    const varsaBins = () => {
    // `ordine`: rândul la desenare al TRANSPARENTELOR. Geamul ferestrelor și
    // sticla balustradelor sunt câte o plasă unită pe tot amplasamentul, iar
    // three le sortează după centrul sferei fiecăreia — două puncte la un
    // metru unul de altul, deci ordinea se întorcea cu direcția privirii: pe
    // jumătate din fețe balustrada se desena prima, scria în adâncime, și
    // geamul ușii din spatele ei era respins — albastru deasupra mâinii
    // curente, gri (încăperea) dedesubt, schimbându-se la rotire («geamul
    // tăiat», «se bat materialele care să se vadă primele», Cătălin,
    // 5.10.2026). Rândul e acum FIX: geamul, apoi sticla, apoi lamelele —
    // iar sticla și lamelele nu scriu în adâncime (v. materialele).
    eidAcum = null;
    const adauga = (list: THREE.BufferGeometry[], mat: THREE.Material, ordine = 0) => {
      if (!list.length) return;
      const tabel = tabelAlegere(list);
      const g = BGU.mergeGeometries(potrivitePentruUnire(list), false);
      list.forEach((x) => x.dispose());
      if (!g) return;
      deArs.push(g);
      const m = new THREE.Mesh(g, mat);
      m.castShadow = false;
      m.renderOrder = ordine;
      if (tabel.eids.some((e) => e)) { m.userData.alege = tabel; plaseAlese.push(m); }
      lume.add(m);
    };
    adauga(geoInterior, M.camera);
    adauga(geoManer, M.maner);
    adauga(geoGeam, M.geam, 1);
    adauga(geoToc, M.toc);
    for (const [culoare, list] of geoTocCuloare) {
      const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(culoare), roughness: 0.6,
                                                   metalness: 0.25, side: THREE.DoubleSide });
      deArs.push(mat);
      adauga(list, mat);
    }
    adauga(geoPlaca, M.placa);
    adauga(geoParapet, M.parapet, 2);
    for (const [culoare, list] of geoSticlaCuloare) {
      const mat = materialSticla(culoare);
      deArs.push(mat);
      adauga(list, mat, 2);
    }
    adauga(geoLamele, M.lamele, 3);
    for (const [culoare, list] of geoLameleCuloare) {
      const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(culoare), roughness: 0.55,
                                                   metalness: 0.3, transparent: true, opacity: 0.55,
                                                   depthWrite: false, side: THREE.DoubleSide });
      deArs.push(mat);
      adauga(list, mat, 3);
    }
    adauga(geoPlin, M.perete);
    for (const [culoare, list] of geoPlinCuloare) {
      const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(culoare), roughness: 0.75 });
      deArs.push(mat);
      adauga(list, mat);
    }
    for (const [cheie, list] of geoMana) {
      const [culoare, material] = cheie.split("|");
      // oțelul și aluminiul vopsite, inoxul lucios, lemnul mat
      const lemn = /lemn/.test(material ?? ""), inox = /inox/.test(material ?? "");
      const mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(/^#?[0-9a-f]{6}$/i.test(culoare) ? (culoare.startsWith("#") ? culoare : `#${culoare}`) : "#2a2c2f"),
        roughness: lemn ? 0.75 : inox ? 0.3 : 0.55, metalness: lemn ? 0 : inox ? 0.8 : 0.35 });
      deArs.push(mat);
      adauga(list, mat);
    }
    for (const [cheie, list] of geoPlaci) {
      const [culoareTxt, rugosTxt] = cheie.split("|");
      const mat = new THREE.MeshStandardMaterial({ color: parseInt(culoareTxt, 16), vertexColors: true,
                                                   map: texturaGranulatie(), roughness: Number(rugosTxt) || 0.6 });
      deArs.push(mat);
      adauga(list, mat);
    }
    for (const [cheie, list] of geoScanduri) {
      const [culoareTxt, rugosTxt] = cheie.split("|");
      const mat = new THREE.MeshStandardMaterial({ color: parseInt(culoareTxt, 16), vertexColors: true,
                                                   map: texturaFibra(), roughness: Number(rugosTxt) || 0.65 });
      deArs.push(mat);
      adauga(list, mat);
    }
    for (const [culoare, list] of geoFundalRost) {
      const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(culoare.startsWith("#") ? culoare : `#${culoare}`),
                                                   roughness: 0.95 });
      deArs.push(mat);
      adauga(list, mat);
    }
    if (geoPamant.length) {
      const matPamant = new THREE.MeshStandardMaterial({ color: 0x3a2e25, roughness: 1 });
      deArs.push(matPamant);
      adauga(geoPamant, matPamant);
    }
    if (planteScena.length) {
      const plante = construiestePlante(planteScena);
      deArs.push(...plante.deArs);
      lume.add(...plante.obiecte);
    }
    for (const [cheie, list] of geoEcran) {
      const [desen, golTxt, culoare] = cheie.split("|");
      if (desen === "green") {
        // ECRANUL VERDE: frunzișul e în TEXTURĂ (culoare și goluri), pe fețele
        // unei cutii groase de 10 cm — nouă sute de ecrane cu frunze modelate
        // una câte una ar fi însemnat milioane de triunghiuri
        const frunzis = texturaFrunzis(Number(golTxt));
        const matVerde = new THREE.MeshStandardMaterial({ map: frunzis, alphaTest: 0.4, roughness: 0.9,
                                                          side: THREE.DoubleSide });
        deArs.push(matVerde, frunzis);
        adauga(list, matVerde);
        continue;
      }
      const tex = texturaEcran(desen, Number(golTxt));
      const mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(/^#?[0-9a-f]{3,8}$/i.test(culoare) ? (culoare.startsWith("#") ? culoare : `#${culoare}`) : "#8a8d90"),
        roughness: 0.5, metalness: 0.45, side: THREE.DoubleSide,
        alphaMap: tex, alphaTest: 0.5 });
      deArs.push(mat, tex);
      adauga(list, mat);
    }
    for (const [cheie, list] of geoPanouri) {
      const tabel = tabelAlegere(list);
      // POTRIVITE ÎNAINTE: o geometrie fără UV în grupă (profilul unui brâu
      // lângă cutiile altora) făcea unirea să întoarcă `null`, iar grupa
      // întreagă lipsea — cu fanta ei tăiată în finisaj (v. `potrivitePentruUnire`)
      const g = BGU.mergeGeometries(potrivitePentruUnire(list), false);
      list.forEach((x) => x.dispose());
      if (!g) continue;
      deArs.push(g);
      const m = new THREE.Mesh(g, matPanouri.get(cheie)!);
      m.userData.cheie = cheie;
      if (tabel.eids.some((e) => e)) { m.userData.alege = tabel; plaseAlese.push(m); }
      // Corpul aruncă umbra; pielea lipită de el ar arunca aceeași umbră, cu
      // 16 cm mai în față, și s-ar umbri pe sine în dungi (acnee de shadow map).
      m.castShadow = false;
      m.receiveShadow = true;
      lume.add(m);
    }
    };
    for (const fat of scena.fatade) {
      // PE NIVEL (starea timelapse-ului): plasele planșeului se lipesc separat
      // și tot ce s-a adăugat pentru el poartă corpul și nivelul lui
      const deAici = lume.children.length;
      if (perNivel) binsNoi();
      // GEAMUL PE COLȚ (`modul_tamplarie`, 12.10.2026): două goluri pe fețele
      // unui colț ieșind, legate în document (`corner.with`), se întâlnesc pe
      // un montant de colț — fără stâlp de toc, șpalet sau zidărie între ele.
      // Aici se află capătul din colț, linia feței vecine (pe care stă
      // montantul) și care din pereche îl desenează — ÎNAINTE de panouri, care
      // își taie gaura prin capătul din colț după el.
      corelaColturile(fat.goluri);
      eidAcum = null;
      const corp = scena.corpuri[fat.corp];
      const areFatada = fat.panouri.length > 0;

      // ATICUL: pe nivelul de terasă, un inel de parapet — corpul lui, retras
      // 5 cm sub panourile de parapet care îl îmbracă — de la placa terasei
      // până la înălțimea panourilor. Terasa (straturile ei) vine ca panouri
      // orizontale, mai jos, ca oricare intrados.
      // PARAPETELE TERASELOR: zidurile joase din document (v. `sit3d`), de la
      // pardoseala nivelului, în culoarea corpului — ele închid terasa, nu o
      // balustradă pusă de fațadă.
      for (const pz of fat.parapete_zid) {
        const f = forma({ poly: pz.poly, holes: pz.holes });
        if (!f) continue;
        const g = new THREE.ExtrudeGeometry(f, { depth: pz.h, bevelEnabled: false });
        g.translate(0, 0, fat.cota);
        deArs.push(g);
        const m = new THREE.Mesh(g, M.perete);
        m.castShadow = true; m.receiveShadow = true;
        lume.add(m);
      }
      if (fat.atic > 0.05 && corp) {
        // DIN PEREȚII `parapet` AI DOCUMENTULUI când îi are: ei urmează
        // retragerea ultimului etaj (terasele), pe când amprenta corpului nu —
        // desenat din amprentă, aticul trecea peste terase, în aer.
        const inele = fat.atic_inele.length
          ? fat.atic_inele.map((r) => ({ poly: insetInel(r.poly, 0.05), holes: r.holes }))
          : [{ poly: insetInel(corp.pts, 0.05), holes: [insetInel(corp.pts, 0.25)] }];
        for (const inel of inele) {
          const f = forma(inel);
          if (f) {
            const g = new THREE.ExtrudeGeometry(f, { depth: fat.atic, bevelEnabled: false });
            g.translate(0, 0, fat.cota + fat.inaltime);
            deArs.push(g);
            const m = new THREE.Mesh(g, M.perete);
            m.castShadow = true; m.receiveShadow = true;
            lume.add(m);
          }
        }
      }

      // Muchia planșeului: banda orizontală care dă ritmul unei fațade. E un
      // element real — placa se vede pe contur la fiecare cotă. Când documentul
      // are fațadă, ritmul îl dau brâiele ei, iar banda ar rămâne în spatele
      // termosistemului.
      if (!areFatada && corp && fat.cota > 0.2 && fat.cota < corp.h - 0.1) {
        geoPlaca.push(...bandaPeInel(corp.pts, fat.cota - 0.28, fat.cota, 0.06));
      }

      // PANOURILE. Se desenează finisajul și relieful — straturile care se văd
      // — iar substratul doar unde nu există finisaj peste el. Golurile
      // nivelului se decupează din panourile verticale ale feței lor.
      const areFinisaj = fat.panouri.some((p) => p.layer === "finish" && p.surface === "facade");
      // Cât iese finisajul pe fiecare față: relieful pornește DE PE EL, nu din
      // zidărie — altfel capetele brâului cad în același plan cu capetele
      // finisajului și cele două se bat la fiecare colț.
      // BRÂIELE NU SE SUPRAPUN CU FINISAJUL. Un brâu de 3 cm peste tencuială
      // înseamnă două fețe paralele la 3 cm; corect ca adâncime, dar la 200 m
      // și pe orice placă video cu un tampon mai slab tot se pot bate. Deci
      // finisajul se DECUPEAZĂ pe înălțimea brâului, iar brâul e o cutie plină
      // din planul zidăriei până la fața lui: pe niciun pixel nu mai stau două
      // fețe una peste alta, oricare ar fi precizia adâncimii.
      // CUSĂTURILE ÎNTRE FINISAJE. Două finisaje vecine pe aceeași față (cărămidă
      // lângă lamele) își ating capetele în același plan, iar cel mai gros are
      // capătul mai mare: două fețe coplanare care se bat pe marginea plăcării.
      // Fiecare se prelungește 1 cm în vecin, unde capătul lui rămâne îngropat.
      // CAPETELE ORNAMENTELOR CU SECȚIUNE de pe nivel, ca la colț profilul să se
      // prelungească DOAR spre un profil vecin (capătul altuia, în același
      // punct): acolo cele două se întrepătrund și umplu pătratul de colț.
      // Fără vecin — o față care nu poartă profilul — capătul rămâne la colț,
      // altfel un bloc de 40 cm ieșea în aer lângă un balcon.
      // Cu vecinul găsit, capătul se TAIE ÎN UNGHI pe planul bisector
      // (v. `profilPeFata`): factorul pe adâncime e `(n_B·u_A)/(1 + n_A·n_B)`
      // — +1 la un colț drept ieșind, −1 la unul intrând — deci cele două
      // profile se ating exact, fără să se încalece și fără ciot.
      // …ȘI LA FEL FINISAJELE: două cutii de finisaj vecine la un colț ieșind se
      // opreau amândouă la colțul zidăriei și pătratul dintre ele (grosimea
      // sistemului pe grosimea sistemului) rămânea gol — o crestătură pe toată
      // înălțimea colțului, prin care se vedea corpul.
      // …ȘI BRÂIELE FĂRĂ SECȚIUNE („band"): cutii pline, tăiate și ele în
      // unghi la colț — altfel la fiecare colț ieșind rămânea între ele
      // pătratul gol, iar la cel intrând se încălecau.
      // …DAR NUMAI CU ACELAȘI BRÂU. Capătul unei fâșii verticale de lamele care
      // cădea la colț se potrivea cu capătul brâului orizontal de pe fața
      // vecină — alt relief, altă cotă — și ieșea cu adâncimea lui peste
      // finisajul vecin, pâlpâind în el (29.09.2026). Un relief se taie în
      // unghi doar spre un relief de același fel (material, culoare) care îl
      // și încalecă pe înălțime: continuarea lui după colț, nu un străin.
      const capete: { pt: number[]; n: number[]; surf: string; fel: string; z0: number; z1: number; ce: string }[] = [];
      const felul = (p: PanouFatada) => (p.layer === "relief" ? (p.section ? "relief" : "band") : p.layer);
      const ceEste = (p: PanouFatada) => `${p.kind}|${p.material ?? ""}|${p.colour ?? ""}`;
      // GURA LOGIEI NU E UN COLȚ TĂIAT ÎN UNGHI, ci o îmbinare dreaptă: fața
      // se oprește la linia zidăriei obrazului (gaura din piele), iar obrazul
      // (`recess`) IESE din gură cu adâncimea stratului de pe față și-i
      // acoperă muchia tăiată — v. `iesireaDinGura`. Deci `facade` și `recess`
      // nu se caută una pe alta la colț.
      for (const p of fat.panouri) {
        const fel = felul(p);
        if (fel && p.n && !p.poly) {
          const ce = ceEste(p);
          capete.push({ pt: p.a, n: p.n, surf: p.surface, fel, z0: p.z0, z1: p.z1, ce },
                      { pt: p.b, n: p.n, surf: p.surface, fel, z0: p.z0, z1: p.z1, ce });
        }
      }
      // CÂT IESE UN PERETE DE LOGIE DIN GURA EI, la capătul `pt`: adâncimea
      // stratului de același fel de pe fața principală care trece pe acolo
      // (pielea, ca să-i acopere muchia tăiată; brâul, ca să ajungă până la
      // fața brâului de afară). Zero când capătul nu e la gura logiei.
      const iesireaDinGura = (pt: number[], nA: number[], fel: string): number => {
        let d = 0;
        for (const q of fat.panouri) {
          if (q.surface !== "facade" || !q.n || q.poly || felul(q) !== fel) continue;
          if (Math.abs(q.n[0] * nA[0] + q.n[1] * nA[1]) > 0.3) continue;   // perpendicular pe obraz
          const Lq = Math.hypot(q.b[0] - q.a[0], q.b[1] - q.a[1]) || 1;
          const ux = (q.b[0] - q.a[0]) / Lq, uy = (q.b[1] - q.a[1]) / Lq;
          const dx = pt[0] - q.a[0], dy = pt[1] - q.a[1];
          const u = dx * ux + dy * uy, dist = Math.abs(dx * q.n[0] + dy * q.n[1]);
          if (dist > 0.05 || u < -0.05 || u > Lq + 0.05) continue;
          d = Math.max(d, q.depth_m);
        }
        return d;
      };
      // un strat de sub finisaj (`p`) al cărui capăt `pt` e și capătul unui
      // finisaj de pe aceeași față, pe aceeași înălțime (v. `SUB_FINISAJ_M`)
      const subFinisaj = (pt: number[], p: PanouFatada): boolean => {
        if (!p.n || p.poly || p.layer === "finish" || p.layer === "relief" || p.layer === "curtain") return false;
        return fat.panouri.some((q) => q.layer === "finish" && q.surface === p.surface && q.n && !q.poly
          && q.n[0] * p.n![0] + q.n[1] * p.n![1] > 0.99
          && Math.min(q.z1, p.z1) - Math.max(q.z0, p.z0) > 0.01
          && (Math.hypot(q.a[0] - pt[0], q.a[1] - pt[1]) < 0.02 || Math.hypot(q.b[0] - pt[0], q.b[1] - pt[1]) < 0.02));
      };
      const unghiColt = (pt: number[], nA: number[], uA: number[], surf: string, fel: string,
                         p?: PanouFatada): number => {
        // vecinul de pe ALTĂ față: cel de pe aceeași față are aceeași normală
        // și ar da 0 — se sare peste el, ca să nu-l ascundă pe cel din colț
        const relief = fel === "relief" || fel === "band";
        for (const v of capete) {
          if (v.pt === pt || v.surf !== surf || v.fel !== fel) continue;
          if (relief && p && (v.ce !== ceEste(p) || Math.min(v.z1, p.z1) - Math.max(v.z0, p.z0) <= 0.01)) continue;
          if (Math.hypot(v.pt[0] - pt[0], v.pt[1] - pt[1]) >= 0.05) continue;
          const cos = nA[0] * v.n[0] + nA[1] * v.n[1];
          if (cos > 0.999 || 1 + cos < 0.1) continue;   // aceeași față, sau întoarcere completă
          return (v.n[0] * uA[0] + v.n[1] * uA[1]) / (1 + cos);
        }
        return 0;
      };
      // RELIEFURILE FIECĂREI FEȚE, cu capetele lor: cele cât fața taie felii,
      // cele mai scurte (glaf, montant, ramă) devin găuri (v. `taieDupaRelief`).
      const reliefuri = new Map<string, PanouFatada[]>();
      for (const p of fat.panouri) {
        if (p.layer !== "relief" || !p.face) continue;
        if (p.surface !== "facade" && p.surface !== "recess" && p.surface !== "parapet") continue;
        (reliefuri.get(p.face) ?? reliefuri.set(p.face, []).get(p.face)!).push(p);
      }
      /** Reliefurile de pe fața panoului, în coordonatele lui (u de la `a`). */
      const reliefurileLui = (p: PanouFatada, ux: number, uy: number, L: number): ReliefPeFata[] => {
        const out: ReliefPeFata[] = [];
        for (const r of reliefuri.get(p.face ?? "") ?? []) {
          if (r.surface !== p.surface) continue;
          const ua = (r.a[0] - p.a[0]) * ux + (r.a[1] - p.a[1]) * uy;
          const ub = (r.b[0] - p.a[0]) * ux + (r.b[1] - p.a[1]) * uy;
          out.push({ u0: Math.min(ua, ub), u1: Math.max(ua, ub), z0: r.z0, z1: r.z1 });
        }
        void L;
        return out;
      };
      // COLȚURILE CU PERETE CORTINĂ: capetele prelungite ale panourilor de
      // cortină (stâlpul de colț). Un brâu de pe fața vecină se termină acolo
      // fără pereche, în același plan cu capătul placajului de sub el — se
      // retrage 5 mm, ca cele două capete să nu se bată
      const coltCortina: number[][] = [];
      for (const p of fat.panouri) {
        if (p.kind !== "curtain_wall" || !p.cortina) continue;
        if (p.cortina.capete[0] > 0.01) coltCortina.push(p.a);
        if (p.cortina.capete[1] > 0.01) coltCortina.push(p.b);
      }
      const laColtCortina = (q: number[]) => coltCortina.some((c) => Math.hypot(c[0] - q[0], c[1] - q[1]) < 0.05);
      for (const p of fat.panouri) {
        eidAcum = p.eid;
        // PE O TERASĂ STRATURILE SE VĂD PE CANT. Termoizolația de 15 cm,
        // hidroizolația și pardoseala fac împreună pragul de la ușă; desenată
        // numai pardoseala, ea plutea la 16 cm deasupra plăcii, iar
        // termoizolația cerută de beneficiar nu se vedea nicăieri. Pe o față
        // verticală sau sub o placă substratul stă ascuns sub finisaj și
        // rămâne nedesenat, ca până acum.
        // …la fel PARDOSEALA unui balcon sau a unei logii (§26): hidroizolația,
        // patul (șapa, ploturile) și piesele fac împreună treapta de la ușă.
        const stiva = p.surface === "terrace" || p.surface === "floor";
        if (p.layer === "thermal" && !stiva) continue;
        if (p.layer === "render" && areFinisaj && !stiva) continue;
        // SUBSTRUCTURA fațadei ventilate stă în golul de aer, sub placă: se
        // numără, nu se vede (cutia finisajului umple oricum adâncimea). Sub o
        // pardoseală însă ploturile sunt ce se vede prin rostul deschis.
        if (p.layer === "subframe" && !stiva) continue;
        // PIESELE PARDOSELII, din calepinajul ei: gresie (cu chitul de sub
        // ea), dale pe ploturi sau scânduri de deck, fiecare cu rostul ei
        if (p.pardoseala && p.poly && p.poly.length >= 3) {
          const r = geometriaPardoselii(p.poly, p.pardoseala, fat.cota + p.z1);
          if (r.pat) inGrup(geoFundalRost, p.pardoseala.culoareRost, r.pat);
          if (r.piese) {
            const hex = culoarePanou(p.kind, p.colour).toString(16);
            if (p.pardoseala.fel === "deck") {
              inGrup(geoScanduri, `${hex}|${/lemn/.test(p.material ?? "") ? 0.72 : 0.62}`, r.piese);
            } else {
              inGrup(geoPlaci, `${hex}|${p.pardoseala.fel === "raised" ? 0.55 : 0.6}`, r.piese);
            }
          }
          continue;
        }
        const culoare = culoarePanou(p.kind, p.colour);
        const cheie = `${p.kind}|${p.material ?? ""}|${culoare.toString(16)}`;
        const rugos = p.kind === "brick" ? 0.95 : p.kind === "cladding" ? 0.6 : 0.9;
        // ORICE PANOU CU INEL E ORIZONTAL: intradosul unui balcon, tavanul unei
        // logii sau al unui pasaj, straturile terasei — se extrudează pe
        // grosimea lui de la cota `z0`.
        if (p.poly && p.poly.length >= 3) {
          // INTRADOSUL, CU 1 cm ÎNĂUNTRUL CONTURULUI PLĂCII: extrudat din același
          // inel ca placa și intrat 1 cm în ea, fețele lui laterale cădeau în
          // planul fețelor laterale ale plăcii pe centimetrul acela și pâlpâiau
          // pe toată muchia balconului (88 px pe un corp, 29.09.2026).
          // …iar SUB PIESELE UNEI PARDOSELI straturile se trag 1,2 cm înăuntru:
          // muchia unei piese de pe margine stă cu o jumătate de rost în spatele
          // inelului, iar cantul stratului de sub ea ar fi căzut la 2 mm de ea,
          // paralel — se băteau de departe
          const subPiese = (p.surface === "floor" || p.surface === "terrace") && p.layer !== "finish";
          // STRATURILE DE SUB FINISAJ încă 5 mm înăuntru: toate urcă până la
          // placă, deci cantul lor și al finisajului cădeau în același plan pe
          // grosimea stratului de sub el — invizibil sub un balcon, dar pe gura
          // decroșului de intrare cantul tavanului e la vedere și pâlpâia.
          const inel = p.surface === "soffit" && p.poly.length >= 3
            ? insetInel(p.poly, p.layer === "finish" ? 0.01 : 0.015)
            : subPiese ? insetInel(p.poly, PAT_RETRAS_M) : p.poly;
          const f2 = forma({ poly: inel.length >= 3 ? inel : p.poly, holes: [] });
          if (!f2) continue;
          // DE LA PLACĂ PÂNĂ LA FAȚA STRATULUI, nu o foiță în aer: se desenează
          // doar finisajul, iar el stă la 12 cm sub placa balconului, cu
          // termoizolația nedesenată între — deci plutea. Sub o placă (intrados)
          // cutia urcă de la fața stratului până la placă; pe o placă (terasă)
          // coboară de la fața lui până la placă.
          // …și cu 1 cm ÎN placă, ca fața dinspre placă să fie îngropată, nu
          // la 3 mm de ea (o dungă de lumină la rotunjirile cotelor).
          // PE O PLACĂ straturile stau UNUL PESTE ALTUL, fiecare între `z0` și
          // `z1` ale lui. Desenate toate «de la fața lor până la placă»
          // (`z1 − depth_m`), cele trei ale unei terase porneau din același
          // plan și își suprapuneau fețele de jos — trei fețe coplanare care
          // se băteau, pâlpâirea de pe pardoseală. Numai PRIMUL strat
          // (termoizolația) intră un centimetru în placă, ca fața lui de jos
          // să fie îngropată; celelalte se așază pe el, spate în spate, deci
          // nu au cum să se bată.
          // PRIMUL STRAT de pe placă (termoizolația terasei, hidroizolația
          // balconului) intră 1 cm în ea: fața lui de jos rămâne îngropată
          const jos = p.surface === "soffit"
            ? p.z0 : (p.layer === "thermal" || (p.surface === "floor" && p.z0 <= 0.001) ? p.z0 - 0.01 : p.z0);
          const gros = Math.max(0.01, p.surface === "soffit"
            ? p.depth_m + 0.01 : p.z1 - jos);
          const g2 = new THREE.ExtrudeGeometry(f2, { depth: gros, bevelEnabled: false });
          g2.translate(0, 0, fat.cota + jos);
          panouIn(cheie, culoare, rugos, g2, p.material);
          continue;
        }
        if (!p.n) continue;
        // PERETELE CORTINĂ (format 3.10): montanții și traversele în culoarea
        // profilului, sticla celulelor vizibile, sticla emailată a celor opace
        // — din grila scrisă în document (v. `cortina3d`)
        if (p.kind === "curtain_wall") {
          if (p.cortina) {
            const c = geometriaCortinei(p.a, p.b, p.n, p.z0, p.z1, p.depth_m, p.cortina, fat.cota);
            const cp = culoarePanou("curtain", p.colour ?? "#4a4f55");
            for (const g of c.profile) panouIn(`curtain|profil|${cp.toString(16)}`, cp, 0.45, g, "aluminiu");
            const cs = culoarePanou("glazing", p.cortina.sticla.culoare);
            for (const g of c.sticla) panouIn(`glazing|cortina|${cs.toString(16)}`, cs, 0.08, g);
            geoInterior.push(...c.camere);
            const co = culoarePanou("cladding", p.cortina.opac.culoare);
            for (const g of c.opac) panouIn(`curtain|opac|${co.toString(16)}`, co, 0.3, g, "sticla_emailata");
          }
          continue;
        }
        // ȘPALETUL (întoarcerea termosistemului în gol) e o cantitate, nu un
        // volum de desenat: pereții golului îi dă deja cutia panoului.
        if (p.surface === "reveal") continue;
        const L = Math.hypot(p.b[0] - p.a[0], p.b[1] - p.a[1]);
        const ux = (p.b[0] - p.a[0]) / (L || 1), uy = (p.b[1] - p.a[1]) / (L || 1);
        // IEȘIREA DIN GURA LOGIEI: un perete de logie se prelungește, la
        // capătul din gură, cu adâncimea stratului de pe față — peste muchia
        // tăiată a pielii feței (v. `iesireaDinGura`). Golurile lui se mută
        // odată cu originea.
        const iesA = p.surface === "recess" ? iesireaDinGura(p.a, p.n, felul(p)) : 0;
        const iesB = p.surface === "recess" ? iesireaDinGura(p.b, p.n, felul(p)) : 0;
        const pa = iesA ? [p.a[0] - ux * iesA, p.a[1] - uy * iesA] : p.a;
        const pb = iesB ? [p.b[0] + ux * iesB, p.b[1] + uy * iesB] : p.b;
        const goluri: GolPanou[] = [];
        if (p.layer !== "relief") {
          for (const gol of fat.goluri) {
            const mx = (gol.a[0] + gol.b[0]) / 2, my = (gol.a[1] + gol.b[1]) / 2;
            const dx = mx - p.a[0], dy = my - p.a[1];
            const u = dx * ux + dy * uy;
            const d = dx * p.n[0] + dy * p.n[1];
            // DIN ORICE PANOU PE CARE ÎL ÎNCALECĂ, nu numai din cel care îi
            // conține mijlocul: un gol care calcă peste cusătura a două
            // finisaje (lemn | tencuială) sau peste îmbinarea a două fețe
            // coliniare rămânea acoperit, pe partea lui de dincolo, de panoul
            // vecin — «geamul tăiat» (Cătălin, 5.10.2026; măsurat cu raze:
            // 299 de goluri din 3537, finisajul la 17–19 cm în fața tocului).
            const wG = Math.hypot(gol.b[0] - gol.a[0], gol.b[1] - gol.a[1]);
            if (Math.abs(d) > 0.6 || u + wG / 2 < 0.02 || u - wG / 2 > L - 0.02) continue;
            // GEAMUL PE COLȚ: gaura trece prin capătul panoului din colț (prin
            // teșitură), ca finisajul să nu rămână între cele două geamuri
            let capU0 = u - wG / 2 + TAMPLARIE.inset, capU1 = u + wG / 2 - TAMPLARIE.inset;
            let capat = false;
            if (gol.colt) {
              const pc = gol.colt.la === "b" ? gol.b : gol.a;
              const uc = (pc[0] - p.a[0]) * ux + (pc[1] - p.a[1]) * uy;
              if (uc > u) capU1 = uc + 1; else capU0 = uc - 1;
              capat = true;
            }
            // …ȘI CARE PRIVESC ÎN ACELAȘI SENS CU PANOUL. O fereastră de pe
            // fața lungă, la 60 cm de colț, cădea și în toleranța frontonului:
            // frontonul primea o gaură lângă colț „unde nu e nicio fereastră",
            // iar când gaura o încăleca pe a unei ferestre adevărate de pe
            // fronton, triangularea se strica (dungi oblice pe toată fața).
            if (gol.n && gol.n[0] * p.n[0] + gol.n[1] * p.n[1] < 0.7) continue;
            // GAURA DIN FINISAJ E MAI MICĂ DECÂT GOLUL cu `TAMPLARIE.inset` pe
            // șpaleți și la buiandrug: finisajul (termosistemul, plăcile) VINE
            // PESTE toc, cum se montează — tăiată cât golul, între marginea ei
            // și căptușeala golului (trasă 1,2 cm înăuntru) rămânea o fâșie
            // de 1,2 cm prin care se vedea corpul alb, în jurul fiecărei
            // ferestre («tocul nu e continuu, o fâșie albă», Cătălin,
            // 12.10.2026; măsurat cu raze: la 1 cm de marginea golului prima
            // lovitură era corpul, la −5 cm). Jos gaura rămâne la parapet:
            // acolo pervazul o acoperă (v. `tuck`).
            goluri.push({ u0: capU0, u1: capU1,
                          z0: gol.parapet, z1: gol.parapet + gol.inaltime - TAMPLARIE.inset,
                          ...(capat ? { capat } : {}) });
          }
          // GOLUL LOGIEI: pe lățimea laturii ei de pe față, de la pardoseală
          // până sub placa de deasupra. Pielea se oprește la marginea scobiturii.
          if (p.surface === "facade" || p.surface === "plinth") {
            for (const lg of fat.logii) {
              // nu și terasele: parapetul lor de pe contur e o față îmbrăcată
              if (fat.terase.includes(lg)) continue;
              let u0 = Infinity, u1 = -Infinity, pe = 0;
              for (const v of lg) {
                const dx = v[0] - p.a[0], dy = v[1] - p.a[1];
                const u = dx * ux + dy * uy, d = dx * p.n[0] + dy * p.n[1];
                // …și puțin DINCOLO de capetele panoului: la colțul intrând deschis
                // pielea se oprește cu 20 cm înaintea colțului (v. `fatada.RETUR_COLT_M`),
                // iar vârful logiei din colț cădea în afara panoului — fără al doilea
                // vârf nu se mai tăia gaura, și geamul câmpului acoperea logia.
                if (Math.abs(d) <= 0.4 && u > -0.6 && u < L + 0.6) { pe++; u0 = Math.min(u0, u); u1 = Math.max(u1, u); }
              }
              if (pe >= 2 && u1 - u0 > 0.3) {
                // …iar gura DECROȘULUI de intrare pleacă de la pardoseala lui,
                // la teren: soclul dispare pe toată lățimea ei
                const jos = fat.intrari?.includes(lg)
                  ? Math.min(-1, ...(fat.denivelate ?? []).map((d) => d.cota - 0.05)) : -1;
                goluri.push({ u0, u1, z0: jos, z1: fat.inaltime - 0.22 });
              }
            }
          }
        }
        // NU PESTE ACOPERIȘ. Etajele arhitecturii au 3,00 m fiecare, iar corpul
        // planului de situație e înalt cât terasa lui (h_terasa, cu 1,50 sub
        // atic) — deci ultimul etaj iese 1,5 m peste corp, și pielea de fațadă
        // se vedea ca un parapet gol în jurul unei terase «fără placă». Panoul
        // se taie la înălțimea corpului; ce e peste e aticul, desenat de corp.
        // …dar nu și PIELEA ATICULUI: panourile care pornesc de la placa de
        // peste ultimul etaj (`z0` ≥ înălțimea nivelului) sunt fața de afară a
        // aticului, cu materialul fațadei pe ea («aticul în același stil ca
        // pereții», Cătălin, 28.09.2026). Tăiate la corp, aticul rămânea zidul
        // alb al corpului, oricare ar fi fost fațada.
        const eAtic = p.z0 >= fat.inaltime - 0.01;
        const zSus = corp && !fat.acoperis && p.surface !== "parapet" && !eAtic
          ? Math.min(p.z1, corp.h - fat.cota) : p.z1;
        if (zSus - p.z0 < 0.02) continue;
        // DIN PLANUL ZIDĂRIEI PÂNĂ LA FAȚA LUI, nu o foiță de 3 mm la 16,5 cm în
        // aer: o cutie subțire la 150 m de cameră cade sub rezoluția
        // adâncimii (~5 mm) și pâlpâie; una plină, lipită de corp, nu are cu ce
        // să se bată — fața ei e singura din planul acela.
        // ORNAMENT CU VOLUM: secțiunea măturată pe față, de pe fața finisajului
        if (p.layer === "relief" && p.section) {
          // DIN PLANUL ZIDĂRIEI: secțiunea stă pe fața finisajului, dar solidul
          // se toarnă de la zidărie până la ea, ca să n-aibă gol în spate acolo
          // unde finisajul e mai subțire decât baza reliefului (fronton cu
          // cărămidă lângă o față cu lamele) și ca la colț cele două cornișe să
          // umple pătratul dintre ele.
          // …AFARĂ DE SECȚIUNEA CARE TRECE PRIN ZID (punctele negative): glaful
          // întreg al aticului și al parapetului (`fatada._glaf_intreg`) e dat
          // chiar de la planul zidăriei, cu lăcrimarele lui pe amândouă fețele,
          // și se desenează cum e — umplut de la zidărie, lăcrimarul de afară
          // s-ar lipi de finisaj și n-ar mai lăsa umbra de sub el.
          const prinZid = Math.min(...p.section.map((q) => q[0])) < 0;
          const bazaRelief = prinZid ? 0 : p.depth_m - Math.max(...p.section.map((q) => q[0]));
          const zMax = Math.max(...p.section.map((q) => q[1]));
          const sec = prinZid ? p.section.map((q) => [q[0], q[1]])
            : [[0, 0], ...p.section.map((q) => [q[0] + bazaRelief, q[1]]), [0, zMax]];
          const Lp = Math.hypot(p.b[0] - p.a[0], p.b[1] - p.a[1]) || 1;
          const uA = [(p.b[0] - p.a[0]) / Lp, (p.b[1] - p.a[1]) / Lp];
          const kA = iesA ? 0 : unghiColt(p.a, p.n, uA, p.surface, "relief", p);
          const kB = iesB ? 0 : unghiColt(p.b, p.n, uA, p.surface, "relief", p);
          // RELIEFUL LIPIT DE UN GOL INTRĂ 5 mm ÎN EL. Glaful se oprea exact la
          // parapetul ferestrei: fața lui de sus și peretele de jos al găurii din
          // finisaj stăteau în același plan, privind amândouă în sus, și se
          // băteau. Cum tocul se așază PE glaf (și sub buiandrug, între
          // montanți), solidul se lungește puțin în gol, iar planul comun dispare.
          // …cât `TAMPLARIE.inset`: fața de sus a pervazului ajunge exact sub
          // tocul tras cu 1,2 cm în sus — căptușeala de jos a golului a plecat
          // (stătea la 7 mm deasupra pervazului, cu aceeași normală)
          const tuck = TAMPLARIE.inset;
          let a = pa, b = pb, z0 = p.z0, z1 = p.z1;
          for (const gol of fat.goluri) {
            if (gol.n[0] * p.n[0] + gol.n[1] * p.n[1] < 0.7) continue;
            const mx = (gol.a[0] + gol.b[0]) / 2, my = (gol.a[1] + gol.b[1]) / 2;
            if (Math.abs((mx - p.a[0]) * p.n[0] + (my - p.a[1]) * p.n[1]) > 0.6) continue;
            const w = Math.hypot(gol.b[0] - gol.a[0], gol.b[1] - gol.a[1]);
            const um = (mx - p.a[0]) * uA[0] + (my - p.a[1]) * uA[1];
            const gu0 = um - w / 2, gu1 = um + w / 2, gz0 = gol.parapet, gz1 = gol.parapet + gol.inaltime;
            const peU = Math.min(gu1, Lp) - Math.max(gu0, 0) > 0.05;
            const peZ = Math.min(gz1, p.z1) - Math.max(gz0, p.z0) > 0.05;
            if (peU && Math.abs(p.z1 - gz0) < 0.01) z1 = p.z1 + tuck;          // glaf sub fereastră
            if (peU && Math.abs(p.z0 - gz1) < 0.01) z0 = p.z0 - tuck;          // buiandrug deasupra
            if (peZ && Math.abs(Lp - gu0) < 0.01) b = [b[0] + uA[0] * tuck, b[1] + uA[1] * tuck];  // montant la stânga golului
            if (peZ && Math.abs(gu1) < 0.01) a = [a[0] - uA[0] * tuck, a[1] - uA[1] * tuck];       // montant la dreapta golului
          }
          // BRÂUL DE LA PLANȘEU se termină exact la capul etajului, iar placajul
          // tăiat sub el (gaura cu 1 cm mai mică decât brâul) păstrează o fâșie
          // până la aceeași cotă: două fețe de sus în același plan. Brâul urcă
          // 3 mm peste cap (și coboară sub pardoseală) și le acoperă
          // …numai un brâu orizontal: o ramă dusă de la pardoseală la cap s-ar
          // suprapune cu cea a etajului vecin pe 6 mm, cu fețele în același plan
          const intreg = Math.abs(z0) < 0.005 && Math.abs(z1 - fat.inaltime) < 0.005;
          if (!intreg && Math.abs(z1 - fat.inaltime) < 0.005 && fat.inaltime > 0) z1 += 0.003;
          if (!intreg && Math.abs(z0) < 0.005) z0 -= 0.003;
          if (!kA && laColtCortina(a)) a = [a[0] + uA[0] * 0.005, a[1] + uA[1] * 0.005];
          if (!kB && laColtCortina(b)) b = [b[0] - uA[0] * 0.005, b[1] - uA[1] * 0.005];
          const H0 = Math.max(...sec.map((q) => q[1])) || 1;
          const secT = (z1 - z0) !== (p.z1 - p.z0)
            ? sec.map((q) => [q[0], q[1] * (z1 - z0) / H0]) : sec;
          const g = profilPeFata(a, b, p.n, z0, 0, secT, fat.cota, kA, kB);
          if (g) panouIn(cheie, culoare, rugos, g, p.material);
          continue;
        }
        let felii: [number, number][] = [[p.z0, zSus]];
        if (p.layer !== "relief") {
          const t = taieDupaRelief(L, p.z0, zSus, reliefurileLui(p, ux, uy, L));
          felii = t.felii;
          // GAURA UNUI RELIEF LIPITĂ DE UN GOL SE DESCHIDE SPRE EL. Gaura din
          // finisaj pentru un brâu e cu 1 cm mai mică decât brâul, ca marginea
          // ei să rămână sub cutia lui. Dar buiandrugul brâului de peste gura
          // logiei pornește exact de la capul golului: rămânea o fâșie de
          // finisaj de 1 cm între ele, cu fața de jos în planul feței de jos a
          // brâului — și se băteau (66 px pe colțul logiilor, 29.09.2026).
          // Gaura brâului intră 5 mm în gol, iar `uneste` le face una.
          // NUMAI cu golul logiei (cel de la pardoseală în jos, `z0 < 0`), nu cu
          // ferestre: o gaură de brâu unită cu o fereastră devine dreptunghiul
          // lor comun și scoate finisajul dintre ele.
          for (const g of t.gauri) {
            for (const h of goluri) {
              if (h.z0 >= 0 || Math.min(g.u1, h.u1) - Math.max(g.u0, h.u0) <= 0) continue;
              if (Math.abs(g.z0 - 0.01 - h.z1) < 0.02) g.z0 = h.z1 - 0.005;
            }
          }
          goluri.push(...t.gauri);
        }
        const uA = [ux, uy];
        const kA = iesA ? 0 : unghiColt(p.a, p.n, uA, p.surface, felul(p), p);
        const kB = iesB ? 0 : unghiColt(p.b, p.n, uA, p.surface, felul(p), p);
        if (iesA) for (const g of goluri) { g.u0 += iesA; g.u1 += iesA; }
        if (p.rosturi && p.layer === "finish" && p.surface === "facade" && !iesA && !iesB
            && p.thickness_m > 0.004 && p.depth_m > p.thickness_m + 0.01) {
          // PLĂCI CU ROST DESCHIS: în spate, cutia substratului până la 3 cm
          // sub plăci, în culoarea rostului (se vede numai prin rosturi). La
          // 5 mm, fața ei și spatele plăcilor se băteau privite de departe (proba
          // de z-fight, 6.10.2026); golul de aer al sistemului e oricum acolo. În
          // față, fiecare placă, cu jumătate de rost lăsat pe fiecare latură.
          // Plăcile de la un colț ieșind își păstrează teșitura (`kA`/`kB`),
          // ca rândurile celor două fețe să se întâlnească în colț.
          const r = p.rosturi.latime / 2;
          const spate = p.depth_m - p.thickness_m - 0.05;
          const cheieRost = p.rosturi.culoare;
          const cheiePlaci = `${culoare.toString(16)}|${rugos}`;
          for (const [f0, f1] of felii) {
            const g = cutiePanou(pa, pb, p.n, f0, f1, spate, spate, goluri, fat.cota, kA, kB);
            if (g) inGrup(geoFundalRost, cheieRost, g);
          }
          for (const [kPlaca, [u0, u1, z0, z1]] of p.rosturi.placi.entries()) {
            const laA = u0 <= 0.001 && kA !== 0, laB = u1 >= L - 0.001 && kB !== 0;
            // PLACA SE ALEGE SINGURĂ (Cătălin, 13.10.2026): pe fațada ventilată
            // fiecare placă e o piesă de montaj, cu id-ul ei `<panou>#p<k>`
            const eidPlaca = p.eid ? `${p.eid}#p${kPlaca}` : "";
            // …și poate avea culoarea ei (`joints.accents`, regula `tile`)
            const acc = p.rosturi.accente?.[kPlaca];
            const cheieAceasta = acc ? `${culoarePanou(p.kind, acc).toString(16)}|${rugos}` : cheiePlaci;
            const ua = laA ? u0 : u0 + r, ub = laB ? u1 : u1 - r;
            if (ub - ua < 0.02) continue;
            const a2 = [pa[0] + ux * ua, pa[1] + uy * ua], b2 = [pa[0] + ux * ub, pa[1] + uy * ub];
            const gauri = goluri.map((h) => ({ ...h, u0: h.u0 - ua, u1: h.u1 - ua }));
            for (const [f0, f1] of felii) {
              const za = Math.max(z0 + r, f0), zb = Math.min(z1 - r, f1);
              if (zb - za < 0.02) continue;
              const g = cutiePanou(a2, b2, p.n, za, zb, p.depth_m, p.thickness_m, gauri, fat.cota,
                                   laA ? kA : 0, laB ? kB : 0);
              if (!g) continue;
              // tonul plăcii: ±3 %, din poziția ei — două plăci vecine nu sunt
              // niciodată perfect la fel, iar fața se citește ca făcută din plăci
              const ton = 0.97 + 0.06 * (((Math.sin((a2[0] * 12.9898 + a2[1] * 78.233 + za * 37.719)) * 43758.5453) % 1 + 1) % 1);
              // CANTURILE PLĂCII ÎN UMBRĂ: fețele laterale (cele care nu privesc
              // afară) iau un ton închis. Luminate ca fața, canturile de 2 cm se
              // citeau ca o linie deschisă între plăci — un rost chituit, nu un
              // gol (Cătălin, 6.10.2026)
              const pos = g.attributes.position.count;
              const nor = g.attributes.normal;
              const col = new Float32Array(pos * 3);
              for (let i = 0; i < pos; i++) {
                const fata = nor ? nor.getX(i) * p.n[0] + nor.getY(i) * p.n[1] > 0.7 : true;
                col.fill(fata ? ton : 0.28, i * 3, i * 3 + 3);
              }
              g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
              if (eidPlaca) g.userData.eid = eidPlaca;
              inGrup(geoPlaci, cheieAceasta, g);
            }
          }
          continue;
        }
        // CAPĂTUL DREPT AL UNUI STRAT DE SUB FINISAJ, 5 mm înapoi: toate
        // straturile se toarnă din planul zidăriei, deci capătul lui cădea în
        // planul capătului finisajului — la vedere și pâlpâind pe soclul tăiat
        // de gura decroșului de intrare. Finisajul îl acoperă oricum pe toată
        // grosimea; retras, capătul lui rămâne înăuntrul finisajului.
        const subA = kA === 0 && !iesA && subFinisaj(p.a, p) ? SUB_FINISAJ_M : 0;
        const subB = kB === 0 && !iesB && subFinisaj(p.b, p) ? SUB_FINISAJ_M : 0;
        const qa = subA ? [pa[0] + ux * subA, pa[1] + uy * subA] : pa;
        const qb = subB ? [pb[0] - ux * subB, pb[1] - uy * subB] : pb;
        const gq = subA ? goluri.map((g) => ({ ...g, u0: g.u0 - subA, u1: g.u1 - subA })) : goluri;
        for (const [f0, f1] of felii) {
          const g = cutiePanou(qa, qb, p.n, f0, f1, p.depth_m, p.depth_m, gq, fat.cota, kA, kB);
          if (g) panouIn(cheie, culoare, rugos, g, p.material);
        }
      }

      // BALUSTRADELE, după fel: sticlă (translucid + mână curentă), lamele
      // (opac închis), plin (opac deschis).
      for (const r of fat.parapete) {
        eidAcum = r.eid;
        // MÂNA CURENTĂ: cea scrisă, niciuna dacă stilul a scos-o; pe sticlă,
        // lamele și gardă umplutura se oprește sub ea (nu iese prin bară)
        const mana = manaDeDesenat(r);
        const subMana = mana && r.kind !== "solid" ? mana.height_m : 0;
        for (let i = 0; i + 1 < r.axis.length; i++) {
          const a = r.axis[i], b = r.axis[i + 1];
          const ex = b[0] - a[0], ey = b[1] - a[1], l = Math.hypot(ex, ey);
          if (l < 0.15) continue;
          let nx = ey / l, ny = -ex / l;
          // NORMALA SPRE AFARĂ: spre partea în care NU e spațiul exterior al
          // balustradei (balconul, logia, terasa). Parapetul plin e un zid cu
          // grosime, așezat înăuntru de la ax; mâna curentă stă pe mijlocul lui.
          const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
          const inele = r.outdoor === "loggia" ? fat.logii : r.outdoor === "terrace" ? fat.terase
            : r.outdoor === "balcony" ? fat.balcoane : [...fat.logii, ...fat.balcoane, ...fat.terase];
          const inel = inele.reduce<number[][] | null>((best, ring) =>
            (!best || distLaInel(mx, my, ring) < distLaInel(mx, my, best)) ? ring : best, null);
          if (inel && inPoligonRing(mx + nx * 0.15, my + ny * 0.15, inel)) { nx = -nx; ny = -ny; }
          // de pe bordura zidită, când e balustrada unei terase
          const zJos = fat.cota + (r.sill_m ?? 0);
          if (r.kind === "screen") {
            // ECRANUL FIX: cât etajul, fără mână curentă — nu e balustradă;
            // GARDA de tablă a unei ferestre înalte o poate avea
            ecranIn(r, a, b, zJos, zJos + r.height_m - subMana, nx, ny, i === 0, i + 2 === r.axis.length);
            if (!mana) continue;
          } else
          if (r.kind === "solid" || r.kind === "planter") {
            // ZID PLIN DE 20 cm, cu fața de afară pe ax: pus ca o foaie pe ax,
            // parapetul logiei părea o placă subțire, în spatele pielii.
            // JARDINIERA e același zid, cât grosimea ei (`thickness_m`).
            const gros = r.kind === "planter" ? (r.thickness_m ?? 0.4) : 0.2;
            const cutie = cutiePanou(a, b, [nx, ny], r.sill_m ?? 0, (r.sill_m ?? 0) + r.height_m,
                                     0, gros, [], fat.cota, 0, 0);
            if (cutie) {
              // PARAPETUL PLIN ÎȘI ARATĂ MATERIALUL (HPL, lemn, piatră), nu doar
              // culoarea: sub el banda de pe marginea plăcii (`fatada.
              // _muchia_ca_parapetul`) vine în același material, iar desenată
              // plat, deasupra unei benzi texturate, ieșeau două fețe diferite
              // (12.10.2026). Fără desen propriu rămâne culoarea plată.
              const cul = culoarePanou("cladding", r.colour);
              if (r.colour && r.material && texturaMaterial(r.material, cul)) {
                panouIn(`cladding|${r.material}|${cul.toString(16)}`, cul, 0.6, cutie, r.material);
              } else plinIn(r.colour, cutie);
            }
            if (r.kind === "planter") {
              // PĂMÂNTUL ȘI PLANTELE, după `planting` (lipsă = amestec; `none`
              // = jgheabul gol)
              if (r.planting !== "none") {
                const zSus = zJos + r.height_m;
                const pam = geometriePamant(a, b, [nx, ny], zSus, gros);
                if (pam) geoPamant.push(pam);
                planteScena.push(...plantePeJardiniera(a, b, [nx, ny], zSus, gros, r.planting));
              }
              continue;                              // n-are mână curentă: e marginea ei
            }
          } else if (r.kind === "slats") {
            // LAMELELE CU GOLURI ADEVĂRATE: aceeași cutie cu fante tăiate ca
            // ecranul fix, nu o foaie transparentă. Foaia de 55 % în culoarea
            // lamelelor se citea drept sticlă fumurie — pe Demo-UTR6 omul a
            // cerut «sticla clară» pe niște lamele roșii (6.10.2026). Blender
            // le desenează la fel (`pierced`).
            ecranIn({ ...r, pattern: "slats", openness: 0.6, thickness_m: 0.03,
                      colour: r.colour ?? "#2a2d31" },
                    a, b, zJos, zJos + r.height_m - subMana, nx, ny, i === 0, i + 2 === r.axis.length);
          } else {
            const q = golQuad(a, b, zJos, zJos + r.height_m - subMana, nx, ny, 0);
            if (!q) continue;
            balustradaIn(r.kind, r.colour, q);
            // PROFILUL DE STICLĂ: șina de la bază, pe ax, în care sticla intră
            // ca într-o menghină (§27); aceeași cutie ca mâna curentă, de jos
            if (r.kind === "glass" && r.profil) {
              const pr = { profile: "rect" as const, width_m: r.profil.width_m, height_m: r.profil.height_m,
                           material: r.profil.material, colour: r.profil.colour };
              const sina = geometriaManii(a, b, [nx, ny], zJos + pr.height_m, 0, pr,
                                          i > 0 ? pr.width_m / 2 : 0, i + 2 < r.axis.length ? pr.width_m / 2 : 0);
              if (sina) {
                // PROFILUL SE ALEGE SINGUR, ca mâna curentă: `<balustradă>#profil`
                if (r.eid) sina.userData.eid = `${r.eid}#profil`;
                const cheieP = `${pr.colour ?? "#2a2c2f"}|${pr.material ?? ""}`;
                (geoMana.get(cheieP) ?? geoMana.set(cheieP, lista()).get(cheieP)!).push(sina);
              }
            }
          }
          // MÂNA CURENTĂ: pe ax la sticlă, lamele și gardă, pe mijlocul zidului
          // la plin (v. `susulManii`); la colțurile axului bucățile se
          // prelungesc cu o jumătate de lățime, ca să se întâlnească
          if (!mana) continue;
          const bara = geometriaManii(a, b, [nx, ny], susulManii(r.kind, zJos + r.height_m, mana),
                                      r.kind === "solid" ? 0.1 : 0, mana,
                                      i > 0 ? mana.width_m / 2 : 0,
                                      i + 2 < r.axis.length ? mana.width_m / 2 : 0);
          if (!bara) continue;
          const cheieMana = `${mana.colour ?? "#2a2c2f"}|${mana.material ?? ""}`;
          // MÂNA CURENTĂ SE ALEGE SINGURĂ: `<balustradă>#mana`
          if (r.eid) bara.userData.eid = `${r.eid}#mana`;
          (geoMana.get(cheieMana) ?? geoMana.set(cheieMana, lista()).get(cheieMana)!).push(bara);
        }
      }

      // GOLURILE LIPITE: două goluri coliniare, în același sens, la cel mult
      // 12 cm unul de altul (frontul vitrat al compoziției: canat–ușă–canat,
      // două camere care se întâlnesc pe axul calcanului). Fiecare își află
      // vecinul de la capătul `a` și de la `b`; tâmplăria le desenează ca un
      // singur front cu un montant între («fereastră, 20 cm, fereastră» —
      // Cătălin, 1.10.2026).
      for (const gol of fat.goluri) { gol.lipitA = undefined; gol.lipitB = undefined; }
      for (const g1 of fat.goluri) {
        const L1 = Math.hypot(g1.b[0] - g1.a[0], g1.b[1] - g1.a[1]) || 1;
        const ux = (g1.b[0] - g1.a[0]) / L1, uy = (g1.b[1] - g1.a[1]) / L1;
        for (const g2 of fat.goluri) {
          if (g2 === g1) continue;
          if (g1.n[0] * g2.n[0] + g1.n[1] * g2.n[1] < 0.9) continue;
          const dx = g2.a[0] - g1.b[0], dy = g2.a[1] - g1.b[1];
          const deALungul = dx * ux + dy * uy;
          const perp = Math.abs(-dx * uy + dy * ux);
          if (perp > 0.05 || deALungul < -0.02 || deALungul > 0.12) continue;
          // același rost jos–sus, altfel o fereastră mică lângă una înaltă nu e «lipită»
          if (Math.abs(g1.parapet - g2.parapet) > 0.05 || Math.abs(g1.inaltime - g2.inaltime) > 0.05) continue;
          const gap = Math.max(0, deALungul);
          g1.lipitB = gap;
          g2.lipitA = gap;
        }
      }
      for (const gol of fat.goluri) {
        eidAcum = gol.eid ?? null;
        const z0 = fat.cota + gol.parapet;
        const z1 = corp ? Math.min(z0 + gol.inaltime, corp.h - 0.1) : z0 + gol.inaltime;
        if (z1 - z0 < 0.2) continue;
        // TÂMPLĂRIA DIN DOCUMENT (v. `tamplarie3d`): toc cu adâncime, tras în
        // spatele finisajului cu retragerea scrisă, canate cu cercevea sau
        // baghetă, montanți, geam, șpaleți de tencuială, mânere pe canatele
        // mobile, prag la uși. Fundalul încăperii rămâne opac și întunecat, la
        // fel la toate ferestrele — ce variază legitim e doar tâmplăria.
        const finGol = finisajulGolului(fat.panouri, gol);
        const parti = geometriaGolului(gol, z0, z1,
          finGol && finGol.depth_m > 0.02 ? finGol.depth_m : TAMPLARIE.finisaj);
        for (const g of parti.toc) tocIn(gol.culoare, g);
        geoGeam.push(...parti.geam);
        geoInterior.push(...parti.interior);
        geoManer.push(...parti.maner);
        // CĂPTUȘEALA GOLULUI, ÎN CULOAREA TOCULUI: glaful lateral și buiandrugul
        // de tablă vopsită ca tâmplăria, deci tocul se citește CONTINUU peste
        // finisaj — în culoarea finisajului, fără desenul plăcilor, ieșea ca o
        // fâșie albă lângă tocul închis («tocul nu e continuu, o fâșie albă»,
        // Cătălin, 12.10.2026). Golul fără culoare de toc rămâne pe albul zidului.
        for (const g of parti.spalet) tocIn(gol.culoare, g);
        // pragul, în culoarea tocului, ca pervazul («de aceeași culoare ca tocul»)
        for (const g of parti.prag) tocIn(gol.culoare, g);
        nrGol++;
      }

      // Balcoanele: placa lor și parapetul de sticlă, pe conturul real.
      // PARDOSEALA LOGIEI: corpul e scobit pe toată înălțimea nivelului, deci
      // placa pe care stă logia trebuie desenată — altfel scobitura ar da în
      // etajul de dedesubt.
      for (const lg of fat.logii) {
        // DECROȘUL INTRĂRII pe un bloc ridicat: pardoseala lui e la teren
        // (`denivelate`, mai jos), nu placa de la ±0,00 a unei logii
        if (fat.intrari?.includes(lg) && (fat.denivelate ?? []).some((d) => d.cota < -0.05)) continue;
        const f2 = forma({ poly: lg, holes: [] });
        if (!f2) continue;
        const g2 = new THREE.ExtrudeGeometry(f2, { depth: 0.23, bevelEnabled: false });
        // …cu 1 cm PESTE corpul de dedesubt: fața lui de sus stă chiar la cota
        // nivelului, iar o placă terminată tot acolo pâlpâia cu ea (podeaua terasei)
        g2.translate(0, 0, fat.cota - 0.22);
        deArs.push(g2);
        const m2 = new THREE.Mesh(g2, M.placa);
        m2.castShadow = true; m2.receiveShadow = true;
        lume.add(m2);
      }
      // PLACA DE PESTE DECROȘUL INTRĂRII: corpul parterului e scobit pe toată
      // înălțimea, iar placa de peste o logie o pune logia etajului de sus. Peste
      // intrare nu stă nicio logie, deci între tavanul scobiturii (sub placă) și
      // corpul etajului 1 rămânea o fantă de 22 cm prin care se vedea corpul alb.
      // Fața de sus cu 5 mm sub cota etajului: acolo începe corpul lui.
      const logiiSus = scena.fatade
        .filter((f) => f.corp === fat.corp && Math.abs(f.cota - (fat.cota + fat.inaltime)) < 0.05)
        .flatMap((f) => f.logii);
      for (const lg of fat.intrari ?? []) {
        if (logiiSus.some((s) => s.length === lg.length
                                 && s.every((q, i) => Math.hypot(q[0] - lg[i][0], q[1] - lg[i][1]) < 0.05))) continue;
        const f2 = forma({ poly: lg, holes: [] });
        if (!f2) continue;
        const g2 = new THREE.ExtrudeGeometry(f2, { depth: 0.215, bevelEnabled: false });
        g2.translate(0, 0, fat.cota + fat.inaltime - 0.22);
        deArs.push(g2);
        const m2 = new THREE.Mesh(g2, M.placa);
        m2.castShadow = true; m2.receiveShadow = true;
        lume.add(m2);
      }
      // PLĂCILE LA ALTĂ COTĂ (3.12): pardoseala holului coborât la teren, cu
      // 1 cm înăuntrul gropii — laturile ei nu cad în planul pereților gropii
      // (soclul scobit); apoi treptele, mâinile curente și platforma lui.
      if (fat.denivelate?.length || fat.trepte?.length || fat.platforme?.length) {
        const matTrepte = new THREE.MeshStandardMaterial({ color: 0xd9d5cd, roughness: 0.92 });
        const matMetal = new THREE.MeshStandardMaterial({ color: 0x4a4e52, roughness: 0.5, metalness: 0.4 });
        deArs.push(matTrepte, matMetal);
        const pune = (geos: THREE.BufferGeometry[], mat: THREE.Material) => {
          for (const g of geos) {
            deArs.push(g);
            const m2 = new THREE.Mesh(g, mat);
            m2.castShadow = true; m2.receiveShadow = true;
            lume.add(m2);
          }
        };
        for (const dn of fat.denivelate ?? []) {
          const f2 = forma({ poly: insetInel(dn.poly, 0.01), holes: [] });
          if (!f2) continue;
          // placa de beton și pardoseala ei finită, până la `FINISAJ_M` peste cotă
          const g2 = new THREE.ExtrudeGeometry(f2, { depth: 0.15 + FINISAJ_M, bevelEnabled: false });
          g2.translate(0, 0, fat.cota + dn.cota - 0.15);
          pune([g2], M.placa);
        }
        for (const tr of fat.trepte ?? []) {
          const z0 = fat.cota + tr.cota0 + FINISAJ_M, h = tr.h - FINISAJ_M;
          pune(treptePeAmprenta(tr.poly, tr.deg, z0, h, tr.contratrepte), matTrepte);
          pune(mainiCurente(tr.poly, tr.deg, z0, h, tr.contratrepte), matMetal);
        }
        for (const pl of fat.platforme ?? []) {
          const p3 = platforma3d(pl.poly, fat.cota + pl.cota0 + FINISAJ_M);
          pune(p3.placa, matTrepte);
          pune(p3.metal, matMetal);
        }
      }
      // COPERTINELE: plăcile în consolă fără balcon pe ele — tavanul unei cutii
      // de dedesubt. Aceeași placă, la cota ei (înălțimea etajului pe ultimul).
      for (const cp of fat.copertine ?? []) {
        const f2 = forma({ poly: cp.poly, holes: [] });
        if (!f2) continue;
        // COPERTINA DE METAL a intrării (3.19): un cadru subțire, antracit, cu
        // sticla deasupra — nu placa albă a unei console de beton
        const metal = cp.structura === "metal";
        const gros = metal ? (cp.grosime ?? 0.12) : 0.215;
        const g2 = new THREE.ExtrudeGeometry(f2, { depth: gros - (metal ? 0.005 : 0), bevelEnabled: false });
        g2.translate(0, 0, fat.cota + cp.cota - (metal ? gros : 0.22));
        deArs.push(g2);
        const m2 = new THREE.Mesh(g2, metal ? M.copertinaMetal : M.placa);
        m2.castShadow = true; m2.receiveShadow = true;
        lume.add(m2);
      }
      // SPOTURILE INTRĂRII: discuri luminoase sub tavanul decroșului și sub
      // copertină, cu 3 mm sub fața lor (nu în planul ei)
      for (const l of fat.lumini ?? []) {
        const g2 = new THREE.CircleGeometry(0.06, 16);
        g2.rotateX(Math.PI);
        g2.translate(l.x, l.y, fat.cota + l.z - 0.003);
        deArs.push(g2);
        lume.add(new THREE.Mesh(g2, M.spot));
      }
      eidAcum = null;
      for (const [ib, b] of fat.balcoane.entries()) {
        const f2 = forma({ poly: b, holes: [] });
        if (!f2) continue;
        // PLACA BALCONULUI, CU FAȚA DE SUS 5 mm SUB COTA NIVELULUI: la cota
        // nivelului se termină finisajul etajului de dedesubt (fața lui de sus)
        // și placa trece prin piele — două fețe în sus în același plan, pe
        // adâncimea pielii, care se băteau (51 px pe fâșiile de balcon, 29.09.2026).
        const g2 = new THREE.ExtrudeGeometry(f2, { depth: 0.215, bevelEnabled: false });
        g2.translate(0, 0, fat.cota - 0.22);
        deArs.push(g2);
        const m2 = new THREE.Mesh(g2, M.placa);
        m2.castShadow = true; m2.receiveShadow = true;
        // placa balconului e un obiect de ales întreg (`Space` din document)
        if (fat.balcoaneEid?.[ib]) { m2.userData.eid = fat.balcoaneEid[ib]; plaseAlese.push(m2); }
        lume.add(m2);
        // Balustrada vine din document (`Railing`) când există; banda dedusă
        // pe conturul balconului rămâne doar pentru documentele fără fațadă —
        // altfel două sticle la 2 cm una de alta pâlpâie între ele.
        if (!fat.parapete.length) {
          geoParapet.push(...bandaPeInel(b, fat.cota, fat.cota + 1.05, 0.02));
        }
      }
      if (perNivel) {
        varsaBins();
        const cn = { corp: scena.corpuri[fat.corp]?.id ?? String(fat.corp), nivel: fat.nivel ?? 0,
                     fel: fat.acoperis ? "acoperis" : "fatada" };
        for (const o of lume.children.slice(deAici)) o.userData.corpNivel = cn;
      }
    }
    if (!perNivel) varsaBins();

    const taietura = new TaieturaEtaj(lume, scena.fatade, planuriRef.current ?? [], {
      nume: (sp) => {
        const k = `macheta.camera.${sp.kind}`;
        return esteCheie(k) ? tr(k as Cheie) : (sp.label ?? sp.kind);
      },
      arie: (m2) => `${m2.toLocaleString(bcp47(limba()), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m²`,
      texturaMax: Math.min(4096, rand.capabilities.maxTextureSize),
      anizotropie: Math.min(8, rand.capabilities.getMaxAnisotropy()),
    });
    // fundalul din spatele geamului e acum camera închipuită (`camera3d`)
    taietura.pregateste(lume.children.slice(inceputCladire), M.camera);
    // aceeași felie e CLĂDIREA pe care o stinge prop-ul `cladire`
    const cladireObiecte = lume.children.slice(inceputCladire);

    // Limita de proprietate, ca linie — nu e o suprafață, e o convenție.
    if (scena.lot.length >= 3) {
      const p = scena.lot.map((q) => new THREE.Vector3(q[0], q[1], 0.12));
      p.push(p[0].clone());
      const g = new THREE.BufferGeometry().setFromPoints(p);
      deArs.push(g);
      sol.add(new THREE.Line(g, M.lot));
    }
    marcheaza("lot");

    // ------------------------------------------------------------- vegetație
    // Pomii și boscheții, o plasă instanțiată pe variantă: vezi `copaci3d.ts`.
    if (scena.copaci.length || scena.tufisuri.length) {
      const pomi = construiestePomi(scena.copaci, scena.tufisuri);
      deArs.push(...pomi.deArs);
      sol.add(...pomi.obiecte);
    }
    marcheaza("copaci");

    // ------------------------------------------------------------ zone
    // Ghenele cu pereți și pubele, locurile de joacă cu gard, leagăn și
    // tobogan: vezi `zone3d.ts`. Deschiderea și poarta dau spre trotuar.
    if (scena.zone.length) {
      const zone3 = construiesteZone(scena.zone, scena.drumuri, scena.trotuare);
      deArs.push(...zone3.deArs);
      sol.add(...zone3.obiecte);
    }
    marcheaza("mobilier");

    // ---------------------------------------------------------------- mașini
    // Corpuri netede în alb și gri, câte o plasă instanțiată pe variantă: vezi
    // `masini3d.ts`. Locul, varianta și nuanța vin din `masiniLot`, ca în 2D.
    if (scena.locuri.length) {
      const masini = construiesteMasini(scena.locuri);
      deArs.push(...masini.deArs);
      sol.add(...masini.obiecte);
    }
    marcheaza("masini");

    // ---------------------------------------------------------------- cameră
    // Planul apropiat urcă cu scara amplasamentului: camera nu coboară sub
    // `minDistance` = 0,12·R, deci la 1 % din R nu taie nimic, iar rezoluția
    // adâncimii — invers proporțională cu planul apropiat — crește de câteva ori.
    const cam = new THREE.PerspectiveCamera(38, 1, Math.max(0.5, R * 0.01), R * 60);
    cam.up.set(0, 0, 1);
    const ctrl = new OrbitControls(cam, rand.domElement);
    // Cârlig de depanare pentru harnașament (`ui/dev/macheta.tsx`): camera, 
    // controalele și corpurile, ca o probă să poată așeza camera pe un colț
    // anume fără să-l caute cu tastele.
    (rand.domElement as HTMLCanvasElement & { __macheta?: unknown }).__macheta =
      { cam, ctrl, corpuri: scena.corpuri, scena, rand, s3, lume, THREE, plase: plaseAlese,
        oglinda: { inCadrulDocumentului, razaInDocument, inDocument, inLume } };

    // ---------------------------------------------------------------- selecția
    // CLIC = ALEGE, fără comutator (Cătălin, 12.10.2026): o apăsare și o
    // ridicare cu sub 5 px între ele e un clic; mai mult e o rotire. Shift +
    // clic adaugă sau scoate. ⌥/Alt + tragere pe o față desenează o ZONĂ.
    const fete = liniileFetelor(scena.fatade);
    rand.localClippingEnabled = true;     // zona taie suprafețele continue la marginea ei
    // ținta camerei urcă lin la etajul tăiat (v. bucla)
    let tintaZ: number | null = null;
    const st: StareAlegere = { lume, plase: plaseAlese, fete, fatade: scena.fatade, cam, ctrl, rand, taietura,
                               cladire: cladireObiecte, sol, laCota: (z) => { tintaZ = z; } };
    alegere.current = st;
    const raza = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const laCursor = (ev: { clientX: number; clientY: number }) => {
      const r = rand.domElement.getBoundingClientRect();
      ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
      raza.setFromCamera(ndc, cam);
      razaInDocument(raza.ray);
    };
    const lovituri = (ev: { clientX: number; clientY: number }) => {
      laCursor(ev);
      // nimic de deasupra tăieturii; un perete interior oprește raza
      return inCadrulDocumentului(lume, () => taietura.vizibile(raza, plaseAlese));
    };
    const eidLovit = (h: THREE.Intersection): string | null => {
      const o = h.object as THREE.Mesh;
      if (o.userData.eid) return String(o.userData.eid);
      const t = o.userData.alege as TabelAlegere | undefined;
      return t && h.faceIndex != null ? eidLaTriunghi(t, h.faceIndex) : null;
    };
    /** Piesa lovită a unui strat ATAȘAT (v. `macheta/tipuri`): cea mai
     *  apropiată de pe raza de acum, dacă e vizibilă și mai aproape decât
     *  `dMax` (prima lovitură în clădire). three nu ține cont de `visible`
     *  la raycast, deci filtrul e aici. */
    const lovituraAtasata = (dMax: number | undefined): Tinta | null => {
      const straturi = cuAlegere.current.atasate ?? [];
      if (!straturi.length) return null;
      const vizibil = (o: THREE.Object3D | null): boolean => !o || (o.visible !== false && vizibil(o.parent));
      const hs = inCadrulDocumentului(lume, () => raza.intersectObjects(straturi.map((s) => s.grup), true))
        .filter((x) => vizibil(x.object) && (dMax == null || x.distance < dMax));
      for (const x of hs) for (const s of straturi) { const t = s.tinta(x.object); if (t) return t; }
      return null;
    };
    /** fața din care face parte obiectul lovit: panoul își spune fața; restul
     *  (o balustradă, un gol) — fața cea mai apropiată de punct */
    const fataLovita = (p: THREE.Vector3): LiniaFetei | null => {
      let best: LiniaFetei | null = null, bd = Infinity;
      for (const f of fete.values()) {
        const { u, dist } = peFata(f, p.x, p.y);
        if (dist < -0.6 || dist > 3.5 || u < -0.5 || u > f.len + 0.5) continue;
        if (Math.abs(dist) < bd) { bd = Math.abs(dist); best = f; }
      }
      return best;
    };
    /** Fața de sub cursor: a panoului lovit, când e o suprafață a feței;
     *  altfel (un balcon, o balustradă, un gol) cea mai apropiată. */
    const fataDeSub = (h: THREE.Intersection): LiniaFetei | null => {
      const eid = eidLovit(h);
      st.info ??= infoElemente(scena.fatade);
      const inf = eid ? st.info.get(eid) : undefined;
      return (inf?.continuu && inf.face ? fete.get(inf.face) : undefined) ?? fataLovita(h.point);
    };
    /** Cât ține corpul pe verticală (de la primul planșeu la tavanul ultimului etaj). */
    const inaltimeCorp = new Map<number, [number, number]>();
    for (const e of scena.fatade) {
      if (e.acoperis || !(e.inaltime > 0)) continue;
      const v = inaltimeCorp.get(e.corp) ?? [Infinity, -Infinity];
      inaltimeCorp.set(e.corp, [Math.min(v[0], e.cota), Math.max(v[1], e.cota + e.inaltime)]);
    }
    /** Punctul razei pe planul feței `f` (vertical, prin linia ei). */
    const peFataRazei = (f: LiniaFetei) => {
      const n = new THREE.Vector3(f.n[0], f.n[1], 0);
      const den = n.dot(raza.ray.direction);
      if (Math.abs(den) < 1e-6) return null;
      const t = -(n.dot(raza.ray.origin) - (f.n[0] * f.o[0] + f.n[1] * f.o[1])) / den;
      if (t <= 0) return null;
      const q = raza.ray.at(t, new THREE.Vector3());
      return { t, u: peFata(f, q.x, q.y).u, z: q.z };
    };
    /** LIPIREA pe orizontală, în metri: ~10 px la distanța punctului. */
    const toleranta = (q: THREE.Vector3) =>
      (10 * 2 * inDocument(cam.position).distanceTo(q) * Math.tan((cam.fov * Math.PI) / 360)) / Math.max(1, rand.domElement.clientHeight);
    const liniiLipire = new Map<string, number[]>();
    const liniiFetei = (f: LiniaFetei) => {
      let l = liniiLipire.get(f.face);
      if (!l) { l = liniiDeLipire(f, scena.fatade, cuAlegere.current.coloane?.[f.face]); liniiLipire.set(f.face, l); }
      return l;
    };
    const fateVecine = (f: LiniaFetei) => [...fete.values()].filter((g) => coltulComun(f, g));

    // ⌥/Alt ȚINUT: cursorul devine cruce, iar fața de sub el capătă un contur
    // fin — ca omul să știe pe ce față va trage, înainte să tragă.
    const matLinie = new THREE.LineDashedMaterial({ color: 0x00e0ff, dashSize: 0.45, gapSize: 0.25,
                                                    depthTest: false, transparent: true, opacity: 0.95 });
    const matLinieFata = new THREE.LineDashedMaterial({ color: 0x9fefff, dashSize: 0.3, gapSize: 0.3,
                                                        depthTest: false, transparent: true, opacity: 0.7 });
    deArs.push(matLinie, matLinieFata);
    let conturFata: THREE.LineSegments | null = null;
    let fataSubCursor: string | null = null;
    const scoateConturFata = () => {
      if (!conturFata) return;
      lume.remove(conturFata);
      conturFata.geometry.dispose();
      conturFata = null;
      fataSubCursor = null;
    };
    const arataFata = (f: LiniaFetei | null) => {
      if ((f?.face ?? null) === fataSubCursor) return;
      scoateConturFata();
      if (!f) return;
      const [z0, z1] = inaltimeCorp.get(f.corp) ?? [0, 3];
      const g = new THREE.BufferGeometry().setFromPoints(margineaBucatii(f, { face: f.face, u0: 0, u1: f.len }, z0, z1));
      conturFata = new THREE.LineSegments(g, matLinieFata);
      conturFata.computeLineDistances();
      conturFata.renderOrder = 19;
      lume.add(conturFata);
      fataSubCursor = f.face;
    };
    let cuAlt = false;
    const puneAlt = (da: boolean) => {
      if (da === cuAlt) return;
      cuAlt = da;
      rand.domElement.style.cursor = da ? "crosshair" : "";
      if (!da) scoateConturFata();
    };
    const laTastaAlt = (ev: KeyboardEvent) => {
      if (ev.key === "Alt" && cuAlegere.current.laZona) puneAlt(ev.type === "keydown");
    };
    const laPierdereFocus = () => puneAlt(false);
    window.addEventListener("keydown", laTastaAlt);
    window.addEventListener("keyup", laTastaAlt);
    window.addEventListener("blur", laPierdereFocus);
    let cautareFata = 0;

    let apasat: { x: number; y: number; shift: boolean } | null = null;
    let ultimulClic: { eid: string; t: number } | null = null;
    let trage: { lant: LiniaFetei[]; a: { u: number; z: number }; b: { u: number; z: number };
                 shift: boolean; zona: ZonaMacheta | null; desen: DesenZona | null; cadru: number } | null = null;
    const redeseneaza = () => {
      const t = trage;
      if (!t) return;
      t.cadru = 0;
      t.desen?.scoate();
      t.desen = null;
      t.zona = zonaDinTragere(scena.fatade, t.lant, t.a, t.b);
      if (t.zona) t.desen = deseneazaZona(st, t.zona, true);
    };
    const laApasare = (ev: PointerEvent) => {
      if (ev.button !== 0 || !cuAlegere.current.laAlegere && !cuAlegere.current.laZona) return;
      if (ev.altKey && cuAlegere.current.laZona) {
        const h = lovituri(ev)[0];
        const f = h ? fataDeSub(h) : null;
        if (!h || !f) return;
        ev.stopImmediatePropagation();
        ev.preventDefault();
        ctrl.enabled = false;
        scoateConturFata();
        const u0 = peFata(f, h.point.x, h.point.y).u;
        const a = { u: ev.metaKey ? u0 : lipeste(u0, liniiFetei(f), toleranta(h.point)), z: h.point.z };
        trage = { lant: [f], a, b: a, shift: ev.shiftKey, zona: null, desen: null, cadru: 0 };
        rand.domElement.setPointerCapture(ev.pointerId);
        return;
      }
      apasat = { x: ev.clientX, y: ev.clientY, shift: ev.shiftKey };
    };
    const laMiscare = (ev: PointerEvent) => {
      if (!trage) {
        // conturul feței de sub cursor, cât ține Alt (o căutare pe cadru)
        puneAlt(ev.altKey && !!cuAlegere.current.laZona);
        if (!cuAlt || cautareFata) return;
        cautareFata = requestAnimationFrame(() => {
          cautareFata = 0;
          if (!cuAlt) return;
          const h = lovituri(ev)[0];
          arataFata(h ? fataDeSub(h) : null);
        });
        return;
      }
      laCursor(ev);
      // PESTE COLȚ: dintre fața de acum, cea dinainte și vecinele ei, cea pe
      // care raza o atinge ÎNTÂI în lungul ei — la un colț ieșit, planul feței
      // de acum e atins dincolo de capătul ei, deci vecina câștigă
      const lant = trage.lant;
      const ultima = lant[lant.length - 1];
      const dinainte = lant.length > 1 ? lant[lant.length - 2] : null;
      const [zj, zs] = inaltimeCorp.get(ultima.corp) ?? [-Infinity, Infinity];
      let ales: { f: LiniaFetei; u: number; z: number; t: number } | null = null;
      for (const f of [ultima, ...fateVecine(ultima).filter((g) => !lant.includes(g) || g === dinainte)]) {
        const q = peFataRazei(f);
        if (!q || q.u < -0.05 || q.u > f.len + 0.05 || q.z < zj - 1 || q.z > zs + 1) continue;
        if (!ales || q.t < ales.t) ales = { f, ...q };
      }
      if (ales && ales.f === dinainte) lant.pop();
      else if (ales && ales.f !== ultima) lant.push(ales.f);
      const f = lant[lant.length - 1];
      const q = ales && ales.f === f ? ales : peFataRazei(f);
      if (!q) return;
      const lumea = raza.ray.at(q.t, new THREE.Vector3());
      trage.b = { u: ev.metaKey ? q.u : lipeste(q.u, liniiFetei(f), toleranta(lumea)), z: q.z };
      if (!trage.cadru) trage.cadru = requestAnimationFrame(redeseneaza);
    };
    const laRidicare = (ev: PointerEvent) => {
      if (trage) {
        const t = trage;
        if (t.cadru) cancelAnimationFrame(t.cadru);
        trage = null;
        t.desen?.scoate();
        ctrl.enabled = true;
        const z = zonaDinTragere(scena.fatade, t.lant, t.a, t.b);
        if (z) cuAlegere.current.laZona?.(z, t.shift);
        return;
      }
      const a = apasat;
      apasat = null;
      if (!a || ev.button !== 0 || Math.hypot(ev.clientX - a.x, ev.clientY - a.y) > 5) return;
      // CLĂDIREA STINSĂ nu se alege: raza trece prin ea, la straturile atașate
      const h = cuAlegere.current.cladire === false ? (laCursor(ev), undefined) : lovituri(ev)[0];
      // un strat atașat câștigă când piesa lui e mai aproape decât clădirea
      const tinta = lovituraAtasata(h?.distance);
      if (tinta) { ultimulClic = null; cuAlegere.current.laTinta?.(tinta, a.shift); return; }
      let eid = h ? eidLovit(h) : null;
      if (!eid) cuAlegere.current.laTinta?.(null, a.shift);
      // DUBLU CLIC PE O PARTE = TOT OBIECTUL: o placă aleasă, al doilea clic
      // pe ea alege panoul întreg; mâna curentă → balustrada
      const acum = performance.now();
      if (eid && eid.includes("#") && ultimulClic && ultimulClic.eid === eid && acum - ultimulClic.t < 400) {
        ultimulClic = null;
        eid = bazaEid(eid);
        cuAlegere.current.laAlegere?.(eid, a.shift);
        return;
      }
      ultimulClic = eid ? { eid, t: acum } : null;
      cuAlegere.current.laAlegere?.(eid, a.shift);
    };
    rand.domElement.addEventListener("pointerdown", laApasare, { capture: true });
    rand.domElement.addEventListener("pointermove", laMiscare);
    rand.domElement.addEventListener("pointerup", laRidicare);
    ctrl.enableDamping = true;
    ctrl.dampingFactor = 0.09;
    ctrl.target.copy(inLume(new THREE.Vector3(cadru.cx, cadru.cy, 4)));
    // PÂNĂ LA NIVELUL OCHILOR, și de acolo cu privirea puțin în sus — ca de pe
    // trotuar spre fațadă. Sub teren camera nu intră: o ține `OCHI_M`, mai jos.
    ctrl.maxPolarAngle = Math.PI / 2 + 0.35;
    // DE APROAPE: cât să stai lângă parter, la câțiva metri de el. La 0,12·R
    // (~13 m pe un amplasament obișnuit) camera nu se putea apropia de nimic,
    // iar ținta stătea sus — macheta se privea numai de departe.
    ctrl.minDistance = Math.max(2, cam.near * 2);
    ctrl.maxDistance = R * 14;
    // AICI SE ROTEȘTE CU STÂNGA, spre deosebire de planșă. E excepția pe care
    // `Randare3D` o face deja pe «3D»: pe o machetă, gestul principal e
    // întoarcerea, iar pe un trackpad butonul drept nu se poate trage.
    ctrl.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY,
                          RIGHT: THREE.MOUSE.PAN };
    // MERSUL PRIN SCENĂ. Stânga rotește în jurul țintei, dreapta (sau
    // shift + stânga) trage ținta, rotița apropie. Ce lipsea era să te MUȚI:
    // săgețile și W/A/S/D duc ținta și camera împreună, în planul solului, pe
    // direcția în care privești — pasul crește cu distanța, ca să nu fie un
    // târâit pe un amplasament de 300 m și un salt pe o curte de 30.
    // …dar nu și din câmpurile de text: săgețile din discuția fațadei sunt ale ei
    ctrl.listenToKeyEvents(tasteInAfaraCampurilor(window));
    ctrl.keyPanSpeed = 14;
    // PANORAMAREA ÎN PLANUL ECRANULUI și ROTIȚA SPRE CE ARĂȚI: ținta se mută
    // INCLUSIV PE ÎNĂLȚIME — trasul cu dreapta în sus sau în jos urcă și coboară
    // vederea, iar apropierea spre parter coboară ținta la parter. Pe planul
    // solului (`screenSpacePanning = false`), ținta rămânea la cota ei (4 m, sau
    // jumătatea clădirilor la ansamblu) și pentru panoramare, și pentru
    // apropierea spre cursor: camera se învârtea mereu în jurul unui punct de sus.
    ctrl.screenSpacePanning = true;
    ctrl.zoomToCursor = true;
    // CAMERA COBOARĂ PÂNĂ LA NIVELUL OCHILOR, nu sub el și nu sub teren — oricum
    // ar trage-o rotirea sau apropierea spre cursor. Ținta urcă odată cu ea:
    // ridicată singură, camera se apleca spre trotuar la fiecare pas (`lookAt`
    // spre o țintă rămasă jos) și la capătul coborârii privea în pământ.
    const OCHI_M = 1.7;
    ctrl.addEventListener("change", () => {
      if (ctrl.target.z < 0) ctrl.target.z = 0;
      if (cam.position.z < OCHI_M) {
        ctrl.target.z += OCHI_M - cam.position.z;
        cam.position.z = OCHI_M;
      }
    });
    // ROTIȚA SE OPREȘTE LA 2 m DE CE E SUB CURSOR — teren sau perete —, nu în
    // aer. `zoomToCursor` duce camera pe raza cursorului, dar măsoară apropierea
    // față de ȚINTĂ — pe care, cu `screenSpacePanning`, o reașază «în fața
    // camerei», în aer, la fiecare pas. Arătând spre cer sau spre un etaj de sus,
    // camera ajungea la `minDistance` de un punct din aer și rotița murea acolo,
    // la zeci de metri deasupra solului; spre fațadă trecea prin zid. Înaintea
    // fiecărui pas ținta se mută la DISTANȚA primei lovituri a razei (aceleași
    // plase ca la alegere) sau a terenului de sub cursor — dar pe axa camerei,
    // nu pe rază: `update` face întâi `lookAt(target)`, și o țintă pe rază ar
    // întoarce privirea spre cursor la fiecare pas (coborând spre sol, camera
    // ajungea să privească în jos, la trotuarul de sub ea). Spre cer nu se
    // zboară: pasul acela apropie spre ținta de dinainte, ca o rotiță obișnuită —
    // altfel camera urca în cer și macheta ieșea din cadru. Un punct de la
    // orizont nu intră dintr-odată: ținta se duce cel mult de două ori mai
    // departe decât era, ca pasul (5 % din distanță) să crească treptat, nu să
    // sară cu 75 m peste machetă.
    const planSol = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const razaRotitei = new THREE.Raycaster(), ndcRotitei = new THREE.Vector2();
    const inainte = new THREE.Vector3();
    const laRotita = (ev: WheelEvent) => {
      const r = rand.domElement.getBoundingClientRect();
      ndcRotitei.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
      razaRotitei.setFromCamera(ndcRotitei, cam);
      const peSol = razaRotitei.ray.distanceToPlane(planSol);
      const pePerete = lovituri(ev)[0]?.distance;   // distanța e aceeași în ambele cadre
      const t = pePerete !== undefined && (peSol === null || pePerete < peSol) ? pePerete : peSol;
      ctrl.zoomToCursor = t !== null;
      if (t !== null) {
        const plafon = Math.min(ctrl.maxDistance, 2 * cam.position.distanceTo(ctrl.target));
        cam.getWorldDirection(inainte);
        ctrl.target.copy(cam.position).addScaledVector(inainte, Math.min(t, plafon));
      }
    };
    rand.domElement.addEventListener("wheel", laRotita, { capture: true, passive: true });
    const pasi: Record<string, [number, number, number]> = {
      w: [0, 1, 0], s: [0, -1, 0], a: [-1, 0, 0], d: [1, 0, 0],
      // SUS ȘI JOS: camera și ținta împreună, ca într-un lift — Q coboară, E urcă
      // (Page Up / Page Down sunt ale selectorului de etaj, v. `SelectorEtaj`)
      q: [0, 0, -1], e: [0, 0, 1],
    };
    const laTasta = (ev: KeyboardEvent) => {
      const k = ev.key.toLowerCase();
      const p = pasi[k];
      if (!p || ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      ev.preventDefault();
      const pas = Math.max(0.5, cam.position.distanceTo(ctrl.target) * 0.04);
      if (p[2]) {
        // pe verticală pasul e un etaj cel mult, ca să se poată opri la parter;
        // ținta se oprește la sol, camera merge mai departe până la nivelul
        // ochilor — altfel, privind în jos, rămânea zeci de metri deasupra
        const dz = p[2] * Math.min(pas, 3);
        cam.position.z = Math.max(OCHI_M, cam.position.z + dz);
        ctrl.target.z = Math.max(0, ctrl.target.z + dz);
        ctrl.update();
        muta = false;
        return;
      }
      const dir = new THREE.Vector3().subVectors(ctrl.target, cam.position);
      dir.z = 0;
      if (dir.lengthSq() < 1e-6) return;
      dir.normalize();
      const dreapta = new THREE.Vector3(dir.y, -dir.x, 0);
      const mut = dir.multiplyScalar(p[1] * pas).add(dreapta.multiplyScalar(p[0] * pas));
      cam.position.add(mut);
      ctrl.target.add(mut);
      muta = false;
    };
    window.addEventListener("keydown", laTasta);

    /** Cât de departe trebuie stat ca tot amplasamentul să intre în cadru.
     *
     *  Sfera care cuprinde cutia scenei — inclusiv ÎNĂLȚIMEA corpurilor, care e
     *  jumătate din motivul pentru care nu încăpea — și unghiul cel mai strâns
     *  dintre cel vertical și cel orizontal, fiindcă pe o fereastră lată
     *  verticalul e cel care taie. */
    function distantaIncadrare(fov: number): number {
      const semiX = (cadru.maxX - cadru.minX) / 2;
      const semiY = (cadru.maxY - cadru.minY) / 2;
      const semiZ = hMax / 2;
      const raza = Math.hypot(semiX, semiY, semiZ);
      const fovV = (fov * Math.PI) / 180;
      const fovH = 2 * Math.atan(Math.tan(fovV / 2) * Math.max(0.3, cam.aspect));
      const jum = Math.min(fovV, fovH) / 2;
      return (raza / Math.sin(jum)) * 1.06;        // o margine subțire de aer
    }

    // `null` după ce omul a pus mâna pe cameră: de atunci redimensionarea nu
    // mai reîncadrează, iar refacerea scenei nu mai sare la preset.
    let vedereaCurenta: Vedere | null = VEDERI[vedere] ?? VEDERI.ansamblu;
    function aseaza(v: Vedere, lin: boolean) {
      vedereaCurenta = v;
      const a = (v.az * Math.PI) / 180, e = (v.el * Math.PI) / 180;
      cam.fov = v.fov;
      cam.updateProjectionMatrix();
      const dist = v.incadreaza ? distantaIncadrare(v.fov)
                                : Math.max(R * (v.d ?? 2), 18);
      // Ținta urcă la jumătatea clădirilor când se încadrează tot: lăsată la
      // patru metri, cutia scenei atârna deasupra ei și blocurile ieșeau sus.
      const tz = v.incadreaza ? Math.max(4, hMax / 2) : 4;
      // Azimutul e de busolă, iar nordul desenului e −y.
      const p = inLume(new THREE.Vector3(
        cadru.cx + dist * Math.sin(a) * Math.cos(e),
        cadru.cy - dist * Math.cos(a) * Math.cos(e),
        tz + dist * Math.sin(e)));
      ctrl.target.copy(inLume(new THREE.Vector3(cadru.cx, cadru.cy, tz)));
      if (!lin) { cam.position.copy(p); ctrl.update(); return; }
      tinta.copy(p);
      muta = true;
    }
    const tinta = new THREE.Vector3();
    let muta = false;

    // ------------------------------------------------------------ soarele viu
    let cerInvechit = true;
    let ultimCer = 0;
    puneSoare.current = (alt: number, az: number) => {
      const v = vectorSoare(alt, az);
      // Cât de lungă e umbra celui mai înalt corp ACUM. Cu o cutie fixă, la
      // soare jos umbrele ies din ea și se retează drept, la mijlocul lor.
      const lung = alt > 2 ? Math.min(hMax / Math.tan((alt * Math.PI) / 180), R * 4)
                           : R * 4;
      const semi = Math.min(Math.max(R * 1.2, R * 0.75 + lung * 0.7), R * 5);
      const d = Math.max(R * 2.4, 60) + lung * 0.5;
      sc.left = -semi; sc.right = semi; sc.top = semi; sc.bottom = -semi;
      sc.near = 1; sc.far = d + R * 2 + hMax + 60;
      sc.updateProjectionMatrix();
      // Sub orizont nu mai e ce arunca umbra, iar lumina rasantă de sub pământ
      // ar da una întinsă la infinit.
      soareL.castShadow = alt > 1.5;
      soareL.position.set(cadru.cx + v[0] * d, cadru.cy + v[1] * d, Math.max(v[2] * d, 2));
      const taria = neted(-1.5, 7, alt) * (0.55 + 0.45 * Math.sin(Math.max(alt, 0) * Math.PI / 180));
      soareL.intensity = 3.2 * taria;
      const cald = mix([1, 0.52, 0.26], [1, 0.84, 0.62], neted(-1, 8, alt));
      const cul = mix(cald, [1, 0.975, 0.93], neted(9, 44, alt));
      soareL.color.setRGB(cul[0], cul[1], cul[2]);
      const c = culoriCer(alt);
      uni.sus.value.set(c.sus[0], c.sus[1], c.sus[2]);
      uni.jos.value.set(c.jos[0], c.jos[1], c.jos[2]);
      // cerul e în afara lumii: direcția soarelui, în cadrul ei
      uni.dirSoare.value.set(v[0], -v[1], v[2]);
      uni.culSoare.value.set(cul[0], cul[1], cul[2]);
      uni.taria.value = neted(-4, 2, alt);
      cerLumina.color.setRGB(c.sus[0] + 0.35, c.sus[1] + 0.35, c.sus[2] + 0.32);
      cerLumina.groundColor.setRGB(c.jos[0] * 0.45, c.jos[1] * 0.42, c.jos[2] * 0.36);
      cerLumina.intensity = 0.35 + 0.5 * neted(-8, 26, alt);
      cerInvechit = true;
    };
    // SOARELE PE SCENA NOUĂ. Efectul de dedesubt îl pune numai când se mișcă ora;
    // o scenă refăcută (o revizie a documentului, anularea sau refacerea fațadei)
    // rămânea cu lumina implicită — umbrele într-o cutie de ±5 m, discul soarelui
    // la zenit — până la prima atingere a riglei.
    puneSoare.current(soareRef.current.alt, soareRef.current.az);
    puneVedere.current = (v: Vedere) => aseaza(v, true);

    // ------------------------------------------------------------- redimensionare
    const dim = new THREE.Vector2();
    const masoara = () => {
      const w = el.clientWidth, h = el.clientHeight;
      if (!w || !h) return;
      // pe ACEEAȘI pânză, la aceeași mărime, nu se mai pune: o lățime pusă din
      // nou golește pânza, iar cadrul scenei vechi trebuie să rămână
      rand.getSize(dim);
      if (dim.x !== w || dim.y !== h) rand.setSize(w, h, false);
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      if (vedereaCurenta?.incadreaza && !muta) aseaza(vedereaCurenta, false);
      cereCadru.current();
    };
    const obs = new ResizeObserver(masoara);
    obs.observe(el);
    masoara();
    const tinuta = camTinuta.current;
    if (tinuta) {
      cam.fov = tinuta.fov;
      cam.updateProjectionMatrix();
      cam.position.copy(tinuta.poz);
      ctrl.target.copy(tinuta.tinta);
      ctrl.update();
      vedereaCurenta = null;
    } else {
      aseaza(VEDERI[vedere] ?? VEDERI.ansamblu, false);
    }
    ctrl.addEventListener("start", () => { vedereaCurenta = null; });

    let cadruId = 0;
    let primul = true;
    // câte cadre mai trebuie desenate: câteva după fiecare cerere, ca o schimbare
    // făcută între două cadre (o umbră, o textură) să apuce să ajungă pe ecran
    let cerute = CADRE_PE_CERERE;
    let ultimDesen = 0;
    const cere = () => { cerute = Math.max(cerute, CADRE_PE_CERERE); };
    cereCadru.current = cere;
    ctrl.addEventListener("change", cere);
    rand.domElement.addEventListener("pointermove", cere);
    rand.domElement.addEventListener("pointerdown", cere);
    rand.domElement.addEventListener("wheel", cere, { passive: true });
    window.addEventListener("keydown", cere);
    window.addEventListener("keyup", cere);
    const buclă = () => {
      if (!viu) return;
      cadruId = requestAnimationFrame(buclă);
      if (tintaZ !== null) {
        const dz = tintaZ - ctrl.target.z;
        const pas = Math.abs(dz) < 0.05 ? dz : dz * 0.18;
        ctrl.target.z += pas;
        cam.position.z += pas;
        if (pas === dz) tintaZ = null;
      }
      if (muta) {
        cam.position.lerp(tinta, 0.12);
        if (cam.position.distanceTo(tinta) < 0.25) { cam.position.copy(tinta); muta = false; }
      }
      // Mediul se recoace DOAR când soarele s-a oprit din mișcat: `fromScene`
      // costă câteva milisecunde bune, iar la tras de riglă s-ar aduna în
      // fiecare cadru fără să se vadă diferența.
      if (cerInvechit && performance.now() - ultimCer > 160) {
        ultimCer = performance.now();
        cerInvechit = false;
        mediu?.dispose();
        mediu = pmrem.fromScene(cerScena, 0, 0.5, 50);
        s3.environment = mediu.texture;
        // Mediul UMPLE, soarele MODELEAZĂ — aceeași împărțire ca în `Randare3D`.
        // Peste jumătate, umbra proprie a unei fațade dispare în lumina difuză.
        s3.environmentIntensity = 0.42;
        cere();
      }
      // amortizarea controalelor ține camera în mișcare și după ce omul a
      // lăsat mouse-ul: cât se mișcă, `update` spune `true`
      const miscata = ctrl.update();
      const acum = performance.now();
      const anima = tintaZ !== null || muta || cerInvechit;
      if (!miscata && !anima && cerute <= 0 && !primul && acum - ultimDesen < BATAIE_MS) return;
      if (cerute > 0) cerute--;
      ultimDesen = acum;
      rand.render(s3, cam);
      if (primul) {
        primul = false;
        const inchide = deInchis.current;
        deInchis.current = null;
        inchide?.();
        laGataRef.current?.();
      }
    };
    buclă();

    return () => {
      viu = false;
      cancelAnimationFrame(cadruId);
      ctrl.removeEventListener("change", cere);
      rand.domElement.removeEventListener("pointermove", cere);
      rand.domElement.removeEventListener("pointerdown", cere);
      rand.domElement.removeEventListener("wheel", cere);
      window.removeEventListener("keydown", cere);
      window.removeEventListener("keyup", cere);
      if (cereCadru.current === cere) cereCadru.current = () => undefined;
      obs.disconnect();
      window.removeEventListener("keydown", laTasta);
      rand.domElement.removeEventListener("wheel", laRotita, { capture: true });
      rand.domElement.removeEventListener("pointerdown", laApasare, { capture: true });
      rand.domElement.removeEventListener("pointermove", laMiscare);
      rand.domElement.removeEventListener("pointerup", laRidicare);
      window.removeEventListener("keydown", laTastaAlt);
      window.removeEventListener("keyup", laTastaAlt);
      window.removeEventListener("blur", laPierdereFocus);
      if (cautareFata) cancelAnimationFrame(cautareFata);
      rand.domElement.style.cursor = "";
      if (alegere.current?.lume === lume) alegere.current = null;
      camTinuta.current = { poz: cam.position.clone(), tinta: ctrl.target.clone(), fov: cam.fov };
      ctrl.dispose();
      puneSoare.current = null;
      puneVedere.current = null;
      // CE A ȚINUT SCENA PE GPU se eliberează după primul cadru al celei
      // următoare. Randorul și pânza rămân (v. `randTinut`), deci nu mai pleacă
      // nimic odată cu contextul: ce n-a trecut prin `deArs` — o geometrie, un
      // material, harta de umbră a soarelui — se caută în scenă. O eliberare în
      // plus e fără urmări; o textură din memoria comună, folosită și de scena
      // nouă, n-are ce căuta aici, deci texturile rămân pe seama lui `deArs`.
      const inchide = () => {
        taietura.elibereaza();
        pmrem.dispose();
        mediu?.dispose();
        deArs.forEach((d) => d.dispose());
        s3.traverse((o) => {
          const m = o as THREE.Mesh;
          m.geometry?.dispose();
          const mat = m.material;
          (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach((x) => x.dispose());
          (o as THREE.DirectionalLight).shadow?.dispose();
        });
      };
      deInchis.current?.();               // o a treia montare, înaintea primului cadru
      deInchis.current = inchide;
      queueMicrotask(() => {
        // fără montare următoare în același pas (demontare adevărată): acum
        if (deInchis.current === inchide && !gazda.current) { deInchis.current = null; inchide(); }
      });
    };
  }, [scena]);

  useEffect(() => { puneSoare.current?.(soare.alt, soare.az); }, [soare.alt, soare.az]);

  // ORAȘUL intră în scena de acum și iese din ea; la o scenă nouă se mută în
  // ea, gata construit. Declarat DUPĂ efectul scenei, deci rulează după el.
  useEffect(() => {
    const st = alegere.current;
    if (!oras || !st) return;
    const scoate = ataseazaOras(oras, { ...st, teren: scena.ridicare });
    cereCadru.current();
    return () => { scoate(); cereCadru.current(); };
  }, [scena, oras]);
  useEffect(() => { puneVedere.current?.(VEDERI[vedere] ?? VEDERI.ansamblu); }, [vedere]);

  // STRATURILE ATAȘATE intră în scena de acum și ies din ea, ca orașul:
  // construite în afară, în cadrul documentului, deci direct în `lume`.
  useEffect(() => {
    const st = alegere.current;
    if (!st || !atasate?.length) return;
    for (const s of atasate) st.lume.add(s.grup);
    cereCadru.current();
    return () => { for (const s of atasate) st.lume.remove(s.grup); cereCadru.current(); };
  }, [scena, atasate]);

  // EVIDENȚIEREA: elementele alese, redesenate peste ele în culoarea
  // accentului, și zonele, prin elementele lor (v. `deseneazaZona`). Se refac
  // la orice schimbare a selecției, a scenei sau a cipului aprins.
  const cheieSel = (selectie ?? []).join("|");
  const cheieZone = JSON.stringify(zone ?? []);
  useEffect(() => {
    const st = alegere.current;
    if (!st) return;
    const grup = new THREE.Group();
    grup.renderOrder = 15;
    const deArs: { dispose(): void }[] = [];
    const umplere = umplereAles(false);
    const umplereTare = umplereAles(true);
    const contur = new THREE.LineBasicMaterial({ color: CULOARE_TARE, depthTest: false, transparent: true,
                                                 opacity: 0.95 });
    deArs.push(umplere, umplereTare, contur);
    for (const e of selectie ?? []) {
      const bucati = new Map<THREE.Mesh, [number, number][] | null>();
      for (const m of st.plase) {
        if (m.userData.eid) {
          if (eAlEi(String(m.userData.eid), e)) bucati.set(m, null);
          continue;
        }
        const t = m.userData.alege as TabelAlegere | undefined;
        const b = t ? bucatileObiectului(t, e) : [];
        if (b.length) bucati.set(m, b);
      }
      evidentiaza(grup, deArs, bucati, aprins === `e:${e}` ? umplereTare : umplere, contur);
    }
    const desene = (zone ?? []).map((z, i) => deseneazaZona(st, z, false, aprins === `z:${i}`));
    st.taietura.taie(grup);
    st.lume.add(grup);
    return () => {
      st.lume.remove(grup);
      deArs.forEach((d) => d.dispose());
      desene.forEach((d) => d.scoate());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scena, cheieSel, cheieZone, aprins]);

  // COTELE elementului din panoul de detalii: punctele lui (aceleași
  // triunghiuri ca evidențierea), cadrul feței lui, liniile de cotă.
  useEffect(() => {
    const st = alegere.current;
    if (!st || !cote) return;
    const parti: Float32Array[] = [];
    const deArs: { dispose(): void }[] = [];
    for (const m of st.plase) {
      let g: THREE.BufferGeometry | null = null;
      if (m.userData.eid) {
        if (eAlEi(String(m.userData.eid), cote)) g = m.geometry;
      } else {
        const t = m.userData.alege as TabelAlegere | undefined;
        const b = t ? bucatileObiectului(t, cote) : [];
        if (b.length) { g = triunghiurileDin(m.geometry, b); if (g) deArs.push(g); }
      }
      const pos = g?.getAttribute("position") as THREE.BufferAttribute | undefined;
      if (!pos) continue;
      const a = new Float32Array(pos.count * 3);
      for (let i = 0; i < pos.count; i++) { a[i * 3] = pos.getX(i); a[i * 3 + 1] = pos.getY(i); a[i * 3 + 2] = pos.getZ(i); }
      parti.push(a);
    }
    deArs.forEach((d) => d.dispose());
    if (!parti.length) return;
    const puncte = new Float32Array(parti.reduce((s, a) => s + a.length, 0));
    let k = 0;
    for (const a of parti) { puncte.set(a, k); k += a.length; }
    // fața elementului: cea scrisă pe el, altfel cea mai apropiată a corpului
    st.info ??= infoElemente(st.fatade);
    const mn: [number, number, number] = [Infinity, Infinity, Infinity], mx: [number, number, number] = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < puncte.length; i += 3) {
      for (let j = 0; j < 3; j++) { mn[j] = Math.min(mn[j], puncte[i + j]); mx[j] = Math.max(mx[j], puncte[i + j]); }
    }
    const fata = fataElementului({ mesh: st.plase[0], start: 0, n: 0, eid: cote, min: mn, max: mx },
                                 st.info.get(bazaEid(cote)), st.fete);
    const cam = st.cam;
    const ochi = inDocument(cam.position);
    const spre: [number, number, number] = [ochi.x, ochi.y, ochi.z];
    const cadru = cadrulElementului(puncte, fata ? st.fete.get(fata) ?? null : null, spre);
    if (!cadru) return;
    const nf = new Intl.NumberFormat(bcp47(limba()), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const desen = deseneazaCotele(st.lume, cotele(cadru, spre),
                                  (c) => `${nf.format(coteValori?.[c.fel] ?? c.valoare)} m`);
    st.taietura.taie(desen.grup);
    return () => desen.scoate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scena, cote, JSON.stringify(coteValori ?? {})]);

  // ETAJUL TĂIAT (v. `taieturaEtaj.ts`): planul coboară la etaj, corpul lui și
  // cele de deasupra se ascund, interiorul intră în scenă, ținta camerei urcă
  // la el. Mouse-ul peste un apartament îl conturează și spune ce e.
  useEffect(() => {
    const st = alegere.current;
    if (!st) return;
    const c = st.taietura.aplica(etaj);
    setPeste(null);
    if (!c) return;
    st.laCota(c.jos + 1.5);
    const ascunde = st.taietura.arata();
    const el = st.rand.domElement;
    const raza = new THREE.Raycaster(), ndc = new THREE.Vector2();
    let cadru = 0, ultim: PointerEvent | null = null, curent: ApartamentEtaj | null = null;
    let contur: THREE.LineLoop | null = null;
    const scoateContur = () => {
      if (!contur) return;
      st.lume.remove(contur);
      contur.geometry.dispose();
      (contur.material as THREE.Material).dispose();
      contur = null;
    };
    const cauta = () => {
      cadru = 0;
      const ev = ultim;
      if (!ev) return;
      let a: ApartamentEtaj | null = null;
      if (!ev.buttons) {                       // cât se rotește camera, nimic
        const r = el.getBoundingClientRect();
        ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
        raza.setFromCamera(ndc, st.cam);
        razaInDocument(raza.ray);
        a = st.taietura.apartamentul(raza);
      }
      if (a !== curent) {
        scoateContur();
        curent = a;
        if (a) {
          const g = new THREE.BufferGeometry().setFromPoints(a.poly.map(([x, y]) => new THREE.Vector3(x, y, c.jos + 0.03)));
          contur = new THREE.LineLoop(g, new THREE.LineBasicMaterial({
            color: tipulApartamentului(a.camere).contur, depthTest: false, transparent: true }));
          contur.renderOrder = 20;
          st.lume.add(contur);
        }
      }
      setPeste(a ? { a, x: ev.clientX, y: ev.clientY } : null);
    };
    const misca = (ev: PointerEvent) => { ultim = ev; if (!cadru) cadru = requestAnimationFrame(cauta); };
    const iese = () => { ultim = null; curent = null; scoateContur(); setPeste(null); };
    el.addEventListener("pointermove", misca);
    el.addEventListener("pointerleave", iese);
    return () => {
      el.removeEventListener("pointermove", misca);
      el.removeEventListener("pointerleave", iese);
      cancelAnimationFrame(cadru);
      scoateContur();
      ascunde();
    };
  }, [scena, etaj]);

  // CLĂDIREA și STAREA PE SĂPTĂMÂNĂ se aplică fără reconstrucție: bucățile
  // clădirii după corpul și nivelul lor, straturile solului după eticheta lor.
  // Declarat DUPĂ efectul tăieturii pe etaj: el aprinde corpurile la `aplica`,
  // iar starea trebuie să vină peste el, nu sub el.
  useEffect(() => {
    const st = alegere.current;
    if (!st) return;
    // tăietura pe etaj întâi (ea își aprinde corpurile), apoi starea, care decide
    if (cladire && etaj != null) st.taietura.aplica(etaj);
    const f = stare?.cladire;
    for (const o of st.cladire) {
      const cn = o.userData.corpNivel as { corp: string; nivel: number; fel: FelCladire } | undefined;
      o.visible = cladire && (!f || !cn || f(cn.corp, cn.nivel, cn.fel));
    }
    const sl = stare?.sol;
    for (const o of st.sol.children) {
      const k = o.userData.strat as StratSol | undefined;
      o.visible = !sl || !k || sl[k] !== false;
    }
    cereCadru.current();
  }, [scena, cladire, stare, etaj]);

  // ORICE RANDARE A COMPONENTEI CERE UN CADRU. Efectele de mai sus (soarele,
  // vederea, selecția, cotele, etajul tăiat, orașul) schimbă scena din afara
  // buclei; declarat ULTIMUL, efectul ăsta rulează după ele, în aceeași trecere,
  // deci schimbarea lor ajunge pe ecran fără ca fiecare să-și amintească s-o ceară.
  useEffect(() => { cereCadru.current(); });

  return (
    <>
      <div className="macheta-sit" ref={gazda} />
      {peste && <EtichetaApartament a={peste.a} x={peste.x} y={peste.y} />}
    </>
  );
}

/** Ce e apartamentul de sub mouse: tipul, suprafața utilă și balconul, cu
 *  camera pe care dă — «2 camere · 45,1 m² · balcon 17,1 m² din living». */
function EtichetaApartament({ a, x, y }: { a: ApartamentEtaj; x: number; y: number }) {
  const nr = (v: number) => v.toLocaleString(bcp47(limba()), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const tip = tipulApartamentului(a.camere);
  const balcon = a.balcoane.reduce((s, b) => s + b.arie, 0);
  const mare = [...a.balcoane].sort((p, q) => q.arie - p.arie)[0];
  const camera = mare?.spre && esteCheie(`macheta.camera.${mare.spre}`)
    ? tr(`macheta.camera.${mare.spre}` as Cheie).toLocaleLowerCase(bcp47(limba())) : null;
  return (
    <div className="macheta-eticheta" style={{ left: x + 14, top: y + 14 }}>
      <i style={{ background: tip.fond, borderColor: tip.contur }} />
      <b>{a.camere === 1 ? tr("macheta.ap.garsoniera") : tr("macheta.ap.camere", { n: a.camere })}</b>
      <span>· {nr(a.util)} m²</span>
      {balcon > 0 && (
        <span>· {camera ? tr("macheta.ap.balcon", { arie: `${nr(balcon)} m²`, camera })
                         : tr("macheta.ap.balcon_fara", { arie: `${nr(balcon)} m²` })}</span>
      )}
    </div>
  );
}
